import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { firstEnabledIndex, lastEnabledIndex, findNextEnabledIndex, safeFocus } from '@norkron/web-a11y';
import { z } from 'zod';

// src/error-codes.ts
var LIB = "@norkron/web-docz";
var WEB_DOCZ_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-docz");
var { panic, reportInvalidInput } = createScopedErrors(LIB, WEB_DOCZ_TOKEN);
var DZ_OK = 0;
var DZ_ERR_OOM = -1;
var DZ_ERR_DECODE = -2;
var DZ_ERR_RENDER = -3;
var DZ_ERR_INVALID = -4;
var DZ_ERR_TRANSFORM = -5;
var DZ_ERR_UNAVAILABLE = -100;
var DZ_FEATURE_RENDER = 1;
var DZ_FEATURE_ANCHOR = 2;
var DZ_FEATURE_TRANSFORM = 4;
var DZ_FEATURE_STYLE_MARKS = 8;
var DZ_FEATURE_BLOCK_PALETTE = 16;
var DZ_FEATURE_MARK_TOGGLE = 32;
var DZ_FEATURE_TABLE_VERBS = 64;
var DZ_FEATURE_LIST_VERBS = 128;
var DZ_FEATURE_BLOCK_KIND = DZ_FEATURE_RENDER;
try {
  registerCodes(LIB, {
    WEB_DOCZ_INVALID_INPUT: {
      description: "A public function received an input that failed zod validation against the DoczBlock / DoczDocument / CommentSpan schema (e.g. a negative offset, an empty commentId, a non-array blocks field). The structured zod issue list is attached; the raw zod error never escapes the boundary.",
      severity: "error",
      docsAnchor: "docz/invalid-input",
      exampleFix: "Pre-validate with `DoczBlockSchema.safeParse(block)` before rendering, and fix the reported path/expected/received issue (offsets are UTF-8 byte offsets, non-negative integers)."
    },
    WEB_DOCZ_WASM_OOM: {
      description: "The docz WASM core ran out of linear memory while rendering a block (dz_render_block_to_html returned -1).",
      severity: "error",
      docsAnchor: "docz/wasm-oom",
      exampleFix: "Reduce the block text / comment count; if it recurs on small inputs, the wasm artefact is likely stale/corrupt \u2014 recopy docz.wasm from `docz`\u2019s `zig build wasm` output."
    },
    WEB_DOCZ_WASM_DECODE: {
      description: "The docz WASM core could not decode the ABI-framed render input (dz_render_block_to_html returned -2). Indicates the JS-side codec and the wasm-side codec disagree.",
      severity: "error",
      docsAnchor: "docz/wasm-decode",
      exampleFix: "This is a codec/version mismatch between web-docz and docz.wasm \u2014 recopy docz.wasm from the matching docz build so the fixed little-endian ABI layout matches."
    },
    WEB_DOCZ_WASM_RENDER: {
      description: "The docz WASM core failed while rendering the block to HTML (dz_render_block_to_html returned -3).",
      severity: "error",
      docsAnchor: "docz/wasm-render",
      exampleFix: "File a bug against docz with the offending block text + comment ranges; the html_writer should never fail on a decoded block."
    },
    WEB_DOCZ_WASM_UNKNOWN: {
      description: "The docz WASM core returned a status code outside the documented 0 / -1 / -2 / -3 range.",
      severity: "error",
      docsAnchor: "docz/wasm-unknown",
      exampleFix: "Likely a wasm/binding version mismatch \u2014 recopy docz.wasm from the matching docz `zig build wasm` output."
    },
    WEB_DOCZ_NO_DOM: {
      description: "A DOM-layer function (mountBlock / renderBlockToFragment / highlightComment) was called in a runtime without a DOM (`document` / `DOMParser` undefined). The loader + codec are runtime-agnostic; only the render-to-DOM layer needs a browser or happy-dom/jsdom.",
      severity: "error",
      docsAnchor: "docz/no-dom",
      exampleFix: "Call the DOM layer only in a browser (or a happy-dom/jsdom test env). For headless HTML use `handle.toHtml()` / `module.renderBlockToHtml()` which need no DOM."
    },
    WEB_DOCZ_WASM_VERB_UNAVAILABLE: {
      description: "An anchor/transform verb (anchorBuildSelector / anchorRebase / transformApplyMark / transformSplitRuns) was called but the loaded docz.wasm does not export it yet (the FR-docz-1/2 verbs have not landed). The verb returns { ok:false, errCode: DZ_ERR_UNAVAILABLE } (-100) rather than fabricate a result \u2014 an honest pending, not a TS reimplementation.",
      severity: "warning",
      docsAnchor: "docz/wasm-verb-unavailable",
      exampleFix: "Recopy docz.wasm from a docz build that includes the dz_anchor_* / dz_transform_* exports (FR-docz-1/2). Until then, gate the caller on `module.features` (DZ_FEATURE_ANCHOR / DZ_FEATURE_TRANSFORM) and fall back to the server anchor path."
    }
  });
} catch {
}

// src/href.ts
var SAFE_URL_SCHEMES = ["http:", "https:", "mailto:"];
function isSafeHref(href) {
  const u = href.trim();
  if (u.length === 0) return false;
  return /^https?:\/\//i.test(u) || /^mailto:[^\s]+$/i.test(u);
}
function safeHref(href) {
  const u = href.trim();
  return isSafeHref(u) ? u : null;
}
var DOCUMENT_URL_SCHEMES = SAFE_URL_SCHEMES;
function documentAllowed(url) {
  const s = scheme(url);
  if (s === null) return false;
  if (s === "") return url.trim().length > 0;
  return DOCUMENT_URL_SCHEMES.includes(`${s}:`) && hasAuthorityOrBody(url, s);
}
function hasAuthorityOrBody(url, s) {
  const u = url.trim();
  if (s === "mailto") return /^mailto:[^\s]+$/i.test(u);
  return /^https?:\/\/./i.test(u);
}
function isSafeDocumentHref(href) {
  return documentAllowed(href) && documentAllowed(decodeProbe(href));
}
function safeDocumentHref(href) {
  const u = href.trim();
  return isSafeDocumentHref(u) ? u : null;
}
var MEDIA_URL_SCHEMES = ["http:", "https:"];
var CONTROL_CHAR = /[\u0000-\u001f\u007f]/;
var NAMED_REFS = {
  colon: ":",
  Tab: "	",
  NewLine: "\n",
  sol: "/",
  num: "#",
  quest: "?",
  amp: "&"
};
var PROBE_LEN = 64;
function decodeProbe(url) {
  let out = "";
  let i = 0;
  while (i < url.length && out.length < PROBE_LEN) {
    if (url[i] !== "&") {
      out += url[i];
      i += 1;
      continue;
    }
    const end = url.indexOf(";", i + 1);
    if (end < 0 || end - i > 12) {
      out += url[i];
      i += 1;
      continue;
    }
    const body = url.slice(i + 1, end);
    let decoded = null;
    if (body.startsWith("#")) {
      const hex = body[1] === "x" || body[1] === "X";
      const n = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (Number.isFinite(n) && n >= 0 && n <= 1114111) decoded = String.fromCodePoint(n);
    } else {
      decoded = NAMED_REFS[body] ?? null;
    }
    if (decoded == null) {
      out += url[i];
      i += 1;
      continue;
    }
    out += decoded;
    i = end + 1;
  }
  return out;
}
function scheme(url) {
  if (CONTROL_CHAR.test(url)) return null;
  const s = url.trim();
  if (s.length === 0) return "";
  const cut = s.search(/[:/?#]/);
  if (cut < 0 || s[cut] !== ":") return "";
  const name = s.slice(0, cut);
  if (name.length === 0) return null;
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*$/.test(name)) return null;
  return name.toLowerCase();
}
function mediaAllowed(url) {
  const s = scheme(url);
  if (s === null) return false;
  if (s === "") return true;
  if (MEDIA_URL_SCHEMES.includes(`${s}:`)) return true;
  if (s !== "data") return false;
  const body = url.trim().slice(s.length + 1).toLowerCase();
  return body.startsWith("image/") && !body.startsWith("image/svg");
}
function isSafeImageSrc(src) {
  return mediaAllowed(src) && mediaAllowed(decodeProbe(src));
}
function safeSrc(src) {
  const u = src.trim();
  return isSafeImageSrc(u) ? u : null;
}

// src/sanitize.ts
function isCrossDocumentHref(href) {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) || href.startsWith("//");
}
var SANITIZE_ALLOWED_TAGS = /* @__PURE__ */ new Set([
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "code",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "span"
]);
var SANITIZE_DROP_TAGS = /* @__PURE__ */ new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "noscript",
  "svg",
  "math",
  "template",
  "link",
  "meta",
  "base",
  "title",
  "head",
  "body",
  "html",
  "form",
  "input",
  "textarea",
  "select",
  "button",
  "option",
  "frame",
  "frameset",
  "applet",
  "audio",
  "video",
  "source",
  "track",
  "canvas",
  "marquee"
]);
var SANITIZE_SAFE_CLASSES = /* @__PURE__ */ new Set([
  "docz-comment",
  "docz-block",
  "docz-anchor-bloom",
  "md"
]);
var POLICY_FORBIDDEN_ATTRS = /* @__PURE__ */ new Set([
  "href",
  "src",
  "srcset",
  "srcdoc",
  "formaction",
  "action",
  "xlink:href",
  "style",
  "class",
  "rel",
  "target",
  "is"
]);
var COMMENT_PROFILE = {
  allowed: SANITIZE_ALLOWED_TAGS,
  drop: SANITIZE_DROP_TAGS,
  classes: SANITIZE_SAFE_CLASSES
};
var SPAN_ATTRS = /* @__PURE__ */ new Set(["colspan", "rowspan"]);
var PASTE_PROFILE = {
  allowed: /* @__PURE__ */ new Set([
    ...SANITIZE_ALLOWED_TAGS,
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "td",
    "th",
    "caption",
    "pre",
    "hr"
  ]),
  drop: SANITIZE_DROP_TAGS,
  classes: SANITIZE_SAFE_CLASSES,
  attrs: SPAN_ATTRS
};
var TEXT_NODE = 3;
var ELEMENT_NODE = 1;
function filterClass(raw, profile) {
  if (!raw) return "";
  return raw.split(/\s+/).filter((t) => t.length > 0 && profile.classes.has(t)).join(" ");
}
function clampSpan(raw) {
  if (raw == null) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isInteger(n)) return 1;
  return n >= 1 ? n : 1;
}
function applyAttrPolicies(fresh, src, tag, profile) {
  if (!profile.attrPolicies) return;
  for (const policy of profile.attrPolicies) {
    const attr = policy.attr.toLowerCase();
    if (POLICY_FORBIDDEN_ATTRS.has(attr) || attr.startsWith("on")) continue;
    if (policy.tags && !policy.tags.has(tag)) continue;
    const raw = src.getAttribute(policy.attr);
    if (raw == null) continue;
    const safe = policy.value(raw);
    if (safe == null) continue;
    fresh.setAttribute(attr, safe);
  }
}
function applyAllowedAttrs(fresh, src, tag, profile) {
  if (tag === "a") {
    const raw = src.getAttribute("href");
    const policy = profile.hrefPolicy ?? safeHref;
    const safe = raw != null ? policy(raw) : null;
    if (safe != null) {
      fresh.setAttribute("href", safe);
      fresh.setAttribute("rel", "noreferrer noopener");
      if (isCrossDocumentHref(safe)) fresh.setAttribute("target", "_blank");
    }
  }
  if (tag === "img") {
    const raw = src.getAttribute("src");
    const safe = raw != null ? safeSrc(raw) : null;
    if (safe != null) fresh.setAttribute("src", safe);
  }
  if (profile.attrs) {
    for (const attr of profile.attrs) {
      if (!SPAN_ATTRS.has(attr)) continue;
      const n = clampSpan(src.getAttribute(attr));
      if (n != null) fresh.setAttribute(attr, String(n));
    }
  }
  applyAttrPolicies(fresh, src, tag, profile);
  const cls = filterClass(src.getAttribute("class"), profile);
  if (cls.length > 0) fresh.setAttribute("class", cls);
}
function cleanNode(node, profile) {
  if (node.nodeType === TEXT_NODE) {
    return [document.createTextNode(node.textContent ?? "")];
  }
  if (node.nodeType !== ELEMENT_NODE) {
    return [];
  }
  const el = node;
  const tag = el.localName.toLowerCase();
  if (profile.drop.has(tag)) return [];
  const children = cleanNodes(el.childNodes, profile);
  if (profile.allowed.has(tag)) {
    const fresh = document.createElement(tag);
    applyAllowedAttrs(fresh, el, tag, profile);
    for (const c of children) fresh.appendChild(c);
    return [fresh];
  }
  return children;
}
function cleanNodes(nodes, profile) {
  const out = [];
  for (const node of Array.from(nodes)) out.push(...cleanNode(node, profile));
  return out;
}
function sanitizeHtml(html, profile = COMMENT_PROFILE) {
  if (!html) return "";
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const cleaned = cleanNodes(tpl.content.childNodes, profile);
  const out = document.createElement("template");
  for (const c of cleaned) out.content.appendChild(c);
  return out.innerHTML;
}
function sanitizeCommentHtml(html) {
  return sanitizeHtml(html, COMMENT_PROFILE);
}

// src/mirror-policy.ts
var MIRROR_ALLOWED_TAGS = /* @__PURE__ */ new Set([
  // inline
  "a",
  "abbr",
  "b",
  "br",
  "cite",
  "code",
  "del",
  "em",
  "i",
  "ins",
  "kbd",
  "mark",
  "q",
  "s",
  "samp",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "u",
  "var",
  "wbr",
  // block
  "blockquote",
  "dd",
  "dl",
  "dt",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "li",
  "ol",
  "p",
  "pre",
  "ul",
  // tabular
  "caption",
  "col",
  "colgroup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  // graphics — the alt text IS the payload
  "img"
]);
var MIRROR_CLASSES = /* @__PURE__ */ new Set([
  "docz-comment",
  "docz-figcaption",
  // Emitted by html_writer when a link mark's scheme is REFUSED. Kept so the
  // projection records that a destination existed and was rejected, rather
  // than silently looking like ordinary prose.
  "docz-blocked-href"
]);
var LANG_RE = /^[A-Za-z]{1,8}(?:-[A-Za-z0-9]{1,8})*$/;
function normaliseLang(raw) {
  if (raw == null) return null;
  const t = raw.trim();
  return t.length > 0 && t.length <= 64 && LANG_RE.test(t) ? t : null;
}
function oneOf(...values) {
  const set = new Set(values);
  return (raw) => {
    const v = raw.trim().toLowerCase();
    return set.has(v) ? v : null;
  };
}
function stripControlChars(s) {
  let out = "";
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 32 || c >= 127 && c <= 159) continue;
    out += ch;
  }
  return out;
}
function speakableText(max) {
  return (raw) => {
    const t = stripControlChars(raw).slice(0, max);
    return t.length > 0 ? t : null;
  };
}
function altText(max) {
  return (raw) => stripControlChars(raw).slice(0, max);
}
function mirrorIdToken(raw) {
  return /^[A-Za-z0-9_.:-]{1,128}$/.test(raw) ? raw : null;
}
function positiveInt(raw) {
  const n = Number.parseInt(raw, 10);
  return Number.isInteger(n) && n >= 0 && n <= 1e6 ? String(n) : null;
}
var TAGS_TH = /* @__PURE__ */ new Set(["th"]);
var TAGS_IMG = /* @__PURE__ */ new Set(["img"]);
var TAGS_OL = /* @__PURE__ */ new Set(["ol"]);
var MIRROR_PROFILE = {
  hrefPolicy: safeDocumentHref,
  allowed: MIRROR_ALLOWED_TAGS,
  drop: SANITIZE_DROP_TAGS,
  classes: MIRROR_CLASSES,
  attrs: /* @__PURE__ */ new Set(["colspan", "rowspan"]),
  attrPolicies: [
    // WCAG 1.3.1 — which header a data cell belongs to.
    { attr: "scope", tags: TAGS_TH, value: oneOf("col", "row", "colgroup", "rowgroup") },
    // WCAG 1.1.1 — the text alternative. `altText`, NOT `speakableText`: an
    // EMPTY alt is the decorative marker and must survive (see {@link altText}).
    { attr: "alt", tags: TAGS_IMG, value: altText(512) },
    // WCAG 3.1.1 / 3.1.2 — language of the page and of its parts.
    { attr: "lang", value: (raw) => normaliseLang(raw) },
    { attr: "dir", value: oneOf("ltr", "rtl", "auto") },
    // List numbering the projection carries (`<ol start>` / `<ol type>`).
    { attr: "start", tags: TAGS_OL, value: positiveInt },
    { attr: "type", tags: TAGS_OL, value: oneOf("1", "a", "i") },
    // The review workflow's coordinate — how a comment anchor is found again.
    { attr: "data-comment-id", value: mirrorIdToken },
    // An abbreviation's expansion, spoken instead of the abbreviation.
    { attr: "abbr", value: speakableText(256) }
  ]
};
var MIRROR_ROOT_STYLE = "position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:normal;";
var MIRROR_VISIBLE_STYLE = "position:static;margin:0;padding:0;border:0;overflow:visible;white-space:normal;max-width:72ch;";
var COMMENT_CLASS = "pl-mirror-comment";
var COMMENT_NOTE_CLASS = "pl-mirror-comment-note";
function upgradeCommentAnchors(root, opts = {}) {
  const doc = root.ownerDocument;
  if (doc == null) return;
  for (const span2 of Array.from(root.querySelectorAll("span[data-comment-id]"))) {
    const id = span2.getAttribute("data-comment-id");
    if (id == null || mirrorIdToken(id) == null) continue;
    const mark = doc.createElement("mark");
    mark.className = COMMENT_CLASS;
    mark.setAttribute("data-comment-id", id);
    mark.setAttribute("tabindex", "-1");
    for (const attr of ["data-byte-start", "data-byte-end"]) {
      const v = span2.getAttribute(attr);
      if (v != null) mark.setAttribute(attr, v);
    }
    while (span2.firstChild) mark.appendChild(span2.firstChild);
    const detailsId = opts.commentDetailsId?.(id) ?? null;
    if (detailsId != null && detailsId.length > 0) mark.setAttribute("aria-details", detailsId);
    const label = opts.commentLabel?.(id) ?? null;
    if (label != null && label.length > 0) {
      const note = doc.createElement("span");
      note.className = COMMENT_NOTE_CLASS;
      note.textContent = ` ${label}`;
      mark.appendChild(note);
    }
    span2.replaceWith(mark);
  }
}
function commentIdsIn(root) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const el of Array.from(root.querySelectorAll("[data-comment-id]"))) {
    const id = el.getAttribute("data-comment-id");
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}
function stepComment(ids, from, dir, loop = true) {
  if (ids.length === 0) return null;
  const items = ids.map(() => ({}));
  if (from == null) {
    const i = dir === 1 ? firstEnabledIndex(items) : lastEnabledIndex(items);
    return i === -1 ? null : ids[i] ?? null;
  }
  const at = ids.indexOf(from);
  if (at === -1) {
    const i = dir === 1 ? firstEnabledIndex(items) : lastEnabledIndex(items);
    return i === -1 ? null : ids[i] ?? null;
  }
  const next = findNextEnabledIndex(items, at, dir, loop);
  return next === -1 ? null : ids[next] ?? null;
}

// src/mirror-project.ts
function bodyInner(full) {
  const open = full.indexOf("<body");
  if (open === -1) return full.trim();
  const start = full.indexOf(">", open);
  if (start === -1) return full.trim();
  const close = full.lastIndexOf("</body>");
  if (close === -1 || close < start) return full.slice(start + 1).trim();
  return full.slice(start + 1, close).trim();
}
function runsText(runs) {
  if (runs == null) return "";
  let out = "";
  for (const r of runs) out += r.text ?? "";
  return out;
}
function blockPlainText(block) {
  switch (block.kind) {
    case "paragraph":
    case "heading":
      return runsText(block.runs);
    case "list": {
      let out = "";
      for (const item of block.items) {
        out += Array.isArray(item) ? runsText(item) : runsText(item.runs);
      }
      return out;
    }
    case "table": {
      let out = "";
      if (block.header != null) for (const c of block.header.cells) out += runsText(c.runs);
      for (const row of block.rows) for (const c of row.cells) out += runsText(c.runs);
      return out;
    }
    case "blockquote": {
      let out = "";
      for (const child of block.children) {
        const t = blockPlainText(child);
        if (t == null) return null;
        out += t;
      }
      return out;
    }
    case "code":
      return block.code;
    case "image":
    case "imageSvg":
    case "horizontalRule":
      return "";
    default:
      return null;
  }
}
function projectHandle(handle) {
  const out = [];
  const bad = [];
  const blocks = handle.toBlocks();
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block === void 0) {
      bad.push({ ord: i, reason: "missing-block" });
      continue;
    }
    const r = handle.module.renderBlockDto(block);
    if (!r.ok) {
      bad.push({ ord: i, reason: `render-failed:${r.errCode ?? "unknown"}:${block.kind}` });
      continue;
    }
    const html = bodyInner(r.html);
    const text = blockPlainText(block);
    const id = block.id;
    const base = { ord: i, html };
    out.push({
      ...base,
      ...id != null ? { id } : {},
      ...text != null ? { text } : {}
    });
  }
  return { blocks: out, unprojectable: bad };
}
function mirrorBlocksFromHandle(handle) {
  return projectHandle(handle).blocks;
}
var UTF8 = new TextEncoder();
function utf8Len(s) {
  return UTF8.encode(s).length;
}
function textNodesIn(root) {
  const out = [];
  const walk = (n) => {
    for (let c = n.firstChild; c != null; c = c.nextSibling) {
      if (c.nodeType === 3) out.push(c);
      else if (c.nodeType === 1) walk(c);
    }
  };
  walk(root);
  return out;
}
function ownerElement(node, root) {
  let p = node.parentNode;
  while (p != null && p.nodeType === 1) {
    const el = p;
    if (el === root || root.contains(el)) return el;
    p = p.parentNode;
  }
  return null;
}
function stampByteOffsets(wrap, block) {
  const oracle = block.text;
  if (oracle == null) return false;
  const nodes = textNodesIn(wrap);
  const ranges = /* @__PURE__ */ new Map();
  let chars = 0;
  let bytes = 0;
  for (const node of nodes) {
    const s = node.data;
    if (s.length === 0) continue;
    if (!oracle.startsWith(s, chars)) {
      if (s.trim().length === 0) continue;
      return false;
    }
    const start = bytes;
    const end = bytes + utf8Len(s);
    for (let el = ownerElement(node, wrap); el != null; el = el.parentElement) {
      const prev = ranges.get(el);
      if (prev == null) ranges.set(el, { start, end });
      else ranges.set(el, { start: Math.min(prev.start, start), end: Math.max(prev.end, end) });
      if (el === wrap) break;
    }
    chars += s.length;
    bytes = end;
  }
  if (chars !== oracle.length) return false;
  for (const [el, r] of ranges) {
    el.setAttribute("data-byte-start", String(r.start));
    el.setAttribute("data-byte-end", String(r.end));
  }
  return true;
}
function mirrorPlainText(root) {
  let out = "";
  for (const node of textNodesIn(root)) out += node.data;
  return out;
}

// src/a11y-mirror.ts
var BLOCK_CLASS = "pl-mirror-block";
var DEFAULT_TEST_ID = "pl-a11y-mirror";
var KEY_ATTR = "data-mirror-key";
var SIGNATURES = /* @__PURE__ */ new WeakMap();
function signatureOf(b) {
  return `${b.lang ?? ""}\0${b.html}`;
}
function keyOf(b) {
  return b.id != null && b.id.length > 0 ? `id:${b.id}` : `doc:${b.ord}`;
}
function stampCoordinates(wrap, b) {
  let changed = false;
  const idx = String(b.ord);
  if (wrap.getAttribute("data-block-ord") !== idx) {
    wrap.setAttribute("data-block-ord", idx);
    changed = true;
  }
  const id = b.id != null && b.id.length > 0 ? b.id : null;
  if ((wrap.getAttribute("data-block-id") ?? null) !== id) {
    if (id == null) wrap.removeAttribute("data-block-id");
    else wrap.setAttribute("data-block-id", id);
    changed = true;
  }
  const lang = normaliseLang(b.lang);
  if ((wrap.getAttribute("lang") ?? null) !== lang) {
    if (lang == null) wrap.removeAttribute("lang");
    else wrap.setAttribute("lang", lang);
    changed = true;
  }
  return changed;
}
var NATIVELY_TABBABLE = "a[href], area[href], button, input, select, textarea, [contenteditable]";
function removeFromTabOrder(root) {
  for (const el of Array.from(root.querySelectorAll(NATIVELY_TABBABLE))) {
    el.setAttribute("tabindex", "-1");
  }
}
function buildBlock(b, opts, doc) {
  const wrap = doc.createElement("div");
  wrap.className = BLOCK_CLASS;
  wrap.setAttribute(KEY_ATTR, keyOf(b));
  stampCoordinates(wrap, b);
  const tpl = doc.createElement("template");
  tpl.innerHTML = sanitizeHtml(b.html, MIRROR_PROFILE);
  wrap.appendChild(tpl.content);
  stampByteOffsets(wrap, b);
  if (opts.visible !== true) removeFromTabOrder(wrap);
  upgradeCommentAnchors(wrap, opts);
  SIGNATURES.set(wrap, signatureOf(b));
  return wrap;
}
function applyRootAttrs(art, opts) {
  art.setAttribute("role", "document");
  if (opts.label != null && opts.label.length > 0) art.setAttribute("aria-label", opts.label);
  else art.removeAttribute("aria-label");
  const lang = normaliseLang(opts.lang);
  if (lang != null) art.setAttribute("lang", lang);
  else art.removeAttribute("lang");
  art.setAttribute("translate", "no");
  art.setAttribute("data-testid", opts.testId ?? DEFAULT_TEST_ID);
  art.setAttribute("style", opts.visible === true ? MIRROR_VISIBLE_STYLE : MIRROR_ROOT_STYLE);
  if ((opts.exposure ?? "silent") === "silent") art.setAttribute("aria-hidden", "true");
  else art.removeAttribute("aria-hidden");
}
function buildMirrorArticle(blocks, opts = {}, doc = document) {
  const art = doc.createElement("article");
  applyRootAttrs(art, opts);
  for (const b of blocks) art.appendChild(buildBlock(b, opts, doc));
  return art;
}
function hideFromAssistiveTech(host, selector = "canvas") {
  let n = 0;
  for (const el of Array.from(host.querySelectorAll(selector))) {
    if (el.getAttribute("aria-hidden") === "true") continue;
    el.setAttribute("aria-hidden", "true");
    n++;
  }
  return n;
}
function escapeSelectorValue(id) {
  return id.replace(/["\\]/g, "\\$&");
}
var DocumentMirror = class {
  constructor(host, opts = {}) {
    this.host = host;
    this.doc = host.ownerDocument;
    this.opts = opts;
    this.art = this.doc.createElement("article");
    applyRootAttrs(this.art, opts);
    host.appendChild(this.art);
    this.enforceExposure();
  }
  host;
  doc;
  art;
  opts;
  /** The mirror root (`role="document"`). */
  get element() {
    return this.art;
  }
  /** Replace the landmark identity / labels / exposure. Blocks are NOT
   *  re-projected — call {@link update} for that. */
  setOptions(opts) {
    this.opts = opts;
    applyRootAttrs(this.art, opts);
    this.enforceExposure();
  }
  /** Flip between shadow-mounted and sole-representation. This is both the
   *  Phase-1 → Phase-4 promotion AND the rollback. */
  setExposure(exposure) {
    this.setOptions({ ...this.opts, exposure });
  }
  /** Hide the painted sibling ONLY when this mirror is the sole representation.
   *  Returns how many surfaces were newly hidden. */
  enforceExposure() {
    if ((this.opts.exposure ?? "silent") !== "exclusive") return 0;
    return hideFromAssistiveTech(this.host, this.opts.hideSelector ?? "canvas");
  }
  /**
   * Reconcile the mirror against `blocks`. Returns `true` when the DOM actually
   * changed — an identical document is a genuine no-op, so a repaint loop does
   * not churn the accessibility tree or move a reading cursor.
   *
   * A block is REUSED when its key and its projected content are unchanged, even
   * if it moved; only its coordinate is re-stamped. A block whose projection
   * changed is rebuilt, and focus inside it is lost — that is inherent, and it
   * is exactly why the reconciliation is per-block and not a whole-tree replace.
   */
  update(blocks) {
    const art = this.art;
    const existing = /* @__PURE__ */ new Map();
    for (const el of Array.from(art.children)) {
      const k = el.getAttribute(KEY_ATTR);
      if (k != null) existing.set(k, el);
    }
    let changed = false;
    const next = [];
    for (const b of blocks) {
      const key = keyOf(b);
      const prev = existing.get(key);
      if (prev !== void 0 && SIGNATURES.get(prev) === signatureOf(b)) {
        existing.delete(key);
        if (stampCoordinates(prev, b)) changed = true;
        next.push(prev);
        continue;
      }
      if (prev !== void 0) existing.delete(key);
      next.push(buildBlock(b, this.opts, this.doc));
      changed = true;
    }
    for (let i = 0; i < next.length; i++) {
      const want = next[i];
      if (want === void 0) continue;
      if (art.children[i] !== want) {
        art.insertBefore(want, art.children[i] ?? null);
        changed = true;
      }
    }
    while (art.children.length > next.length) {
      const extra = art.children[next.length];
      if (extra == null) break;
      extra.remove();
      changed = true;
    }
    if (this.enforceExposure() > 0) changed = true;
    return changed;
  }
  /**
   * Reconcile from a whole projection, REPORTING every block that had no
   * transport form before mirroring what did. This is the entry point a mount
   * should use: {@link update} alone cannot tell the host that content was
   * dropped, and a mirror that silently omits content while claiming to be the
   * document is the worst possible accessibility outcome.
   */
  updateFrom(result) {
    const report = this.opts.onUnprojectable;
    if (report != null) for (const u of result.unprojectable) report(u.ord, u.reason);
    return this.update(result.blocks);
  }
  /** Every comment id in the mirror, in reading order. */
  commentIds() {
    return commentIdsIn(this.art);
  }
  /** Put the AT cursor on a comment anchor, announcing the move when an
   *  announcer is wired. `false` when the anchor is not in the mirror (an
   *  orphan) — the caller must not pretend it succeeded. */
  focusComment(commentId) {
    const safe = mirrorIdToken(commentId);
    if (safe == null) return false;
    const sel = `mark[data-comment-id="${escapeSelectorValue(safe)}"]`;
    const mark = this.art.querySelector(sel);
    if (!safeFocus(mark)) return false;
    const ids = this.commentIds();
    const at = ids.indexOf(safe);
    if (at !== -1) this.opts.announce?.(`Comment ${at + 1} of ${ids.length}`);
    return true;
  }
  /** The next comment in reading order, focused. `null` when there is none. */
  nextComment(from) {
    return this.step(from, 1);
  }
  /** The previous comment in reading order, focused. `null` when there is none. */
  prevComment(from) {
    return this.step(from, -1);
  }
  step(from, dir) {
    const target = stepComment(this.commentIds(), from, dir);
    if (target == null) return null;
    return this.focusComment(target) ? target : null;
  }
  /** The text a screen reader reads out of this mirror, in reading order. */
  plainText() {
    return mirrorPlainText(this.art);
  }
  /** Remove the mirror from the host. Rollback is exactly this. */
  destroy() {
    this.art.remove();
  }
};

// src/block-address.ts
var ADDRESS_SEP = "::";
var LIST_ITEM_RE = /^li:(\d+)$/;
var QUOTE_CHILD_RE = /^bq:(\d+)$/;
var HEADER_CELL_RE = /^th:(\d+)$/;
var BODY_CELL_RE = /^td:(\d+):(\d+)$/;
function encodeAddressSegment(seg) {
  switch (seg.kind) {
    case "listItem":
      return `li:${seg.index}`;
    case "quoteChild":
      return `bq:${seg.index}`;
    case "tableCell":
      return seg.header ? `th:${seg.col}` : `td:${seg.row}:${seg.col}`;
  }
}
function decodeAddressSegment(token) {
  const li = LIST_ITEM_RE.exec(token);
  if (li?.[1] != null) return { kind: "listItem", index: Number(li[1]) };
  const bq = QUOTE_CHILD_RE.exec(token);
  if (bq?.[1] != null) return { kind: "quoteChild", index: Number(bq[1]) };
  const th = HEADER_CELL_RE.exec(token);
  if (th?.[1] != null) return { kind: "tableCell", header: true, row: 0, col: Number(th[1]) };
  const td = BODY_CELL_RE.exec(token);
  if (td?.[1] != null && td[2] != null)
    return { kind: "tableCell", header: false, row: Number(td[1]), col: Number(td[2]) };
  return null;
}
function formatBlockAddress(rootId, segments) {
  let out = rootId;
  for (const seg of segments) out += ADDRESS_SEP + encodeAddressSegment(seg);
  return out;
}
function parseBlockAddress(id) {
  const parts = id.split(ADDRESS_SEP);
  const segments = [];
  let end = parts.length;
  while (end > 1) {
    const seg = decodeAddressSegment(parts[end - 1]);
    if (!seg) break;
    segments.unshift(seg);
    end -= 1;
  }
  return { rootId: parts.slice(0, end).join(ADDRESS_SEP), segments };
}
function isSubBlockAddress(id) {
  return parseBlockAddress(id).segments.length > 0;
}
function listItemAddress(listId, index) {
  return formatBlockAddress(listId, [{ kind: "listItem", index }]);
}
function quoteChildAddress(quoteId, index) {
  return formatBlockAddress(quoteId, [{ kind: "quoteChild", index }]);
}
function tableCellAddress(tableId, cell) {
  return formatBlockAddress(tableId, [
    { kind: "tableCell", header: cell.header, row: cell.header ? 0 : cell.row, col: cell.col }
  ]);
}

// src/abi.ts
var UTF8_ENCODER = new TextEncoder();
var UTF8_DECODER = new TextDecoder();
var ByteWriter = class {
  chunks = [];
  len = 0;
  push(bytes) {
    this.chunks.push(bytes);
    this.len += bytes.length;
  }
  /** Append a `u32` (little-endian). */
  u32(v) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, v >>> 0, true);
    this.push(b);
    return this;
  }
  /** Append a `u8`. */
  u8(v) {
    this.push(Uint8Array.of(v & 255));
    return this;
  }
  /** Append a `f64` (little-endian IEEE-754). */
  f64(v) {
    const b = new Uint8Array(8);
    new DataView(b.buffer).setFloat64(0, v, true);
    this.push(b);
    return this;
  }
  /** Append a `f32` (little-endian IEEE-754 — image dimensions, column widths). */
  f32(v) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setFloat32(0, v, true);
    this.push(b);
    return this;
  }
  /** Append a `u16` (little-endian — table cell col_span). */
  u16(v) {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setUint16(0, v & 65535, true);
    this.push(b);
    return this;
  }
  /** Append raw bytes verbatim (no length prefix). */
  bytes(b) {
    this.push(b);
    return this;
  }
  /** Append a length-prefixed UTF-8 string (`u32 len | bytes`). */
  str(s) {
    const b = UTF8_ENCODER.encode(s);
    return this.u32(b.length).bytes(b);
  }
  /** Materialise the accumulated bytes into one contiguous `Uint8Array`. */
  finish() {
    const out = new Uint8Array(this.len);
    let o = 0;
    for (const c of this.chunks) {
      out.set(c, o);
      o += c.length;
    }
    return out;
  }
};
var ByteReader = class {
  constructor(bytes) {
    this.bytes = bytes;
  }
  bytes;
  p = 0;
  need(n) {
    if (this.p + n > this.bytes.length) {
      throw new RangeError("web-docz: truncated ABI buffer");
    }
  }
  get view() {
    return new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
  }
  /** Read a `u32` (little-endian). */
  u32() {
    this.need(4);
    const v = this.view.getUint32(this.p, true);
    this.p += 4;
    return v;
  }
  /** Read a `u8`. */
  u8() {
    this.need(1);
    const v = this.bytes[this.p];
    this.p += 1;
    return v;
  }
  /** Read a `u16` (little-endian — table cell col_span). */
  u16() {
    this.need(2);
    const v = this.view.getUint16(this.p, true);
    this.p += 2;
    return v;
  }
  /** Read a `f32` (little-endian IEEE-754 — column widths, image dims, borders). */
  f32() {
    this.need(4);
    const v = this.view.getFloat32(this.p, true);
    this.p += 4;
    return v;
  }
  /** Read a `f64` (little-endian IEEE-754). */
  f64() {
    this.need(8);
    const v = this.view.getFloat64(this.p, true);
    this.p += 8;
    return v;
  }
  /** Read `n` raw bytes (a fresh copy). */
  take(n) {
    this.need(n);
    const s = this.bytes.slice(this.p, this.p + n);
    this.p += n;
    return s;
  }
  /** Read a length-prefixed UTF-8 string (`u32 len | bytes`). */
  str() {
    const n = this.u32();
    this.need(n);
    const s = UTF8_DECODER.decode(this.bytes.subarray(this.p, this.p + n));
    this.p += n;
    return s;
  }
  /** Bytes consumed so far (for parity assertions). */
  get offset() {
    return this.p;
  }
  /** Bytes not yet consumed. `0` = the buffer is drained. */
  get remaining() {
    return this.bytes.length - this.p;
  }
  /**
   * Take everything from `start` (an offset previously observed via
   * {@link offset}) to the END of the buffer, and drain the reader.
   *
   * This is the PASSTHROUGH primitive for a block the reader cannot interpret.
   * The docz block stream is positional (`u32 n | block*`) with NO per-block
   * length prefix, so an unrecognised tag is UNSKIPPABLE — the reader cannot
   * measure where that block ends, and therefore cannot find the next one.
   * Rather than throw (which loses the whole document) or drop (which loses the
   * bytes on the next save), the caller stands the remaining bytes down
   * verbatim and re-emits them unchanged.
   *
   * `start` may be BEFORE the current position — a nested reader may already
   * have drained the buffer, and the enclosing container re-claims the span
   * from its own tag byte. Calling it twice with the same `start` yields the
   * same bytes.
   */
  restFrom(start) {
    const from = Math.max(0, Math.min(start, this.bytes.length));
    const out = this.bytes.slice(from);
    this.p = this.bytes.length;
    return out;
  }
};
function toHex(bytes) {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

// src/para-props.ts
var TEXT_ALIGN_TO_ORD = {
  start: 0,
  center: 1,
  end: 2,
  justify: 3
};
var TAB_ALIGN_TO_ORD = {
  left: 0,
  center: 1,
  right: 2,
  decimal: 3,
  bar: 4
};
var TAB_LEADER_TO_ORD = {
  none: 0,
  dot: 1,
  hyphen: 2,
  underscore: 3,
  middle_dot: 4
};
var LINE_RULE_TO_ORD = { multiple: 0, atLeast: 1, exact: 2 };
var DROP_CAP_KIND_TO_ORD = { dropped: 0, in_margin: 1 };
var ORD_TO_DROP_CAP_KIND = ["dropped", "in_margin"];
function isDefaultParaProps(p) {
  const ind = p.indent;
  const indentIsZero = ind === void 0 || (ind.leftPt ?? 0) === 0 && (ind.rightPt ?? 0) === 0 && (ind.firstLinePt ?? 0) === 0 && (ind.hangingPt ?? 0) === 0;
  return p.textAlign === void 0 && indentIsZero && p.lineSpacing === void 0 && p.spaceBeforePt === void 0 && p.spaceAfterPt === void 0 && (p.tabStops === void 0 || p.tabStops.length === 0) && // FEATURE BUNDLE 1 — presence of the object gates the mask bit (docz's
  // `props.borders != null` / `props.shading != null` null-check semantics),
  // so ANY borders/shading (even an all-empty `ParaBorders{}`) is non-default.
  p.borders === void 0 && p.shading === void 0 && // FEATURE BUNDLE 3 (3A) — presence of the drop-cap object gates bit6.
  p.dropCap === void 0;
}
function writeParaProps(w, p) {
  let mask = 0;
  if (p.textAlign !== void 0) mask |= 1;
  if (p.lineSpacing !== void 0) mask |= 2;
  if (p.spaceBeforePt !== void 0) mask |= 4;
  if (p.spaceAfterPt !== void 0) mask |= 8;
  if (p.borders !== void 0) mask |= 16;
  if (p.shading !== void 0) mask |= 32;
  if (p.dropCap !== void 0) mask |= 64;
  w.u8(mask);
  if (p.textAlign !== void 0) w.u8(TEXT_ALIGN_TO_ORD[p.textAlign]);
  const ind = p.indent ?? {};
  w.f32(ind.leftPt ?? 0).f32(ind.rightPt ?? 0).f32(ind.firstLinePt ?? 0).f32(ind.hangingPt ?? 0);
  if (p.lineSpacing !== void 0) {
    w.u8(LINE_RULE_TO_ORD[p.lineSpacing.rule]).f32(p.lineSpacing.value);
  }
  if (p.spaceBeforePt !== void 0) w.f32(p.spaceBeforePt);
  if (p.spaceAfterPt !== void 0) w.f32(p.spaceAfterPt);
  const tabs = p.tabStops ?? [];
  w.u32(tabs.length);
  for (const t of tabs) {
    w.f32(t.posPt).u8(TAB_ALIGN_TO_ORD[t.align ?? "left"]).u8(TAB_LEADER_TO_ORD[t.leader ?? "none"]);
  }
  if (p.borders !== void 0) {
    writeBorderSide(w, p.borders.top);
    writeBorderSide(w, p.borders.left);
    writeBorderSide(w, p.borders.bottom);
    writeBorderSide(w, p.borders.right);
    writeBorderSide(w, p.borders.between);
  }
  if (p.shading !== void 0) w.str(p.shading);
  if (p.dropCap !== void 0) {
    w.u8(p.dropCap.lines & 255).u8(DROP_CAP_KIND_TO_ORD[p.dropCap.kind]).f32(p.dropCap.distancePt);
  }
}
function writeParaPropsGate(w, p) {
  if (p === void 0 || isDefaultParaProps(p)) {
    w.u8(0);
  } else {
    w.u8(1);
    writeParaProps(w, p);
  }
}
var ORD_TO_TEXT_ALIGN = ["start", "center", "end", "justify"];
var ORD_TO_TAB_ALIGN = ["left", "center", "right", "decimal", "bar"];
var ORD_TO_TAB_LEADER = ["none", "dot", "hyphen", "underscore", "middle_dot"];
var ORD_TO_LINE_RULE = ["multiple", "atLeast", "exact"];
function readParaProps(r) {
  const mask = r.u8();
  const textAlign = (mask & 1) !== 0 ? ORD_TO_TEXT_ALIGN[r.u8()] : void 0;
  const indent = {
    leftPt: r.f32(),
    rightPt: r.f32(),
    firstLinePt: r.f32(),
    hangingPt: r.f32()
  };
  const lineSpacing = (mask & 2) !== 0 ? { rule: ORD_TO_LINE_RULE[r.u8()] ?? "multiple", value: r.f32() } : void 0;
  const spaceBeforePt = (mask & 4) !== 0 ? r.f32() : void 0;
  const spaceAfterPt = (mask & 8) !== 0 ? r.f32() : void 0;
  const nTabs = r.u32();
  const tabStops = [];
  for (let i = 0; i < nTabs; i++) {
    const posPt = r.f32();
    const align = ORD_TO_TAB_ALIGN[r.u8()] ?? "left";
    const leader = ORD_TO_TAB_LEADER[r.u8()] ?? "none";
    tabStops.push({ posPt, align, leader });
  }
  const borders = (mask & 16) !== 0 ? {
    top: readBorderSide(r),
    left: readBorderSide(r),
    bottom: readBorderSide(r),
    right: readBorderSide(r),
    between: readBorderSide(r)
  } : void 0;
  const shading = (mask & 32) !== 0 ? r.str() : void 0;
  const dropCap = (mask & 64) !== 0 ? {
    lines: r.u8(),
    kind: ORD_TO_DROP_CAP_KIND[r.u8()] ?? "dropped",
    distancePt: r.f32()
  } : void 0;
  return {
    ...textAlign !== void 0 ? { textAlign } : {},
    indent,
    ...lineSpacing !== void 0 ? { lineSpacing } : {},
    ...spaceBeforePt !== void 0 ? { spaceBeforePt } : {},
    ...spaceAfterPt !== void 0 ? { spaceAfterPt } : {},
    ...tabStops.length > 0 ? { tabStops } : {},
    ...borders !== void 0 ? { borders } : {},
    ...shading !== void 0 ? { shading } : {},
    ...dropCap !== void 0 ? { dropCap } : {}
  };
}
function readParaPropsGate(r) {
  return r.u8() === 1 ? readParaProps(r) : void 0;
}

// src/mark-codec.ts
var MARK_TYPE_TO_ORD = {
  bold: 0,
  italic: 1,
  underline: 2,
  strikeout: 3,
  smallcaps: 4,
  superscript: 5,
  subscript: 6,
  color: 7,
  highlight: 8,
  code: 9,
  link: 10,
  comment: 11,
  font_family: 12,
  font_size: 13,
  all_caps: 14,
  letter_spacing: 15,
  // FEATURE BUNDLE 3 W2 char-decoration marks (docz MarkType @intFromEnum 16/17/18).
  text_effects: 16,
  char_border: 17,
  emphasis: 18,
  // FEATURE BUNDLE 4 (1E) references — invisible anchor marks (docz MarkType 19/20).
  index_entry: 19,
  toa_citation: 20
};
var ORD_TO_MARK_TYPE = [
  "bold",
  "italic",
  "underline",
  "strikeout",
  "smallcaps",
  "superscript",
  "subscript",
  "color",
  "highlight",
  "code",
  "link",
  "comment",
  "font_family",
  "font_size",
  "all_caps",
  "letter_spacing",
  "text_effects",
  "char_border",
  "emphasis",
  "index_entry",
  "toa_citation"
];
var INDEX_ENTRY_KIND_TO_ORD = {
  main: 0,
  sub: 1,
  cross_ref: 2,
  range: 3
};
var ORD_TO_INDEX_ENTRY_KIND = [
  "main",
  "sub",
  "cross_ref",
  "range"
];
var CHAR_BORDER_STYLE_TO_ORD = {
  solid: 0,
  dashed: 1,
  dotted: 2,
  double: 3
};
var ORD_TO_CHAR_BORDER_STYLE = [
  "solid",
  "dashed",
  "dotted",
  "double"
];
var EMPHASIS_TO_ORD = {
  dot: 1,
  comma: 2,
  circle: 3,
  under_dot: 4
};
var ORD_TO_EMPHASIS = [
  "dot",
  // index 0 unused (docz `.none`); a valued mark starts at 1
  "dot",
  "comma",
  "circle",
  "under_dot"
];
var UNDERLINE_STYLE_TO_ORD = {
  single: 1,
  double: 2,
  dotted: 3,
  dashed: 4,
  wavy: 5,
  thick: 6
};
var ORD_TO_UNDERLINE_STYLE = [
  void 0,
  "single",
  "double",
  "dotted",
  "dashed",
  "wavy",
  "thick"
];
function readF32(r) {
  const b = r.take(4);
  return new DataView(b.buffer, b.byteOffset, 4).getFloat32(0, true);
}
function parseSrgb(css) {
  const s = css.trim();
  const hex = s.startsWith("#") ? s.slice(1) : "";
  if (hex.length === 3) {
    const c = (i) => Number.parseInt(hex[i].repeat(2), 16);
    return { r: c(0) || 0, g: c(1) || 0, b: c(2) || 0 };
  }
  if (hex.length === 6) {
    const c = (i) => Number.parseInt(hex.slice(i, i + 2), 16);
    return { r: c(0) || 0, g: c(2) || 0, b: c(4) || 0 };
  }
  const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (m)
    return { r: +m[1] & 255, g: +m[2] & 255, b: +m[3] & 255 };
  return { r: 0, g: 0, b: 0 };
}
function readSrgbHex(r) {
  const hex = (n) => n.toString(16).padStart(2, "0").toUpperCase();
  return `#${hex(r.u8())}${hex(r.u8())}${hex(r.u8())}`;
}
function writeColor(w, css) {
  const { r, g, b } = parseSrgb(css);
  w.u8(0).u8(r).u8(g).u8(b);
}
function readColor(r) {
  const kind = r.u8();
  if (kind === 0) return readSrgbHex(r);
  const l = readF32(r);
  const c2 = readF32(r);
  const c3 = readF32(r);
  return kind === 1 ? `oklch(${l} ${c2} ${c3})` : `oklab(${l} ${c2} ${c3})`;
}
function writeUnderline(w, m) {
  w.u8(m.style ? UNDERLINE_STYLE_TO_ORD[m.style] : 1);
  if (m.color) {
    const { r, g, b } = parseSrgb(m.color);
    w.u8(1).u8(r).u8(g).u8(b);
  } else {
    w.u8(0);
  }
}
function readUnderline(r) {
  const style = ORD_TO_UNDERLINE_STYLE[r.u8()];
  const color = r.u8() !== 0 ? readSrgbHex(r) : void 0;
  if ((style === void 0 || style === "single") && color === void 0)
    return { type: "underline" };
  return {
    type: "underline",
    ...style ? { style } : {},
    ...color ? { color } : {}
  };
}
function writeMark(w, m) {
  w.u8(MARK_TYPE_TO_ORD[m.type]);
  switch (m.type) {
    case "link":
      w.str(m.href);
      break;
    case "comment":
      w.str(m.commentId);
      break;
    case "color":
    case "highlight":
      writeColor(w, m.color);
      break;
    case "font_family":
      w.str(m.font);
      break;
    case "font_size":
      w.f32(m.sizePt);
      break;
    case "letter_spacing":
      w.f32(m.spacingPt);
      break;
    case "underline":
      writeUnderline(w, m);
      break;
    // FEATURE BUNDLE 3 W2 — char-decoration marks (byte-identical to docz
    // verbs.zig encodeMark).
    case "text_effects": {
      const e = m.effects;
      let flags = 0;
      if (e.shadow) flags |= 1;
      if (e.outline) flags |= 2;
      if (e.emboss) flags |= 4;
      if (e.engrave) flags |= 8;
      if (e.reflection) flags |= 16;
      w.u8(flags);
      w.str(e.glow ?? "");
      break;
    }
    case "char_border":
      w.f32(m.border.widthPt);
      w.u8(CHAR_BORDER_STYLE_TO_ORD[m.border.style]);
      w.str(m.border.color);
      break;
    case "emphasis":
      w.u8(EMPHASIS_TO_ORD[m.emphasis]);
      break;
    // FEATURE BUNDLE 4 (1E) references — invisible anchor payloads (byte-
    // identical to docz verbs.zig encodeMark: absent optionals use the
    // empty-string sentinel; decode maps '' → undefined).
    case "index_entry": {
      const e = m.entry;
      w.str(e.key);
      w.str(e.sub ?? "");
      w.str(e.crossRef ?? "");
      w.str(e.rangeBookmark ?? "");
      w.u8(INDEX_ENTRY_KIND_TO_ORD[e.kind ?? "main"]);
      break;
    }
    case "toa_citation": {
      const c = m.citation;
      w.u8(c.category ?? 1);
      w.str(c.longForm);
      w.str(c.shortForm ?? "");
      break;
    }
  }
}
function readMark(r) {
  const type = ORD_TO_MARK_TYPE[r.u8()] ?? "bold";
  switch (type) {
    case "link":
      return { type: "link", href: r.str() };
    case "comment":
      return { type: "comment", commentId: r.str() };
    case "color":
    case "highlight":
      return { type, color: readColor(r) };
    case "font_family":
      return { type: "font_family", font: r.str() };
    case "font_size":
      return { type: "font_size", sizePt: readF32(r) };
    case "letter_spacing":
      return { type: "letter_spacing", spacingPt: readF32(r) };
    case "underline":
      return readUnderline(r);
    // FEATURE BUNDLE 3 W2 — mirror writeMark for the char-decoration marks.
    case "text_effects": {
      const flags = r.u8();
      const glow = r.str();
      return {
        type: "text_effects",
        effects: {
          ...flags & 1 ? { shadow: true } : {},
          ...flags & 2 ? { outline: true } : {},
          ...flags & 4 ? { emboss: true } : {},
          ...flags & 8 ? { engrave: true } : {},
          ...flags & 16 ? { reflection: true } : {},
          ...glow.length > 0 ? { glow } : {}
        }
      };
    }
    case "char_border":
      return {
        type: "char_border",
        border: {
          widthPt: readF32(r),
          style: ORD_TO_CHAR_BORDER_STYLE[r.u8()] ?? "solid",
          color: r.str()
        }
      };
    case "emphasis":
      return { type: "emphasis", emphasis: ORD_TO_EMPHASIS[r.u8()] ?? "dot" };
    // FEATURE BUNDLE 4 (1E) references — mirror writeMark. Empty optional
    // strings (len 0) decode back to `undefined` (omitted).
    case "index_entry": {
      const key = r.str();
      const sub = r.str();
      const crossRef = r.str();
      const rangeBookmark = r.str();
      const kind = ORD_TO_INDEX_ENTRY_KIND[r.u8()] ?? "main";
      return {
        type: "index_entry",
        entry: {
          key,
          ...sub.length > 0 ? { sub } : {},
          ...crossRef.length > 0 ? { crossRef } : {},
          ...rangeBookmark.length > 0 ? { rangeBookmark } : {},
          kind
        }
      };
    }
    case "toa_citation": {
      const category = r.u8();
      const longForm = r.str();
      const shortForm = r.str();
      return {
        type: "toa_citation",
        citation: {
          category,
          longForm,
          ...shortForm.length > 0 ? { shortForm } : {}
        }
      };
    }
    default:
      return { type };
  }
}

// src/verb-codec.ts
var ANCHOR_KIND_TO_TAG = {
  text_range: 0,
  section: 1,
  document_scoped: 2
};
var TAG_TO_ANCHOR_KIND = ["text_range", "section", "document_scoped"];
var TAG_TO_REBASE_STATE = ["exact", "shifted", "repaired", "orphaned"];
var QUOTE_HASH_BYTES = 32;
function writeSelector(w, sel) {
  w.u8(ANCHOR_KIND_TO_TAG[sel.kind]);
  w.u32(sel.start);
  w.u32(sel.end);
  w.bytes(hexToBytes(sel.quoteHash));
  w.str(sel.exact);
  w.str(sel.prefix);
  w.str(sel.suffix);
  w.str(sel.baseVersionId);
}
function readSelector(r) {
  const kind = TAG_TO_ANCHOR_KIND[r.u8()] ?? "text_range";
  const start = r.u32();
  const end = r.u32();
  const quoteHash = toHex(r.take(QUOTE_HASH_BYTES));
  const exact = r.str();
  const prefix = r.str();
  const suffix = r.str();
  const baseVersionId = r.str();
  return { kind, exact, prefix, suffix, start, end, quoteHash, baseVersionId };
}
function encodeBuildSelectorInput(input) {
  return new ByteWriter().str(input.text).u32(input.start).u32(input.end).u8(ANCHOR_KIND_TO_TAG[input.kind ?? "text_range"]).str(input.baseVersionId ?? "").finish();
}
function decodeSelector(bytes) {
  return readSelector(new ByteReader(bytes));
}
function encodeRebaseInput(input) {
  const w = new ByteWriter();
  writeSelector(w, input.selector);
  w.str(input.newText);
  if (input.hint) {
    w.u8(1).u32(input.hint.start).u32(input.hint.end);
  } else {
    w.u8(0).u32(0).u32(0);
  }
  return w.finish();
}
function decodeRebaseOutcome(bytes) {
  const r = new ByteReader(bytes);
  const state = TAG_TO_REBASE_STATE[r.u8()] ?? "orphaned";
  const start = r.u32();
  const end = r.u32();
  const confidence = r.f64();
  const range = state === "orphaned" ? null : { start, end };
  return { state, range, confidence, needsAudit: state !== "exact" };
}
function writeRuns(w, runs) {
  w.u32(runs.length);
  for (const run of runs) {
    w.str(run.text);
    w.u32(run.marks.length);
    for (const m of run.marks) writeMark(w, m);
  }
}
function readRuns(r) {
  const n = r.u32();
  const runs = [];
  for (let i = 0; i < n; i++) {
    const text = r.str();
    const nMarks = r.u32();
    const marks = [];
    for (let j = 0; j < nMarks; j++) marks.push(readMark(r));
    runs.push({ text, marks });
  }
  return runs;
}
function decodeRuns(bytes) {
  return readRuns(new ByteReader(bytes));
}
function encodeApplyMarkInput(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  w.str(input.commentId).u32(input.start).u32(input.end);
  return w.finish();
}
function encodeSplitRunsInput(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  w.u32(input.offsets.length);
  for (const o of input.offsets) w.u32(o);
  return w.finish();
}
function encodeEditTextInput(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  w.u32(input.start).u32(input.end).str(input.replacement);
  return w.finish();
}
function encodeApplyStyleMarkInput(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  writeMark(w, input.mark);
  w.u32(input.start).u32(input.end);
  return w.finish();
}
var encodeToggleStyleMarkInput = encodeApplyStyleMarkInput;
var BLOCK_KIND_TO_TAG = {
  paragraph: 0,
  heading: 1,
  list_item: 2,
  blockquote: 3,
  code: 4,
  html: 5,
  table_cell: 0
};
function encodeRenderRunsInput(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  w.u8(BLOCK_KIND_TO_TAG[input.kind ?? "paragraph"]).u8((input.level ?? 0) & 255);
  return w.finish();
}
function encodeRenderRunsInputWithProps(input) {
  const w = new ByteWriter();
  writeRuns(w, input.runs);
  w.u8(BLOCK_KIND_TO_TAG[input.kind ?? "paragraph"]).u8((input.level ?? 0) & 255);
  writeParaPropsGate(w, input.props);
  return w.finish();
}
function hexToBytes(hex) {
  const out = new Uint8Array(QUOTE_HASH_BYTES);
  const clean = hex.length % 2 === 0 ? hex : `0${hex}`;
  const n = Math.min(QUOTE_HASH_BYTES, clean.length >> 1);
  for (let i = 0; i < n; i++) {
    out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16) || 0;
  }
  return out;
}

// src/block-dto.ts
var LIST_STYLE_TO_ORD = {
  bullet: 0,
  decimal: 1,
  lower_alpha: 2,
  upper_alpha: 3,
  lower_roman: 4,
  upper_roman: 5,
  decimal_leading_zero: 6
};
var COLUMN_ALIGN_TO_ORD = { left: 0, center: 1, right: 2 };
var V_MERGE_TO_ORD = { none: 0, restart: 1, continue: 2 };
var V_ALIGN_TO_ORD = { top: 0, middle: 1, bottom: 2 };
var CELL_ALIGN_TO_ORD = { start: 0, center: 1, end: 2 };
var BORDER_STYLE_TO_ORD = {
  solid: 0,
  dashed: 1,
  dotted: 2,
  double: 3
};
var BlockTag = {
  Paragraph: 0,
  Heading: 1,
  List: 2,
  Table: 3,
  Image: 4,
  ImageSvg: 5,
  BlockQuote: 6,
  CodeBlock: 7,
  // 8 = header_row (a table sub-structure, not a top-level palette block).
  HorizontalRule: 9,
  // --- blitz-10 transport widening (APPENDED — never renumber the above) ---
  PageBreak: 10,
  Spacer: 11,
  Text: 12,
  Figure: 13,
  Note: 14,
  Field: 15,
  Bookmark: 16,
  ReferenceMark: 17,
  RawHtml: 18,
  RawOoxml: 19,
  RawSvg: 20,
  Generated: 21
};
var CAPTION_PLACEMENT_TO_ORD = { above: 0, below: 1 };
var NOTE_KIND_TO_ORD = { footnote: 0, endnote: 1 };
var NOTE_MARKER_TO_ORD = { auto_number: 0, custom: 1 };
var FIELD_KIND_TO_ORD = {
  page_number: 0,
  page_count: 1,
  page_ref: 2,
  section_number: 3,
  sequence: 4,
  date: 5,
  doc_property: 6
};
var NUMBER_FORMAT_TO_ORD = {
  arabic: 0,
  roman_lower: 1,
  roman_upper: 2,
  alpha_lower: 3,
  alpha_upper: 4,
  decimal_zero: 5
};
var REF_LABEL_TO_ORD = {
  number_only: 0,
  full_label: 1,
  page: 2,
  above_below: 3
};
var GENERATED_KIND_TO_ORD = {
  toc: 0,
  index: 1,
  bibliography: 2,
  list_of_figures: 3,
  list_of_tables: 4,
  table_of_authorities: 5
};
var LENGTH_UNIT_TO_ORD = {
  pt: 1,
  percent: 2,
  auto: 3,
  fr: 4
};
var SPEC_KIND_TO_ORD = {
  toc: 0,
  index: 1,
  bibliography: 2,
  tableOfAuthorities: 3
};
function writeOptStr(w, s) {
  if (s === void 0) {
    w.u8(0);
    return;
  }
  w.u8(1).str(s);
}
function writeOptFont(w, f) {
  if (f === void 0) {
    w.u8(0);
    return;
  }
  w.u8(1).str(f.cssFamily).u16(f.weight).u8(f.italic ? 1 : 0);
  if (f.pdfzHandle === void 0) {
    w.u8(0);
  } else {
    w.u8(1).u32(f.pdfzHandle);
  }
}
function writeOptLength(w, l) {
  if (l === void 0) {
    w.u8(0);
    return;
  }
  w.u8(LENGTH_UNIT_TO_ORD[l.unit]);
  if (l.unit !== "auto") w.f32(l.value);
}
function writeOptCaption(w, c) {
  if (c === void 0) {
    w.u8(0);
    return;
  }
  w.u8(1);
  writeRuns(w, c.runs);
  w.u8(CAPTION_PLACEMENT_TO_ORD[c.placement]);
}
function writeGeneratedSpec(w, spec) {
  w.u8(SPEC_KIND_TO_ORD[spec.specKind]);
  switch (spec.specKind) {
    case "toc":
      w.u8(spec.maxDepth & 255).u32(spec.fromOutlineLevels.length);
      for (const lvl of spec.fromOutlineLevels) w.u8(lvl & 255);
      w.u32(spec.entryStyleMap.length);
      for (const es of spec.entryStyleMap) w.u8(es.level & 255).str(es.styleName);
      writeOptStr(w, spec.fromCounter);
      return;
    case "index":
      w.u8(spec.columns & 255);
      writeOptStr(w, spec.entryStyle);
      return;
    case "bibliography":
      writeOptStr(w, spec.cslStyleRef);
      writeOptStr(w, spec.sourcesRef);
      return;
    case "tableOfAuthorities":
      w.u8(spec.category & 255);
      writeOptStr(w, spec.entryStyle);
      return;
  }
}
function writeBlockSlice(w, blocks) {
  writeBlockList(w, blocks);
}
function writeBorderSide(w, side) {
  if (side === void 0) {
    w.u8(0);
    return;
  }
  w.u8(1).f32(side.widthPt ?? 0.5).str(side.color ?? "#94A3B8").u8(BORDER_STYLE_TO_ORD[side.style ?? "solid"]);
}
function writeCellChannels(w, c) {
  w.u8(V_MERGE_TO_ORD[c.vMerge ?? "none"]);
  if (c.shading !== void 0) {
    w.u8(1).str(c.shading);
  } else {
    w.u8(0);
  }
  w.u8(c.vAlign !== void 0 ? V_ALIGN_TO_ORD[c.vAlign] + 1 : 0);
  w.u8(c.alignH !== void 0 ? CELL_ALIGN_TO_ORD[c.alignH] + 1 : 0);
  if (c.borders !== void 0) {
    w.u8(1);
    writeBorderSide(w, c.borders.top);
    writeBorderSide(w, c.borders.left);
    writeBorderSide(w, c.borders.bottom);
    writeBorderSide(w, c.borders.right);
  } else {
    w.u8(0);
  }
}
function writeBordersArg(w, borders) {
  if (borders === void 0) {
    w.u8(0);
    return;
  }
  w.u8(1);
  writeBorderSide(w, borders.top);
  writeBorderSide(w, borders.left);
  writeBorderSide(w, borders.bottom);
  writeBorderSide(w, borders.right);
}
function writeRow(w, row) {
  w.u32(row.cells.length);
  for (const c of row.cells) {
    w.u16(c.colSpan && c.colSpan > 0 ? c.colSpan : 1);
    writeRuns(w, c.runs);
    writeCellChannels(w, c);
  }
}
function writeTable(w, columns, header, rows, rowHeadColumns) {
  w.u32(columns.length);
  for (const col of columns) {
    w.u8(COLUMN_ALIGN_TO_ORD[col.align ?? "left"]);
    w.u8(col.widthKind === "fixed" ? 0 : 1);
    w.f32(col.width ?? 1 / Math.max(1, columns.length));
  }
  if (header) {
    w.u8(1);
    writeRow(w, header);
  } else {
    w.u8(0);
  }
  w.u32(rows.length);
  for (const r of rows) writeRow(w, r);
  w.u32(rowHeadColumns > 0 ? rowHeadColumns : 0);
}
function listItemRuns(item) {
  return Array.isArray(item) ? item : item.runs;
}
function checkedFlagByte(checked) {
  if (checked === null || checked === void 0) return 0;
  return checked ? 2 : 1;
}
function writeBlock(w, block) {
  switch (block.kind) {
    case "paragraph":
      w.u8(BlockTag.Paragraph);
      writeRuns(w, block.runs);
      writeParaPropsGate(w, block.props);
      return;
    case "heading":
      w.u8(BlockTag.Heading).u8(Math.min(6, Math.max(1, block.level)) & 255);
      writeRuns(w, block.runs);
      writeParaPropsGate(w, block.props);
      return;
    case "list": {
      w.u8(BlockTag.List).u8(LIST_STYLE_TO_ORD[block.style]);
      let flags = 0;
      if (block.numId !== void 0) flags |= 1;
      if (block.startOverride !== void 0) flags |= 2;
      if (block.bulletChar !== void 0) flags |= 4;
      w.u8(flags);
      if (block.numId !== void 0) w.str(block.numId);
      if (block.startOverride !== void 0) w.u32(block.startOverride);
      if (block.bulletChar !== void 0) w.str(block.bulletChar);
      w.u32(block.items.length);
      for (const item of block.items) {
        const level = Array.isArray(item) ? 0 : item.level ?? 0;
        const checked = Array.isArray(item) ? null : item.checked;
        w.u8(level & 255).u8(checkedFlagByte(checked));
        writeRuns(w, listItemRuns(item));
      }
      return;
    }
    case "table":
      w.u8(BlockTag.Table);
      writeTable(w, block.columns, block.header, block.rows, block.rowHeadColumns ?? 0);
      return;
    case "image":
      w.u8(BlockTag.Image).u8(block.srcKind === "bytes" ? 1 : 0).str(block.src);
      w.f32(block.width ?? 0).f32(block.height ?? 0).str(block.alt ?? "");
      return;
    case "imageSvg":
      w.u8(BlockTag.ImageSvg).str(block.svg);
      w.f32(block.width ?? 0).f32(block.height ?? 0).str(block.alt ?? "");
      return;
    case "blockquote":
      w.u8(BlockTag.BlockQuote).u32(block.children.length);
      for (const child of block.children) writeBlock(w, child);
      return;
    case "code":
      w.u8(BlockTag.CodeBlock).str(block.language ?? "").str(block.code);
      return;
    case "horizontalRule":
      w.u8(BlockTag.HorizontalRule).f32(block.widthPct ?? 100).f32(block.thicknessPt ?? 0.5).str(block.color ?? "#94A3B8").u8(BORDER_STYLE_TO_ORD[block.style ?? "solid"]).u8(TEXT_ALIGN_TO_ORD[block.alignment ?? "center"]);
      return;
    // ─── blitz-10 transport widening (tags 10..21) ───────────────────────────
    case "pageBreak":
      w.u8(BlockTag.PageBreak);
      return;
    case "spacer":
      w.u8(BlockTag.Spacer).f32(block.heightPt);
      return;
    case "text":
      w.u8(BlockTag.Text);
      writeRuns(w, block.runs);
      writeOptFont(w, block.font);
      writeOptLength(w, block.maxW);
      return;
    case "figure":
      w.u8(BlockTag.Figure);
      writeBlockSlice(w, block.content);
      writeOptCaption(w, block.caption);
      writeOptStr(w, block.counterName);
      writeOptStr(w, block.figureId);
      return;
    case "note":
      w.u8(BlockTag.Note).u8(NOTE_KIND_TO_ORD[block.noteKind]).u8(NOTE_MARKER_TO_ORD[block.marker]);
      if (block.customMarker !== void 0) {
        w.u8(1);
        writeRuns(w, block.customMarker);
      } else {
        w.u8(0);
      }
      writeBlockSlice(w, block.body);
      writeOptStr(w, block.callStyle);
      writeOptStr(w, block.markerStyle);
      return;
    case "field":
      w.u8(BlockTag.Field).u8(FIELD_KIND_TO_ORD[block.fieldKind]);
      writeOptStr(w, block.target);
      writeOptStr(w, block.counterName);
      w.u8(NUMBER_FORMAT_TO_ORD[block.format]);
      writeOptStr(w, block.cachedValue);
      w.u8(REF_LABEL_TO_ORD[block.refLabel]);
      return;
    case "bookmark":
      w.u8(BlockTag.Bookmark).str(block.bookmarkId);
      return;
    case "referenceMark":
      w.u8(BlockTag.ReferenceMark).str(block.refId);
      return;
    case "rawHtml":
      w.u8(BlockTag.RawHtml).str(block.html);
      return;
    case "rawOoxml":
      w.u8(BlockTag.RawOoxml).str(block.xml);
      return;
    case "rawSvg":
      w.u8(BlockTag.RawSvg).str(block.svg);
      return;
    case "generated":
      w.u8(BlockTag.Generated).u8(GENERATED_KIND_TO_ORD[block.generatedKind]);
      writeGeneratedSpec(w, block.spec);
      writeBlockSlice(w, block.cachedBlocks);
      return;
    // ─── forward compatibility ───────────────────────────────────────────────
    case "unknown":
      w.bytes(block.bytes);
      return;
  }
}
function encodeBlock(block) {
  const w = new ByteWriter();
  writeBlock(w, block);
  return w.finish();
}
function writeBlockList(w, blocks) {
  let slots = 0;
  for (const b of blocks) slots += b.kind === "unknown" ? b.blockCount : 1;
  w.u32(slots);
  for (const b of blocks) writeBlock(w, b);
}
function encodeBlockList(blocks) {
  const w = new ByteWriter();
  writeBlockList(w, blocks);
  return w.finish();
}
function writeTablePayload(w, table) {
  writeTable(w, table.columns, table.header, table.rows, table.rowHeadColumns ?? 0);
}
function encodeTablePayload(table) {
  const w = new ByteWriter();
  writeTablePayload(w, table);
  return w.finish();
}
function textCell(text) {
  return { runs: [{ text, marks: [] }] };
}
function emptyTable(rows, cols) {
  const columns = Array.from({ length: cols }, () => ({ align: "left" }));
  const mkRow = () => ({ cells: Array.from({ length: cols }, () => textCell("")) });
  const header = mkRow();
  const body = Array.from({ length: Math.max(0, rows - 1) }, mkRow);
  return { kind: "table", columns, header, rows: body };
}
var ORD_TO_LIST_STYLE = [
  "bullet",
  "decimal",
  "lower_alpha",
  "upper_alpha",
  "lower_roman",
  "upper_roman",
  "decimal_leading_zero"
];
var ORD_TO_COLUMN_ALIGN = ["left", "center", "right"];
var ORD_TO_V_MERGE = ["none", "restart", "continue"];
var ORD_TO_V_ALIGN = ["top", "middle", "bottom"];
var ORD_TO_CELL_ALIGN = ["start", "center", "end"];
var ORD_TO_BORDER_STYLE = ["solid", "dashed", "dotted", "double"];
var TAG_TO_KIND = [
  "paragraph",
  "heading",
  "list",
  "table",
  "image",
  "imageSvg",
  "blockquote",
  "code",
  void 0,
  // 8 = header_row (a table sub-structure; not a top-level palette block)
  "horizontalRule",
  // 9 = FEATURE BUNDLE 1
  // --- blitz-10 transport widening (APPENDED — never renumber the above) ---
  "pageBreak",
  // 10
  "spacer",
  // 11
  "text",
  // 12
  "figure",
  // 13
  "note",
  // 14
  "field",
  // 15
  "bookmark",
  // 16
  "referenceMark",
  // 17
  "rawHtml",
  // 18
  "rawOoxml",
  // 19
  "rawSvg",
  // 20
  "generated"
  // 21
];
var ORD_TO_CAPTION_PLACEMENT = ["above", "below"];
var ORD_TO_NOTE_KIND = ["footnote", "endnote"];
var ORD_TO_NOTE_MARKER = ["auto_number", "custom"];
var ORD_TO_FIELD_KIND = [
  "page_number",
  "page_count",
  "page_ref",
  "section_number",
  "sequence",
  "date",
  "doc_property"
];
var ORD_TO_NUMBER_FORMAT = [
  "arabic",
  "roman_lower",
  "roman_upper",
  "alpha_lower",
  "alpha_upper",
  "decimal_zero"
];
var ORD_TO_REF_LABEL = [
  "number_only",
  "full_label",
  "page",
  "above_below"
];
var ORD_TO_GENERATED_KIND = [
  "toc",
  "index",
  "bibliography",
  "list_of_figures",
  "list_of_tables",
  "table_of_authorities"
];
var ORD_TO_LENGTH_UNIT = [
  void 0,
  // 0 = absent
  "pt",
  "percent",
  "auto",
  "fr"
];
function readOptStr(r) {
  return r.u8() === 1 ? r.str() : void 0;
}
function readOptFont(r) {
  if (r.u8() === 0) return void 0;
  const cssFamily = r.str();
  const weight = r.u16();
  const italic = r.u8() !== 0;
  const pdfzHandle = r.u8() === 1 ? r.u32() : void 0;
  return { cssFamily, weight, italic, ...pdfzHandle !== void 0 ? { pdfzHandle } : {} };
}
function readOptLength(r) {
  const unit = ORD_TO_LENGTH_UNIT[r.u8()];
  if (unit === void 0) return void 0;
  if (unit === "auto") return { unit: "auto" };
  return { unit, value: r.f32() };
}
function readOptCaption(r) {
  if (r.u8() === 0) return void 0;
  const runs = readRuns(r);
  return { runs, placement: ORD_TO_CAPTION_PLACEMENT[r.u8()] ?? "below" };
}
function readGeneratedSpec(r) {
  switch (r.u8()) {
    case 0: {
      const maxDepth = r.u8();
      const nLevels = r.u32();
      const fromOutlineLevels = [];
      for (let i = 0; i < nLevels; i++) fromOutlineLevels.push(r.u8());
      const nStyles = r.u32();
      const entryStyleMap = [];
      for (let i = 0; i < nStyles; i++) {
        const level = r.u8();
        entryStyleMap.push({ level, styleName: r.str() });
      }
      const fromCounter = readOptStr(r);
      return {
        specKind: "toc",
        maxDepth,
        fromOutlineLevels,
        entryStyleMap,
        ...fromCounter !== void 0 ? { fromCounter } : {}
      };
    }
    case 1: {
      const columns = r.u8();
      const entryStyle = readOptStr(r);
      return { specKind: "index", columns, ...entryStyle !== void 0 ? { entryStyle } : {} };
    }
    case 2: {
      const cslStyleRef = readOptStr(r);
      const sourcesRef = readOptStr(r);
      return {
        specKind: "bibliography",
        ...cslStyleRef !== void 0 ? { cslStyleRef } : {},
        ...sourcesRef !== void 0 ? { sourcesRef } : {}
      };
    }
    default: {
      const category = r.u8();
      const entryStyle = readOptStr(r);
      return {
        specKind: "tableOfAuthorities",
        category,
        ...entryStyle !== void 0 ? { entryStyle } : {}
      };
    }
  }
}
function checkedFromFlag(flag) {
  if (flag === 0) return null;
  return flag === 2;
}
function readBorderSide(r) {
  if (r.u8() === 0) return void 0;
  const widthPt = r.f32();
  const color = r.str();
  const style = ORD_TO_BORDER_STYLE[r.u8()] ?? "solid";
  return { widthPt, color, style };
}
function readCellChannels(r) {
  const vMerge = ORD_TO_V_MERGE[r.u8()] ?? "none";
  const shading = r.u8() === 1 ? r.str() : void 0;
  const avRaw = r.u8();
  const vAlign = avRaw === 0 ? void 0 : ORD_TO_V_ALIGN[avRaw - 1];
  const ahRaw = r.u8();
  const alignH = ahRaw === 0 ? void 0 : ORD_TO_CELL_ALIGN[ahRaw - 1];
  let borders;
  if (r.u8() === 1) {
    borders = {
      top: readBorderSide(r),
      left: readBorderSide(r),
      bottom: readBorderSide(r),
      right: readBorderSide(r)
    };
  }
  return {
    vMerge,
    ...shading !== void 0 ? { shading } : {},
    ...vAlign !== void 0 ? { vAlign } : {},
    ...alignH !== void 0 ? { alignH } : {},
    ...borders !== void 0 ? { borders } : {}
  };
}
function readRow(r) {
  const nCells = r.u32();
  const cells = [];
  for (let i = 0; i < nCells; i++) {
    const colSpan = r.u16();
    const runs = readRuns(r);
    const ch = readCellChannels(r);
    cells.push({ runs, ...colSpan > 1 ? { colSpan } : {}, ...ch });
  }
  return { cells };
}
function readTable(r) {
  const nCols = r.u32();
  const columns = [];
  for (let i = 0; i < nCols; i++) {
    const align = ORD_TO_COLUMN_ALIGN[r.u8()] ?? "left";
    const widthKind = r.u8() === 0 ? "fixed" : "fraction";
    const width = r.f32();
    columns.push({ align, widthKind, width });
  }
  const header = r.u8() === 1 ? readRow(r) : void 0;
  const nRows = r.u32();
  const rows = [];
  for (let i = 0; i < nRows; i++) rows.push(readRow(r));
  const rowHeadColumns = r.u32();
  return {
    kind: "table",
    columns,
    ...header !== void 0 ? { header } : {},
    rows,
    ...rowHeadColumns > 0 ? { rowHeadColumns } : {}
  };
}
function readListItem(r) {
  const level = r.u8();
  const cf = r.u8();
  const runs = readRuns(r);
  if (level === 0 && cf === 0) return runs;
  const checked = checkedFromFlag(cf);
  return {
    runs,
    ...level > 0 ? { level } : {},
    ...checked !== null ? { checked } : {}
  };
}
function swallowFrom(r, ctx, start, tag) {
  ctx.swallowed = true;
  return { kind: "unknown", tag, bytes: r.restFrom(start), blockCount: 1 };
}
function readBlock(r) {
  return readBlockCtx(r, { swallowed: false });
}
function readBlockCtx(r, ctx) {
  const start = r.offset;
  const tag = r.u8();
  const kind = TAG_TO_KIND[tag];
  if (kind === void 0) return swallowFrom(r, ctx, start, tag);
  switch (kind) {
    case "paragraph": {
      const runs = readRuns(r);
      const props = readParaPropsGate(r);
      return { kind: "paragraph", runs, ...props !== void 0 ? { props } : {} };
    }
    case "heading": {
      const level = r.u8();
      const runs = readRuns(r);
      const props = readParaPropsGate(r);
      return { kind: "heading", level, runs, ...props !== void 0 ? { props } : {} };
    }
    case "list": {
      const style = ORD_TO_LIST_STYLE[r.u8()] ?? "bullet";
      const flags = r.u8();
      const numId = (flags & 1) !== 0 ? r.str() : void 0;
      const startOverride = (flags & 2) !== 0 ? r.u32() : void 0;
      const bulletChar = (flags & 4) !== 0 ? r.str() : void 0;
      const nItems = r.u32();
      const items = [];
      for (let i = 0; i < nItems; i++) items.push(readListItem(r));
      return {
        kind: "list",
        style,
        items,
        ...numId !== void 0 ? { numId } : {},
        ...startOverride !== void 0 ? { startOverride } : {},
        ...bulletChar !== void 0 ? { bulletChar } : {}
      };
    }
    case "table":
      return readTable(r);
    case "image": {
      const srcKind = r.u8() === 1 ? "bytes" : "path";
      const src = r.str();
      const width = r.f32();
      const height = r.f32();
      const alt = r.str();
      return { kind: "image", src, srcKind, width, height, alt };
    }
    case "imageSvg": {
      const svg = r.str();
      const width = r.f32();
      const height = r.f32();
      const alt = r.str();
      return { kind: "imageSvg", svg, width, height, alt };
    }
    case "blockquote": {
      const children = readBlockSlice(r, ctx);
      if (ctx.swallowed) return swallowFrom(r, ctx, start, tag);
      return { kind: "blockquote", children };
    }
    case "code": {
      const language = r.str();
      const code = r.str();
      return { kind: "code", ...language.length > 0 ? { language } : {}, code };
    }
    case "horizontalRule": {
      const widthPct = r.f32();
      const thicknessPt = r.f32();
      const color = r.str();
      const style = ORD_TO_BORDER_STYLE[r.u8()] ?? "solid";
      const alignment = ORD_TO_TEXT_ALIGN[r.u8()] ?? "center";
      return { kind: "horizontalRule", widthPct, thicknessPt, color, style, alignment };
    }
    // ─── blitz-10 transport widening (tags 10..21) ───────────────────────────
    case "pageBreak":
      return { kind: "pageBreak" };
    case "spacer":
      return { kind: "spacer", heightPt: r.f32() };
    case "text": {
      const runs = readRuns(r);
      const font = readOptFont(r);
      const maxW = readOptLength(r);
      return {
        kind: "text",
        runs,
        ...font !== void 0 ? { font } : {},
        ...maxW !== void 0 ? { maxW } : {}
      };
    }
    case "figure": {
      const content = readBlockSlice(r, ctx);
      if (ctx.swallowed) return swallowFrom(r, ctx, start, tag);
      const caption = readOptCaption(r);
      const counterName = readOptStr(r);
      const figureId = readOptStr(r);
      return {
        kind: "figure",
        content,
        ...caption !== void 0 ? { caption } : {},
        ...counterName !== void 0 ? { counterName } : {},
        ...figureId !== void 0 ? { figureId } : {}
      };
    }
    case "note": {
      const noteKind = ORD_TO_NOTE_KIND[r.u8()] ?? "footnote";
      const marker = ORD_TO_NOTE_MARKER[r.u8()] ?? "auto_number";
      const customMarker = r.u8() === 1 ? readRuns(r) : void 0;
      const body = readBlockSlice(r, ctx);
      if (ctx.swallowed) return swallowFrom(r, ctx, start, tag);
      const callStyle = readOptStr(r);
      const markerStyle = readOptStr(r);
      return {
        kind: "note",
        noteKind,
        marker,
        ...customMarker !== void 0 ? { customMarker } : {},
        body,
        ...callStyle !== void 0 ? { callStyle } : {},
        ...markerStyle !== void 0 ? { markerStyle } : {}
      };
    }
    case "field": {
      const fieldKind = ORD_TO_FIELD_KIND[r.u8()] ?? "page_number";
      const target = readOptStr(r);
      const counterName = readOptStr(r);
      const format = ORD_TO_NUMBER_FORMAT[r.u8()] ?? "arabic";
      const cachedValue = readOptStr(r);
      const refLabel = ORD_TO_REF_LABEL[r.u8()] ?? "number_only";
      return {
        kind: "field",
        fieldKind,
        ...target !== void 0 ? { target } : {},
        ...counterName !== void 0 ? { counterName } : {},
        format,
        ...cachedValue !== void 0 ? { cachedValue } : {},
        refLabel
      };
    }
    case "bookmark":
      return { kind: "bookmark", bookmarkId: r.str() };
    case "referenceMark":
      return { kind: "referenceMark", refId: r.str() };
    case "rawHtml":
      return { kind: "rawHtml", html: r.str() };
    case "rawOoxml":
      return { kind: "rawOoxml", xml: r.str() };
    case "rawSvg":
      return { kind: "rawSvg", svg: r.str() };
    case "generated": {
      const generatedKind = ORD_TO_GENERATED_KIND[r.u8()] ?? "toc";
      const spec = readGeneratedSpec(r);
      const cachedBlocks = readBlockSlice(r, ctx);
      if (ctx.swallowed) return swallowFrom(r, ctx, start, tag);
      return { kind: "generated", generatedKind, spec, cachedBlocks };
    }
  }
}
function readBlockSlice(r, ctx) {
  const n = r.u32();
  const blocks = [];
  for (let i = 0; i < n; i++) {
    const b = readBlockCtx(r, ctx);
    blocks.push(b);
    if (ctx.swallowed) {
      if (b.kind === "unknown") {
        blocks[blocks.length - 1] = { ...b, blockCount: n - i };
      }
      break;
    }
  }
  return blocks;
}
function readBlockList(r) {
  return readBlockSlice(r, { swallowed: false });
}
function isUnknownBlock(b) {
  return b.kind === "unknown";
}
function decodeTablePayload(bytes) {
  return readTable(new ByteReader(bytes));
}
function decodeBlock(bytes) {
  return readBlock(new ByteReader(bytes));
}
function decodeBlockList(bytes) {
  return readBlockList(new ByteReader(bytes));
}
var UTF82 = new TextEncoder();
function markCoexistence(type) {
  switch (type) {
    case "bold":
    case "italic":
    case "strikeout":
    case "smallcaps":
    case "code":
    case "comment":
    case "all_caps":
    // FEATURE BUNDLE 4 (1E) — index/ToA anchors STACK (several per range).
    case "index_entry":
    case "toa_citation":
      return "toggle";
    // `underline` is now VALUED (one style+color per range) and
    // `font_family`/`font_size`/`letter_spacing` each carry one value — all
    // EXCLUSIVE (last-wins), joining super/sub/color/highlight/link.
    default:
      return "exclusive";
  }
}
var MARK_RANK = {
  link: 0,
  comment: 1,
  highlight: 2,
  color: 3,
  smallcaps: 4,
  strikeout: 5,
  underline: 6,
  bold: 7,
  italic: 8,
  superscript: 9,
  subscript: 10,
  code: 11,
  // render-next v1.17 inner ranks (docz `markCanonicalRank`). This RENDER rank
  // is SEPARATE from the wire ordinal (`mark-codec.ts`); the only requirement
  // is a stable total order.
  font_family: 12,
  font_size: 13,
  letter_spacing: 14,
  all_caps: 15,
  // FEATURE BUNDLE 3 W2 — appended inner ranks (docz `markCanonicalRank` 16/17/18).
  text_effects: 16,
  char_border: 17,
  emphasis: 18,
  // FEATURE BUNDLE 4 (1E) — appended inner ranks (docz `markCanonicalRank` 19/20).
  index_entry: 19,
  toa_citation: 20
};
var DoczBlockKindSchema = z.enum(["paragraph", "heading", "list_item", "blockquote", "code"]);
var FragmentMarkSchema = z.union([
  z.object({
    type: z.enum([
      "bold",
      "italic",
      "strikeout",
      "smallcaps",
      "superscript",
      "subscript",
      "code",
      "all_caps"
    ])
  }),
  z.object({ type: z.literal("link"), href: z.string() }),
  z.object({ type: z.enum(["color", "highlight"]), color: z.string() }),
  z.object({ type: z.literal("comment"), commentId: z.string().min(1) }),
  z.object({
    type: z.literal("underline"),
    style: z.enum(["single", "double", "dotted", "dashed", "wavy", "thick"]).optional(),
    color: z.string().optional()
  }),
  z.object({ type: z.literal("font_family"), font: z.string() }),
  z.object({ type: z.literal("font_size"), sizePt: z.number() }),
  z.object({ type: z.literal("letter_spacing"), spacingPt: z.number() }),
  // FEATURE BUNDLE 3 W2 char-decoration marks.
  z.object({
    type: z.literal("text_effects"),
    effects: z.object({
      shadow: z.boolean().optional(),
      outline: z.boolean().optional(),
      emboss: z.boolean().optional(),
      engrave: z.boolean().optional(),
      glow: z.string().optional(),
      reflection: z.boolean().optional()
    })
  }),
  z.object({
    type: z.literal("char_border"),
    border: z.object({
      widthPt: z.number(),
      color: z.string(),
      style: z.enum(["solid", "dashed", "dotted", "double"])
    })
  }),
  z.object({ type: z.literal("emphasis"), emphasis: z.enum(["dot", "comma", "circle", "under_dot"]) }),
  // FEATURE BUNDLE 4 (1E) references — invisible anchor marks.
  z.object({
    type: z.literal("index_entry"),
    entry: z.object({
      key: z.string(),
      sub: z.string().optional(),
      crossRef: z.string().optional(),
      rangeBookmark: z.string().optional(),
      kind: z.enum(["main", "sub", "cross_ref", "range"]).optional()
    })
  }),
  z.object({
    type: z.literal("toa_citation"),
    citation: z.object({
      category: z.number().int().min(0).max(16).optional(),
      longForm: z.string(),
      shortForm: z.string().optional()
    })
  })
]);
var FragmentRunSchema = z.object({
  text: z.string(),
  marks: z.array(FragmentMarkSchema).readonly()
});
var DoczFragmentSchema = z.object({
  kind: DoczBlockKindSchema.optional(),
  level: z.number().int().min(1).max(6).optional(),
  runs: z.array(FragmentRunSchema).readonly(),
  baseVersionId: z.string().min(1).optional()
});
function byteLen(s) {
  return UTF82.encode(s).length;
}
function emptyFragment(baseVersionId) {
  return { kind: "paragraph", runs: [{ text: "", marks: [] }], baseVersionId };
}
function textFragment(text, opts = {}) {
  return {
    kind: opts.kind ?? "paragraph",
    level: opts.level,
    runs: [{ text, marks: [] }],
    baseVersionId: opts.baseVersionId
  };
}
function fragmentText(frag) {
  let out = "";
  for (const run of frag.runs) out += run.text;
  return out;
}
function fragmentByteLength(frag) {
  return byteLen(fragmentText(frag));
}
function isEmptyFragment(frag) {
  return fragmentText(frag).length === 0;
}
function canonicalizeMarks(marks) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const m of marks) {
    const key = markKey(m);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  out.sort((a, b) => MARK_RANK[a.type] - MARK_RANK[b.type]);
  return out;
}
function markKey(m) {
  switch (m.type) {
    case "link":
      return `link:${m.href}`;
    case "color":
    case "highlight":
      return `${m.type}:${m.color}`;
    case "comment":
      return `comment:${m.commentId}`;
    // Exclusive valued marks key by value so distinct values do NOT dedupe
    // (last-wins resolution runs in docz.wasm). A bare underline normalizes to
    // its default single/no-color key so it matches a decoded single underline.
    case "underline":
      return `underline:${m.style ?? "single"}:${m.color ?? ""}`;
    case "font_family":
      return `font_family:${m.font}`;
    case "font_size":
      return `font_size:${m.sizePt}`;
    case "letter_spacing":
      return `letter_spacing:${m.spacingPt}`;
    // FEATURE BUNDLE 3 W2 — exclusive valued marks key by value (distinct
    // values do NOT dedupe; last-wins resolution runs in docz.wasm).
    case "text_effects": {
      const e = m.effects;
      return `text_effects:${e.shadow ? 1 : 0}${e.outline ? 1 : 0}${e.emboss ? 1 : 0}${e.engrave ? 1 : 0}${e.reflection ? 1 : 0}:${e.glow ?? ""}`;
    }
    case "char_border":
      return `char_border:${m.border.widthPt}:${m.border.style}:${m.border.color}`;
    case "emphasis":
      return `emphasis:${m.emphasis}`;
    // FEATURE BUNDLE 4 (1E) — anchors key by value (distinct anchors stack;
    // only an EXACT duplicate dedupes).
    case "index_entry": {
      const e = m.entry;
      return `index_entry:${e.kind ?? "main"}:${e.key}:${e.sub ?? ""}:${e.crossRef ?? ""}:${e.rangeBookmark ?? ""}`;
    }
    case "toa_citation": {
      const c = m.citation;
      return `toa_citation:${c.category ?? 1}:${c.longForm}:${c.shortForm ?? ""}`;
    }
    default:
      return m.type;
  }
}
function normalizeFragmentRuns(runs) {
  const out = [];
  for (const run of runs) {
    const marks = canonicalizeMarks(run.marks);
    const prev = out[out.length - 1];
    if (prev && sameMarks(prev.marks, marks)) {
      out[out.length - 1] = { text: prev.text + run.text, marks: prev.marks };
    } else {
      out.push({ text: run.text, marks });
    }
  }
  const nonEmpty = out.filter((r) => r.text.length > 0);
  return nonEmpty.length > 0 ? nonEmpty : [{ text: "", marks: [] }];
}
function sameMarks(a, b) {
  if (a.length !== b.length) return false;
  const ka = a.map(markKey).sort();
  const kb = b.map(markKey).sort();
  return ka.every((k, i) => k === kb[i]);
}

// src/caret-style.ts
function runSpans(frag) {
  const spans = [];
  let offset = 0;
  for (const run of frag.runs) {
    const start = offset;
    const end = offset + byteLen(run.text);
    offset = end;
    spans.push({ start, end, marks: run.marks });
  }
  return spans;
}
function caretMarks(frag, at, affinity = "upstream") {
  const spans = runSpans(frag);
  const textual = spans.filter((s) => s.end > s.start);
  if (textual.length === 0) {
    const first = spans[0];
    return first ? [...first.marks] : [];
  }
  if (affinity === "downstream") {
    const hit2 = textual.find((s) => s.start <= at && at < s.end);
    const run2 = hit2 ?? textual[textual.length - 1];
    return [...run2.marks];
  }
  const hit = textual.find((s) => s.start < at && at <= s.end);
  const run = hit ?? textual[0];
  return [...run.marks];
}
function marksInRange(frag, lo, hi, affinity = "upstream") {
  if (hi <= lo) return caretMarks(frag, lo, affinity);
  const covering = runSpans(frag).filter((s) => s.end > s.start && s.start < hi && s.end > lo).map((s) => s.marks);
  const first = covering[0];
  if (!first) return [];
  return first.filter((m) => covering.every((set) => set.some((x) => markKey(x) === markKey(m))));
}

// src/range-style.ts
var MIXED = /* @__PURE__ */ Symbol("mixed");
var DEFAULT_FONT_SIZE_PT = 10;
var DEFAULT_FONT_FAMILY = "Calibri";
var DEFAULT_TEXT_COLOR = "#000000";
var STYLE_PROPS = {
  fontSize: {
    mark: "font_size",
    read: (m) => m.type === "font_size" ? m.sizePt : DEFAULT_FONT_SIZE_PT,
    make: (v) => ({ type: "font_size", sizePt: v }),
    inherited: () => DEFAULT_FONT_SIZE_PT
  },
  fontFamily: {
    mark: "font_family",
    read: (m) => m.type === "font_family" ? m.font : DEFAULT_FONT_FAMILY,
    make: (v) => ({ type: "font_family", font: v }),
    inherited: () => DEFAULT_FONT_FAMILY
  },
  color: {
    mark: "color",
    read: (m) => m.type === "color" ? m.color : DEFAULT_TEXT_COLOR,
    make: (v) => ({ type: "color", color: v }),
    inherited: () => DEFAULT_TEXT_COLOR
  },
  highlight: {
    mark: "highlight",
    read: (m) => m.type === "highlight" ? m.color : "transparent",
    make: (v) => ({ type: "highlight", color: v }),
    inherited: () => "transparent"
  },
  letterSpacing: {
    mark: "letter_spacing",
    read: (m) => m.type === "letter_spacing" ? m.spacingPt : 0,
    make: (v) => ({ type: "letter_spacing", spacingPt: v }),
    inherited: () => 0
  },
  underlineStyle: {
    mark: "underline",
    // An `underline` mark with no explicit style IS a single underline — the
    // valued arm's own default, not a missing value.
    read: (m) => m.type === "underline" ? m.style ?? "single" : "none",
    make: (v) => ({ type: "underline", style: v }),
    inherited: () => "none"
  }
};
function resolveStyle(slices, fragmentAt, prop, affinity) {
  const same = prop.eql ?? Object.is;
  let answer = null;
  for (const s of slices) {
    const frag = fragmentAt(s.blockOrd);
    if (!frag) continue;
    for (const value of sliceValues(frag, s, prop, affinity)) {
      if (answer === null) answer = { value };
      else if (!same(answer.value, value)) return MIXED;
    }
  }
  return answer === null ? MIXED : answer.value;
}
function* sliceValues(frag, s, prop, affinity) {
  const lo = Math.min(s.start, s.end);
  const hi = Math.max(s.start, s.end);
  if (hi <= lo) {
    yield valueOf(marksInRange(frag, lo, hi, affinity), frag, prop);
    return;
  }
  let at = 0;
  let covered = false;
  for (const run of frag.runs) {
    const start = at;
    const end = at + byteLen(run.text);
    at = end;
    if (end <= start) continue;
    if (start >= hi || end <= lo) continue;
    covered = true;
    yield valueOf(run.marks, frag, prop);
  }
  if (!covered) return;
}
function valueOf(marks, frag, prop) {
  const m = marks.find((x) => x.type === prop.mark);
  return m ? prop.read(m) : prop.inherited(frag);
}
function marksAcrossSlices(slices, fragmentAt, affinity) {
  let common = null;
  for (const s of slices) {
    const frag = fragmentAt(s.blockOrd);
    if (!frag) continue;
    const lo = Math.min(s.start, s.end);
    const hi = Math.max(s.start, s.end);
    const here = marksInRange(frag, lo, hi, affinity);
    if (common === null) {
      common = [...here];
      continue;
    }
    const keys = new Set(here.map(markKey));
    common = common.filter((m) => keys.has(markKey(m)));
    if (common.length === 0) return [];
  }
  return common ?? [];
}
function applyAcrossSlices(slices, fragmentAt, op, commit) {
  const applied = [];
  const declined = [];
  for (const s of slices) {
    const lo = Math.min(s.start, s.end);
    const hi = Math.max(s.start, s.end);
    if (hi <= lo) {
      if (slices.length > 1) {
        declined.push({ blockOrd: s.blockOrd, reason: "no text in this block to mark" });
      }
      continue;
    }
    const before = fragmentAt(s.blockOrd);
    if (!before) {
      declined.push({ blockOrd: s.blockOrd, reason: "this surface holds no fragment here" });
      continue;
    }
    const res = op(before, { start: lo, end: hi });
    if (!res.ok) {
      declined.push({ blockOrd: s.blockOrd, reason: `docz declined (err ${res.errCode})` });
      continue;
    }
    if (res.fragment === before) continue;
    if (!commit(s.blockOrd, res.fragment)) {
      declined.push({
        blockOrd: s.blockOrd,
        reason: "this block names no single editable run slice (a table or code island)"
      });
      continue;
    }
    applied.push(s.blockOrd);
  }
  return { applied, declined };
}
function applyStyle(slices, fragmentAt, prop, value, stamp2, commit) {
  return applyAcrossSlices(slices, fragmentAt, stamp2(prop.make(value)), commit);
}

// src/block-scope.ts
function applyAcrossBlocks(slices, blockAt, op, commit) {
  const applied = [];
  const declined = [];
  const seen = /* @__PURE__ */ new Set();
  for (const s of slices) {
    if (seen.has(s.blockOrd)) continue;
    seen.add(s.blockOrd);
    const before = blockAt(s.blockOrd);
    if (!before) {
      declined.push({ blockOrd: s.blockOrd, reason: "this surface holds no block here" });
      continue;
    }
    const res = op(s.blockOrd, before);
    if (!res.ok) {
      declined.push({ blockOrd: s.blockOrd, reason: res.reason });
      continue;
    }
    if (res.block === before) continue;
    if (!commit(s.blockOrd, res.block)) {
      declined.push({
        blockOrd: s.blockOrd,
        reason: "this surface refused the new block"
      });
      continue;
    }
    applied.push(s.blockOrd);
  }
  return { applied, declined };
}
function itemLevel(item) {
  return Array.isArray(item) ? 0 : item.level ?? 0;
}
function itemChecked(item) {
  return Array.isArray(item) ? null : item.checked ?? null;
}
var LIST_PROPS = {
  /** The marker style, or `'none'` for any block that is not a list. `'none'` is
   *  a real answer — it is what makes a paragraph+bullet selection resolve MIXED
   *  and therefore light NO list button. */
  style: {
    values: (b) => [b.kind === "list" ? b.style : "none"]
  },
  /** The nesting depth, per item. A non-list block has none — see
   *  {@link BlockProp.values} on why that is an empty array, not `0`. */
  level: {
    values: (b) => b.kind === "list" ? b.items.map(itemLevel) : []
  },
  /** The checkbox state, per item. */
  checked: {
    values: (b) => b.kind === "list" ? b.items.map(itemChecked) : []
  },
  /** The ordinal restart, `null` when the list continues its predecessor. */
  startOverride: {
    values: (b) => b.kind === "list" ? [b.startOverride ?? null] : []
  },
  /** The author's custom bullet glyph, `null` for the engine's own marker.
   *  Per-LIST (like {@link LIST_PROPS.startOverride}), not per-item.
   *
   *  Without this row a bullet-glyph control has nothing to read, so it can only
   *  render an empty select that claims nothing — a control that cannot show the
   *  value in force is the readout half of the very drift this table exists to
   *  remove. pdfz paints the glyph as of `loop/pdfz/list-props-to-canvas`, so the
   *  value is now observable on the raster and must be observable here too. */
  bulletChar: {
    values: (b) => b.kind === "list" ? [b.bulletChar ?? null] : []
  }
};
function resolveBlockProp(slices, blockAt, prop) {
  const same = prop.eql ?? Object.is;
  let answer = null;
  for (const s of slices) {
    const block = blockAt(s.blockOrd);
    if (!block) continue;
    for (const value of prop.values(block)) {
      if (answer === null) answer = { value };
      else if (!same(answer.value, value)) return MIXED;
    }
  }
  return answer === null ? MIXED : answer.value;
}
function resolveListState(slices, blockAt) {
  return {
    style: resolveBlockProp(slices, blockAt, LIST_PROPS.style),
    level: resolveBlockProp(slices, blockAt, LIST_PROPS.level),
    checked: resolveBlockProp(slices, blockAt, LIST_PROPS.checked),
    startOverride: resolveBlockProp(slices, blockAt, LIST_PROPS.startOverride),
    bulletChar: resolveBlockProp(slices, blockAt, LIST_PROPS.bulletChar)
  };
}

// src/case-transform.ts
var graphemeSeg = new Intl.Segmenter(void 0, { granularity: "grapheme" });
var wordSeg = new Intl.Segmenter(void 0, { granularity: "word" });
var LETTER = /\p{L}/u;
var WHITESPACE = /\s/u;
var SENTENCE_TERMINATOR = /[.!?]/;
function toggleGrapheme(g) {
  const up = g.toUpperCase();
  const lo = g.toLowerCase();
  if (up === lo) return g;
  return g === lo ? up : lo;
}
function titleCapStarts(text) {
  const starts = /* @__PURE__ */ new Set();
  for (const seg of wordSeg.segment(text)) {
    if (!seg.isWordLike) continue;
    for (const g of graphemeSeg.segment(seg.segment)) {
      if (LETTER.test(g.segment)) {
        starts.add(seg.index + g.index);
        break;
      }
    }
  }
  return starts;
}
function sentenceCapStarts(text) {
  const starts = /* @__PURE__ */ new Set();
  let armed = true;
  for (const g of graphemeSeg.segment(text)) {
    const ch = g.segment;
    if (SENTENCE_TERMINATOR.test(ch)) {
      armed = true;
      continue;
    }
    if (WHITESPACE.test(ch)) continue;
    if (armed) {
      if (LETTER.test(ch)) starts.add(g.index);
      armed = false;
    }
  }
  return starts;
}
function changeCaseRuns(runs, mode) {
  const selText = runs.map((r) => r.text).join("");
  if (selText.length === 0) return runs.map((r) => ({ text: r.text, marks: [...r.marks] }));
  const caps = mode === "title" ? titleCapStarts(selText) : mode === "sentence" ? sentenceCapStarts(selText) : null;
  const cum = [0];
  for (const r of runs) cum.push(cum[cum.length - 1] + r.text.length);
  const out = runs.map(() => "");
  const runOf = (idx) => {
    for (let k = 0; k < runs.length; k++) {
      if (idx >= cum[k] && idx < cum[k + 1]) return k;
    }
    return runs.length - 1;
  };
  for (const g of graphemeSeg.segment(selText)) {
    const grapheme = g.segment;
    let next;
    if (mode === "upper") next = grapheme.toUpperCase();
    else if (mode === "lower") next = grapheme.toLowerCase();
    else if (mode === "toggle") next = toggleGrapheme(grapheme);
    else if (caps?.has(g.index)) next = grapheme.toUpperCase();
    else if (LETTER.test(grapheme)) next = grapheme.toLowerCase();
    else next = grapheme;
    const k = runOf(g.index);
    out[k] = out[k] + next;
  }
  return runs.map((r, k) => ({ text: out[k], marks: [...r.marks] }));
}
function changeCaseText(text, mode) {
  const runs = changeCaseRuns([{ text, marks: [] }], mode);
  return runs[0]?.text ?? text;
}

// src/dom.ts
var DOCZ_COMMENT_CLASS = "docz-comment";
var DOCZ_BLOCK_CLASS = "docz-block";
var DOCZ_BLOOM_CLASS = "docz-anchor-bloom";
function requireDom(fn) {
  if (typeof document === "undefined" || typeof document.createElement !== "function") {
    panic({
      fn,
      code: "WEB_DOCZ_NO_DOM",
      kind: "state",
      message: `${LIB}: ${fn} requires a DOM (document is undefined)`
    });
  }
}
function bodyInnerHtml(fullHtml) {
  const open = fullHtml.indexOf("<body");
  if (open === -1) return fullHtml.trim();
  const contentStart = fullHtml.indexOf(">", open);
  if (contentStart === -1) return fullHtml.trim();
  const close = fullHtml.lastIndexOf("</body>");
  if (close === -1 || close < contentStart) return fullHtml.slice(contentStart + 1).trim();
  return fullHtml.slice(contentStart + 1, close).trim();
}
function unwrap(fn, r) {
  if (r.ok) return r.html;
  const code = r.errCode === -1 ? "WEB_DOCZ_WASM_OOM" : r.errCode === -2 ? "WEB_DOCZ_WASM_DECODE" : r.errCode === -3 ? "WEB_DOCZ_WASM_RENDER" : "WEB_DOCZ_WASM_UNKNOWN";
  return panic({
    fn,
    code,
    kind: "contract",
    received: r.errCode,
    message: `${LIB}: ${fn} \u2014 docz.wasm render failed (status ${r.errCode})`
  });
}
function renderBlockToFragment(module, block) {
  requireDom("renderBlockToFragment");
  const html = unwrap(
    "renderBlockToFragment",
    module.renderBlockToHtml(block.text, block.comments, block.kind, block.level)
  );
  const inner = bodyInnerHtml(html);
  const tpl = document.createElement("template");
  tpl.innerHTML = inner;
  const first = tpl.content.firstElementChild;
  if (first) {
    first.classList.add(DOCZ_BLOCK_CLASS);
    if (block.id) first.setAttribute("data-block-id", block.id);
    first.setAttribute("data-block-kind", block.kind ?? "paragraph");
    if (block.kind === "heading" && block.level) {
      first.setAttribute("data-level", String(block.level));
    }
  }
  return tpl.content;
}
function mountBlock(module, container, block) {
  requireDom("mountBlock");
  const frag = renderBlockToFragment(module, block);
  const root = frag.firstElementChild;
  container.appendChild(frag);
  return root;
}
function commentSpans(root, commentId) {
  requireDom("commentSpans");
  const sel = `.${DOCZ_COMMENT_CLASS}[data-comment-id="${cssEscape(commentId)}"]`;
  return Array.from(root.querySelectorAll(sel));
}
function allCommentIds(root) {
  requireDom("allCommentIds");
  const ids = [];
  const seen = /* @__PURE__ */ new Set();
  for (const el of root.querySelectorAll(`.${DOCZ_COMMENT_CLASS}[data-comment-id]`)) {
    const id = el.getAttribute("data-comment-id");
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}
function highlightComment(root, commentId, state = "exact") {
  requireDom("highlightComment");
  const spans = commentSpans(root, commentId);
  for (const el of spans) {
    el.classList.add(DOCZ_BLOOM_CLASS);
    el.setAttribute("data-anchor-state", state);
  }
  return spans;
}
function rebaseToAnchorState(state) {
  if (state === "exact") return "exact";
  if (state === "orphaned") return "orphan";
  return "moved";
}
function highlightRebase(root, commentId, outcome) {
  return highlightComment(root, commentId, rebaseToAnchorState(outcome.state));
}
function clearHighlight(root) {
  requireDom("clearHighlight");
  for (const el of root.querySelectorAll(`.${DOCZ_BLOOM_CLASS}`)) {
    el.classList.remove(DOCZ_BLOOM_CLASS);
    el.removeAttribute("data-anchor-state");
  }
}
function cssEscape(value) {
  const g = globalThis;
  if (typeof g.CSS?.escape === "function") return g.CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}

// src/table-css.ts
var PT_PER_PX = 72 / 96;
var BASE_FONT_PT = 12;
var UNITS = [
  ["rem", BASE_FONT_PT],
  ["em", BASE_FONT_PT],
  ["px", PT_PER_PX],
  ["pt", 1],
  ["pc", 12],
  ["in", 72],
  ["cm", 72 / 2.54],
  ["mm", 72 / 25.4]
];
var NAMED_COLORS = {
  black: "#000000",
  silver: "#C0C0C0",
  gray: "#808080",
  grey: "#808080",
  white: "#FFFFFF",
  maroon: "#800000",
  red: "#FF0000",
  purple: "#800080",
  fuchsia: "#FF00FF",
  magenta: "#FF00FF",
  green: "#008000",
  lime: "#00FF00",
  olive: "#808000",
  yellow: "#FFFF00",
  navy: "#000080",
  blue: "#0000FF",
  teal: "#008080",
  aqua: "#00FFFF",
  cyan: "#00FFFF",
  orange: "#FFA500",
  pink: "#FFC0CB",
  brown: "#A52A2A",
  gold: "#FFD700",
  violet: "#EE82EE",
  indigo: "#4B0082"
};
function styleDeclValue(style, prop) {
  for (const decl of style.split(";")) {
    const colon = decl.indexOf(":");
    if (colon < 0) continue;
    if (decl.slice(0, colon).trim().toLowerCase() === prop.toLowerCase()) {
      return decl.slice(colon + 1).trim();
    }
  }
  return void 0;
}
function hex2(n) {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0").toUpperCase();
}
function rgbHex(r, g, b) {
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
}
function hslHex(body) {
  const parts = body.split(",").map((p) => p.trim());
  if (parts.length < 3) return void 0;
  const h = Number.parseFloat(parts[0]);
  const s = Number.parseFloat(parts[1].replace("%", "")) / 100;
  const l = Number.parseFloat(parts[2].replace("%", "")) / 100;
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) return void 0;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (h % 360 + 360) % 360 / 60;
  const x = c * (1 - Math.abs(hp % 2 - 1));
  const m = l - c / 2;
  const [r1, g1, b1] = hp < 1 ? [c, x, 0] : hp < 2 ? [x, c, 0] : hp < 3 ? [0, c, x] : hp < 4 ? [0, x, c] : hp < 5 ? [x, 0, c] : [c, 0, x];
  return rgbHex((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
}
function cssHexColor(raw) {
  const v = raw.trim();
  if (v.length === 0) return void 0;
  if (v.startsWith("#")) {
    const h = v.slice(1);
    if (/^[0-9a-fA-F]{3}$/.test(h)) {
      const dbl = [...h].map((ch) => ch + ch).join("");
      return `#${dbl.toUpperCase()}`;
    }
    if (/^[0-9a-fA-F]{6}$/.test(h)) return `#${h.toUpperCase()}`;
    return void 0;
  }
  if (v.endsWith(")")) {
    const body = v.slice(v.indexOf("(") + 1, -1);
    const head = v.slice(0, v.indexOf("(")).toLowerCase();
    if (head === "rgb" || head === "rgba") {
      const parts = body.split(",").map((p) => Number.parseFloat(p.trim()));
      if (parts.length < 3 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) return void 0;
      return rgbHex(parts[0], parts[1], parts[2]);
    }
    if (head === "hsl" || head === "hsla") return hslHex(body);
    return void 0;
  }
  return NAMED_COLORS[v.toLowerCase()];
}
function lengthToPt(raw) {
  const v = raw.trim();
  if (v.length === 0) return void 0;
  for (const [suffix, mul] of UNITS) {
    if (v.toLowerCase().endsWith(suffix)) {
      const n = Number.parseFloat(v.slice(0, v.length - suffix.length).trim());
      return Number.isFinite(n) ? n * mul : void 0;
    }
  }
  return void 0;
}
function borderStyleFromCss(tok) {
  switch (tok.toLowerCase()) {
    case "solid":
    case "groove":
    case "ridge":
    case "inset":
    case "outset":
      return "solid";
    case "dashed":
      return "dashed";
    case "dotted":
      return "dotted";
    case "double":
      return "double";
    default:
      return void 0;
  }
}
function keywordWidth(tok) {
  switch (tok.toLowerCase()) {
    case "thin":
      return 0.5;
    case "medium":
      return 1;
    case "thick":
      return 2;
    default:
      return void 0;
  }
}
function parseBorderShorthand(raw) {
  if (raw === void 0) return void 0;
  let widthPt;
  let color;
  let style;
  for (const tok of raw.split(/[\s\t]+/).filter((t) => t.length > 0)) {
    const low = tok.toLowerCase();
    if (low === "none" || low === "hidden") return void 0;
    const bs = borderStyleFromCss(tok);
    if (bs !== void 0) {
      style = bs;
      continue;
    }
    if (widthPt === void 0) {
      const pt = lengthToPt(tok) ?? keywordWidth(tok);
      if (pt !== void 0) {
        widthPt = pt;
        continue;
      }
      if (/^0+(\.0+)?$/.test(tok)) {
        widthPt = 0;
        continue;
      }
    }
    if (color === void 0) {
      const hex = cssHexColor(tok);
      if (hex !== void 0) {
        color = hex;
        continue;
      }
    }
  }
  if (widthPt === void 0 && color === void 0 && (style === void 0 || style === "solid")) {
    return void 0;
  }
  if (widthPt !== void 0 && widthPt <= 0) return void 0;
  return {
    ...widthPt !== void 0 ? { widthPt } : {},
    ...color !== void 0 ? { color } : {},
    ...style !== void 0 ? { style } : {}
  };
}
function cellShading(style) {
  if (style == null) return void 0;
  const bg = styleDeclValue(style, "background");
  if (bg !== void 0) {
    const hex = cssHexColor(bg);
    if (hex !== void 0) return hex;
  }
  const bgc = styleDeclValue(style, "background-color");
  return bgc === void 0 ? void 0 : cssHexColor(bgc);
}
function cellBorders(style) {
  if (style == null) return void 0;
  const all = parseBorderShorthand(styleDeclValue(style, "border"));
  const top = parseBorderShorthand(styleDeclValue(style, "border-top")) ?? all;
  const left = parseBorderShorthand(styleDeclValue(style, "border-left")) ?? all;
  const bottom = parseBorderShorthand(styleDeclValue(style, "border-bottom")) ?? all;
  const right = parseBorderShorthand(styleDeclValue(style, "border-right")) ?? all;
  if (top === void 0 && left === void 0 && bottom === void 0 && right === void 0) {
    return void 0;
  }
  return {
    ...top !== void 0 ? { top } : {},
    ...left !== void 0 ? { left } : {},
    ...bottom !== void 0 ? { bottom } : {},
    ...right !== void 0 ? { right } : {}
  };
}

// src/raw-html.ts
var TEXT_NODE2 = 3;
var ELEMENT_NODE2 = 1;
function textRuns(text) {
  return [{ text, marks: [] }];
}
var INLINE_TAGS = /* @__PURE__ */ new Set([
  "a",
  "abbr",
  "b",
  "bdi",
  "bdo",
  "big",
  "br",
  "cite",
  "code",
  "data",
  "del",
  "dfn",
  "em",
  "font",
  "i",
  "ins",
  "kbd",
  "label",
  "mark",
  "q",
  "ruby",
  "s",
  "samp",
  "small",
  "span",
  "strike",
  "strong",
  "sub",
  "sup",
  "time",
  "tt",
  "u",
  "var",
  "wbr"
]);
function inlineMark(el) {
  switch (el.tagName.toLowerCase()) {
    case "b":
    case "strong":
      return { type: "bold" };
    case "i":
    case "em":
    case "dfn":
    case "var":
      return { type: "italic" };
    case "u":
    case "ins":
      return { type: "underline" };
    case "s":
    case "strike":
    case "del":
      return { type: "strikeout" };
    case "code":
    case "kbd":
    case "samp":
    case "tt":
      return { type: "code" };
    case "sup":
      return { type: "superscript" };
    case "sub":
      return { type: "subscript" };
    case "a": {
      const raw = el.getAttribute("href");
      const safe = raw != null ? safeHref(raw) : null;
      return safe != null ? { type: "link", href: safe } : null;
    }
    default:
      return null;
  }
}
function isInline(el) {
  return INLINE_TAGS.has(el.tagName.toLowerCase());
}
function isBreak(p) {
  return p.br === true;
}
function collectInline(container, marks, out) {
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType === TEXT_NODE2) {
      const text = node.textContent ?? "";
      if (text.length > 0) out.push({ text, marks: [...marks] });
      continue;
    }
    if (node.nodeType !== ELEMENT_NODE2) continue;
    const el = node;
    if (el.tagName.toLowerCase() === "br") {
      out.push({ br: true });
      continue;
    }
    const mark = inlineMark(el);
    collectInline(el, mark ? [...marks, mark] : marks, out);
  }
}
function tidyRuns(runs) {
  const merged = [];
  for (const run of runs) {
    const marks = canonicalizeMarks(run.marks);
    const prev = merged[merged.length - 1];
    if (prev && sameMarks(prev.marks, marks)) {
      merged[merged.length - 1] = { text: prev.text + run.text, marks: prev.marks };
    } else {
      merged.push({ text: run.text, marks });
    }
  }
  const head = merged[0];
  if (head) merged[0] = { text: head.text.replace(/^\s+/, ""), marks: head.marks };
  const lastIdx = merged.length - 1;
  const tail = merged[lastIdx];
  if (tail) merged[lastIdx] = { text: tail.text.replace(/\s+$/, ""), marks: tail.marks };
  return merged.filter((r) => r.text.length > 0);
}
function runGroups(pieces) {
  const groups = [];
  let current = [];
  for (const piece of pieces) {
    if (isBreak(piece)) {
      groups.push(current);
      current = [];
      continue;
    }
    current.push(piece);
  }
  groups.push(current);
  return groups.map(tidyRuns).filter((g) => g.length > 0);
}
function inlineRunsOf(el) {
  const pieces = [];
  collectInline(el, [], pieces);
  return joinGroups(runGroups(pieces));
}
function joinGroups(groups) {
  const out = [];
  for (const group of groups) {
    if (out.length > 0) out.push({ text: " ", marks: [] });
    out.push(...group);
  }
  return tidyRuns(out);
}
function slotRuns(el) {
  const runs = inlineRunsOf(el);
  return runs.length > 0 ? runs : textRuns("");
}
function span(el, attr) {
  const raw = el.getAttribute(attr);
  const n = raw == null ? 1 : Number.parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : 1;
}
function cellChrome(el) {
  const style = el.getAttribute("style");
  const shading = cellShading(style);
  const borders = cellBorders(style);
  return {
    ...shading !== void 0 ? { shading } : {},
    ...borders !== void 0 ? { borders } : {}
  };
}
function placeCells(rows) {
  const occupied = [];
  const placed = [];
  let width = 0;
  const reserve = (r, c) => {
    while (occupied.length <= r) occupied.push([]);
    occupied[r][c] = true;
  };
  const isFree = (r, c) => !(occupied[r]?.[c] === true);
  rows.forEach((tr, r) => {
    let c = 0;
    for (const cellEl of Array.from(tr.children)) {
      const tag = cellEl.tagName.toLowerCase();
      if (tag !== "td" && tag !== "th") continue;
      while (!isFree(r, c)) c += 1;
      const colSpan = span(cellEl, "colspan");
      const rowSpan = span(cellEl, "rowspan");
      placed.push({
        r0: r,
        c0: c,
        rowSpan,
        colSpan,
        runs: slotRuns(cellEl),
        isTh: tag === "th",
        ...cellChrome(cellEl)
      });
      for (let dr = 0; dr < rowSpan; dr += 1) {
        for (let dc = 0; dc < colSpan; dc += 1) reserve(r + dr, c + dc);
      }
      c += colSpan;
      if (c > width) width = c;
    }
  });
  return { placed, width };
}
function buildRow(r, width, placed) {
  const cells = [];
  let c = 0;
  while (c < width) {
    const origin = placed.find((p) => p.r0 === r && p.c0 === c);
    if (origin) {
      const cell = {
        runs: origin.runs,
        ...origin.colSpan > 1 ? { colSpan: origin.colSpan } : {},
        ...origin.rowSpan > 1 ? { vMerge: "restart" } : {},
        ...origin.shading !== void 0 ? { shading: origin.shading } : {},
        ...origin.borders !== void 0 ? { borders: origin.borders } : {}
      };
      cells.push(cell);
      c += origin.colSpan;
      continue;
    }
    const cover = placed.find(
      (p) => p.r0 < r && p.r0 + p.rowSpan > r && p.c0 <= c && p.c0 + p.colSpan > c
    );
    if (cover) {
      if (cover.c0 === c) {
        cells.push({
          runs: textRuns(""),
          ...cover.colSpan > 1 ? { colSpan: cover.colSpan } : {},
          vMerge: "continue",
          ...cover.shading !== void 0 ? { shading: cover.shading } : {},
          ...cover.borders !== void 0 ? { borders: cover.borders } : {}
        });
        c += cover.colSpan;
        continue;
      }
      c += 1;
      continue;
    }
    cells.push({ runs: textRuns("") });
    c += 1;
  }
  return { cells };
}
function explodeTable(table) {
  const theadRows = Array.from(table.querySelectorAll("thead > tr"));
  const bodyRowEls = Array.from(table.querySelectorAll("tbody > tr"));
  const looseRows = Array.from(table.children).filter((el) => el.tagName.toLowerCase() === "tr").filter((tr) => tr.parentElement === table);
  let headerRowEl = theadRows[0] ?? null;
  const orderedBody = [];
  if (theadRows.length > 0) {
    orderedBody.push(...theadRows.slice(1), ...bodyRowEls, ...looseRows);
  } else {
    const candidates = bodyRowEls.length > 0 ? bodyRowEls : looseRows;
    const first = candidates[0];
    const firstIsAllTh = first != null && Array.from(first.children).some((el) => el.tagName.toLowerCase() === "th") && Array.from(first.children).filter((el) => ["td", "th"].includes(el.tagName.toLowerCase())).every((el) => el.tagName.toLowerCase() === "th");
    if (firstIsAllTh) {
      headerRowEl = first;
      orderedBody.push(...candidates.slice(1));
    } else {
      orderedBody.push(...candidates);
    }
  }
  const allRowEls = headerRowEl ? [headerRowEl, ...orderedBody] : orderedBody;
  if (allRowEls.length === 0) return null;
  const { placed, width } = placeCells(allRowEls);
  if (width === 0) return null;
  const visualRows = allRowEls.map((_, r) => buildRow(r, width, placed));
  const header = headerRowEl ? visualRows[0] : void 0;
  const bodyRows = headerRowEl ? visualRows.slice(1) : visualRows;
  const columns = Array.from({ length: width }, () => ({}));
  return { kind: "table", columns, ...header ? { header } : {}, rows: bodyRows };
}
function listStyle(el) {
  if (el.tagName.toLowerCase() === "ul") return "bullet";
  switch ((el.getAttribute("type") ?? "").trim()) {
    case "a":
      return "lower_alpha";
    case "A":
      return "upper_alpha";
    case "i":
      return "lower_roman";
    case "I":
      return "upper_roman";
    default:
      return "decimal";
  }
}
function itemRuns(li) {
  const pieces = [];
  for (const node of Array.from(li.childNodes)) {
    if (node.nodeType === TEXT_NODE2) {
      const text = node.textContent ?? "";
      if (text.length > 0) pieces.push({ text, marks: [] });
      continue;
    }
    if (node.nodeType !== ELEMENT_NODE2) continue;
    const el = node;
    const tag = el.tagName.toLowerCase();
    if (tag === "ul" || tag === "ol") continue;
    if (tag === "br") {
      pieces.push({ br: true });
      continue;
    }
    const mark = inlineMark(el);
    collectInline(el, mark ? [mark] : [], pieces);
  }
  const runs = joinGroups(runGroups(pieces));
  return runs.length > 0 ? runs : textRuns("");
}
function explodeList(el, level = 0) {
  const style = listStyle(el);
  const items = [];
  const collect = (listEl, depth) => {
    for (const li of Array.from(listEl.children)) {
      if (li.tagName.toLowerCase() !== "li") continue;
      const runs = itemRuns(li);
      items.push(depth > 0 ? { runs, level: depth } : runs);
      for (const child of Array.from(li.children)) {
        const tag = child.tagName.toLowerCase();
        if (tag === "ul" || tag === "ol") collect(child, depth + 1);
      }
    }
  };
  collect(el, level);
  if (items.length === 0) return null;
  return { kind: "list", style, items };
}
function explodeElement(el) {
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case "table": {
      const t = explodeTable(el);
      return t ? [t] : [];
    }
    case "ul":
    case "ol": {
      const l = explodeList(el);
      return l ? [l] : [];
    }
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6": {
      const level = Number.parseInt(tag.slice(1), 10);
      return [{ kind: "heading", level, runs: slotRuns(el) }];
    }
    case "blockquote": {
      const children = explodeChildren(el);
      return [
        {
          kind: "blockquote",
          children: children.length > 0 ? children : [{ kind: "paragraph", runs: slotRuns(el) }]
        }
      ];
    }
    // `<pre>` is the CODE BLOCK. `<code>` is PHRASING content and is handled by
    // the inline walker as a `code` mark — it never reaches here.
    case "pre":
      return [{ kind: "code", code: el.textContent ?? "" }];
    default: {
      const children = explodeChildren(el);
      if (children.length > 0) return children;
      const runs = inlineRunsOf(el);
      return runs.length > 0 ? [{ kind: "paragraph", runs }] : [];
    }
  }
}
function explodeChildren(container) {
  const out = [];
  let buffer = [];
  const flush = () => {
    if (buffer.length === 0) return;
    for (const runs of runGroups(buffer)) out.push({ kind: "paragraph", runs });
    buffer = [];
  };
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType === TEXT_NODE2) {
      const text = node.textContent ?? "";
      if (buffer.length === 0 && text.trim().length === 0) continue;
      buffer.push({ text, marks: [] });
      continue;
    }
    if (node.nodeType !== ELEMENT_NODE2) continue;
    const el = node;
    if (isInline(el)) {
      if (el.tagName.toLowerCase() === "br") buffer.push({ br: true });
      else {
        const mark = inlineMark(el);
        collectInline(el, mark ? [mark] : [], buffer);
      }
      continue;
    }
    flush();
    out.push(...explodeElement(el));
  }
  flush();
  return out;
}
function looksLikeRawHtml(html) {
  return /<(table|ul|ol|h[1-6]|blockquote|pre|p|div|section|article|figure)[\s>]/i.test(html);
}
function explodeRawHtml(html, doc) {
  if (!html) return null;
  const tpl = doc.createElement("template");
  tpl.innerHTML = html;
  const blocks = explodeChildren(tpl.content);
  return blocks.length > 0 ? blocks : null;
}

// src/fragment-codec.ts
function markToJson(m) {
  switch (m.type) {
    case "link":
      return { type: "link", href: m.href };
    case "color":
    case "highlight":
      return { type: m.type, color: m.color };
    case "comment":
      return { type: "comment", commentId: m.commentId };
    case "underline":
      return {
        type: "underline",
        ...m.style ? { style: m.style } : {},
        ...m.color ? { color: m.color } : {}
      };
    case "font_family":
      return { type: "font_family", font: m.font };
    case "font_size":
      return { type: "font_size", sizePt: m.sizePt };
    case "letter_spacing":
      return { type: "letter_spacing", spacingPt: m.spacingPt };
    // FEATURE BUNDLE 3 W2 — preserve the char-decoration payloads (the
    // `default` toggle-only path would DROP them).
    case "text_effects":
      return { type: "text_effects", effects: m.effects };
    case "char_border":
      return { type: "char_border", border: m.border };
    case "emphasis":
      return { type: "emphasis", emphasis: m.emphasis };
    // FEATURE BUNDLE 4 (1E) — preserve the anchor payloads (the `default`
    // toggle-only path would DROP them).
    case "index_entry":
      return { type: "index_entry", entry: m.entry };
    case "toa_citation":
      return { type: "toa_citation", citation: m.citation };
    default:
      return { type: m.type };
  }
}
function fragmentToJson(frag) {
  const runs = normalizeFragmentRuns(frag.runs).map((r) => ({
    text: r.text,
    marks: canonicalizeMarks(r.marks).map(markToJson)
  }));
  const out = { runs };
  return {
    ...frag.kind ? { kind: frag.kind } : {},
    ...frag.level ? { level: frag.level } : {},
    ...out,
    ...frag.baseVersionId ? { baseVersionId: frag.baseVersionId } : {}
  };
}
function sanitizeLinkHrefs(runs) {
  return runs.map((run) => {
    if (!run.marks.some((m) => m.type === "link" && !isSafeHref(m.href))) return run;
    return {
      text: run.text,
      marks: run.marks.filter((m) => m.type !== "link" || isSafeHref(m.href))
    };
  });
}
function fragmentFromJson(json) {
  const parsed = DoczFragmentSchema.parse(json);
  return { ...parsed, runs: normalizeFragmentRuns(sanitizeLinkHrefs(parsed.runs)) };
}
function encodeFragment(frag) {
  return JSON.stringify(fragmentToJson(frag));
}
function decodeFragment(json) {
  return fragmentFromJson(JSON.parse(json));
}
function fragmentCommentSpans(frag) {
  const open = /* @__PURE__ */ new Map();
  const spans = [];
  let offset = 0;
  for (const run of frag.runs) {
    const len = byteLen(run.text);
    const ids = new Set(
      run.marks.filter((m) => m.type === "comment").map((m) => m.commentId)
    );
    for (const id of ids) {
      const cur = open.get(id);
      if (cur && cur.end === offset) {
        cur.end = offset + len;
      } else {
        if (cur) spans.push({ commentId: id, start: cur.start, end: cur.end });
        open.set(id, { start: offset, end: offset + len });
      }
    }
    for (const [id, cur] of [...open.entries()]) {
      if (!ids.has(id)) {
        spans.push({ commentId: id, start: cur.start, end: cur.end });
        open.delete(id);
      }
    }
    offset += len;
  }
  for (const [id, cur] of open) spans.push({ commentId: id, start: cur.start, end: cur.end });
  return spans;
}
function runsFromCommentSpans(text, spans) {
  const bytes = new TextEncoder().encode(text);
  const total = bytes.length;
  if (total === 0) return [{ text: "", marks: [] }];
  const bounds = /* @__PURE__ */ new Set([0, total]);
  for (const s of spans) {
    if (s.start >= 0 && s.start <= total) bounds.add(s.start);
    if (s.end >= 0 && s.end <= total) bounds.add(s.end);
  }
  const edges = [...bounds].sort((a, b) => a - b);
  const decoder = new TextDecoder();
  const out = [];
  for (let i = 0; i < edges.length - 1; i++) {
    const a = edges[i];
    const b = edges[i + 1];
    if (b <= a) continue;
    const seg = decoder.decode(bytes.subarray(a, b));
    const marks = [];
    for (const s of spans) {
      if (s.end > s.start && s.start <= a && s.end >= b) {
        marks.push({ type: "comment", commentId: s.commentId });
      }
    }
    out.push({ text: seg, marks });
  }
  return out.length > 0 ? out : [{ text: "", marks: [] }];
}
function fragmentToRenderInput(frag) {
  let text = "";
  for (const run of frag.runs) text += run.text;
  return {
    text,
    comments: fragmentCommentSpans(frag),
    kind: frag.kind ?? "paragraph",
    level: frag.level ?? 0
  };
}
function fragmentToWasmRuns(frag) {
  return frag.runs.map((run) => ({ text: run.text, marks: run.marks }));
}
function fragmentStyleMarks(frag) {
  const ranges = [];
  const open = /* @__PURE__ */ new Map();
  let offset = 0;
  for (const run of frag.runs) {
    const len = byteLen(run.text);
    const here = /* @__PURE__ */ new Map();
    for (const m of run.marks) {
      if (m.type === "comment") continue;
      here.set(markKey(m), m);
    }
    for (const [key, mark] of here) {
      const cur = open.get(key);
      if (cur && cur.end === offset) cur.end = offset + len;
      else {
        if (cur) ranges.push({ mark: cur.mark, start: cur.start, end: cur.end });
        open.set(key, { mark, start: offset, end: offset + len });
      }
    }
    for (const [key, cur] of [...open.entries()]) {
      if (!here.has(key)) {
        ranges.push({ mark: cur.mark, start: cur.start, end: cur.end });
        open.delete(key);
      }
    }
    offset += len;
  }
  for (const [, cur] of open) ranges.push({ mark: cur.mark, start: cur.start, end: cur.end });
  return ranges;
}
function wasmRunsToFragment(runs, opts = {}) {
  const styleMarks = opts.styleMarks ?? [];
  const meta2 = {
    kind: opts.kind ?? "paragraph",
    level: opts.level,
    baseVersionId: opts.baseVersionId
  };
  if (styleMarks.length === 0) {
    const out2 = runs.map((r) => ({ text: r.text, marks: [...r.marks] }));
    return {
      ...meta2,
      runs: normalizeFragmentRuns(out2.length > 0 ? out2 : [{ text: "", marks: [] }])
    };
  }
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  const runBytes = runs.map((r) => encoder.encode(r.text));
  const total = runBytes.reduce((n, b) => n + b.length, 0);
  const bounds = /* @__PURE__ */ new Set([0, total]);
  let acc = 0;
  for (const b of runBytes) {
    acc += b.length;
    bounds.add(acc);
  }
  for (const sm of styleMarks) {
    if (sm.start >= 0 && sm.start <= total) bounds.add(sm.start);
    if (sm.end >= 0 && sm.end <= total) bounds.add(sm.end);
  }
  const edges = [...bounds].sort((a, b) => a - b);
  const marksAt = (pos) => {
    let o = 0;
    for (let i = 0; i < runs.length; i++) {
      const len = runBytes[i]?.length ?? 0;
      if (pos >= o && pos < o + len) return runs[i]?.marks ?? [];
      o += len;
    }
    return [];
  };
  const all = new Uint8Array(total);
  {
    let o = 0;
    for (const b of runBytes) {
      all.set(b, o);
      o += b.length;
    }
  }
  const out = [];
  for (let i = 0; i < edges.length - 1; i++) {
    const s = edges[i];
    const e = edges[i + 1];
    if (e <= s) continue;
    const text = decoder.decode(all.subarray(s, e));
    const marks = [...marksAt(s)];
    for (const sm of styleMarks) if (sm.start <= s && sm.end >= e) marks.push(sm.mark);
    out.push({ text, marks });
  }
  return { ...meta2, runs: normalizeFragmentRuns(out.length > 0 ? out : [{ text: "", marks: [] }]) };
}
function shiftStyleRange(r, at, deleteBytes, insertBytes) {
  const delEnd = at + deleteBytes;
  const delta = insertBytes - deleteBytes;
  const start = r.start >= delEnd ? r.start + delta : r.start > at ? at : r.start;
  const end = r.end >= delEnd ? r.end + delta : r.end > at ? at + insertBytes : r.end;
  if (end <= start) return null;
  return { mark: r.mark, start, end };
}

// src/structured.ts
function headingLevel(block) {
  return block.level != null && block.level >= 1 && block.level <= 6 ? block.level : 1;
}
function containerIdOf(block) {
  if (block.id == null) return void 0;
  const addr = parseBlockAddress(block.id);
  const seg = addr.segments[0];
  if (addr.segments.length !== 1 || seg === void 0) return block.id;
  if (block.kind === "list_item" && seg.kind === "listItem") return addr.rootId;
  if (block.kind === "blockquote" && seg.kind === "quoteChild") return addr.rootId;
  if (block.kind === "table_cell" && seg.kind === "tableCell") return addr.rootId;
  return block.id;
}
function structuredFromLegacyBlock(block, parseDoc) {
  const id = containerIdOf(block);
  const stamp2 = (b) => id != null ? { ...b, id } : b;
  const runs = () => runsFromCommentSpans(block.text, block.comments ?? []);
  switch (block.kind) {
    case "heading":
      return [stamp2({ kind: "heading", level: headingLevel(block), runs: runs() })];
    case "list_item":
      return [stamp2({ kind: "list", style: "bullet", items: [runs()] })];
    case "blockquote":
      return [stamp2({ kind: "blockquote", children: [{ kind: "paragraph", runs: runs() }] })];
    case "code":
      return [stamp2({ kind: "code", code: block.text })];
    case "table_cell": {
      const t = tableFromCell(block, runs());
      return [stamp2(t ?? { kind: "paragraph", runs: runs() })];
    }
    case "html": {
      const exploded = parseDoc ? explodeRawHtml(block.text, parseDoc) : null;
      if (exploded && exploded.length > 0) return exploded.map((b) => stamp2(b));
      return [stamp2({ kind: "paragraph", runs: runs() })];
    }
    default:
      return [stamp2({ kind: "paragraph", runs: runs() })];
  }
}
function structuredBlocksFromLegacy(doc, parseDoc) {
  const out = [];
  let prevLegacy;
  for (const block of doc.blocks) {
    const mapped = structuredFromLegacyBlock(block, parseDoc);
    const head = mapped[0];
    const prev = out[out.length - 1];
    const joins = prevLegacy !== void 0 && sameContainer(prevLegacy, block);
    if (block.kind === "list_item" && mapped.length === 1 && prev?.kind === "list" && head?.kind === "list" && joins) {
      out[out.length - 1] = { ...prev, items: [...prev.items, ...head.items] };
      prevLegacy = block;
      continue;
    }
    if (block.kind === "blockquote" && mapped.length === 1 && prev?.kind === "blockquote" && head?.kind === "blockquote" && isAddressedChild(block) && isAddressedChild(prevLegacy) && joins) {
      out[out.length - 1] = { ...prev, children: [...prev.children, ...head.children] };
      prevLegacy = block;
      continue;
    }
    if (block.kind === "table_cell" && mapped.length === 1 && prev?.kind === "table" && head?.kind === "table" && isAddressedChild(block) && isAddressedChild(prevLegacy) && joins) {
      const merged = tableFromCell(
        block,
        runsFromCommentSpans(block.text, block.comments ?? []),
        prev
      );
      if (merged) {
        out[out.length - 1] = keepBlockId(merged, prev.id);
        prevLegacy = block;
        continue;
      }
    }
    out.push(...mapped);
    prevLegacy = block;
  }
  return out;
}
function keepBlockId(block, id) {
  return id != null ? { ...block, id } : block;
}
function isAddressedChild(block) {
  return block?.id != null && parseBlockAddress(block.id).segments.length === 1;
}
function sameContainer(prev, cur) {
  if (!isAddressedChild(prev) || !isAddressedChild(cur)) return true;
  return containerIdOf(prev) === containerIdOf(cur);
}
function structuredDocFromLegacy(doc, parseDoc) {
  return {
    ...doc.id != null ? { id: doc.id } : {},
    ...doc.baseVersionId != null ? { baseVersionId: doc.baseVersionId } : {},
    blocks: structuredBlocksFromLegacy(doc, parseDoc),
    // FEATURE BUNDLE 2 — page decoration is a section/document property (NOT a
    // block); carry it verbatim across the legacy→structured bridge so a page
    // color / border box / line-numbering config survives the promotion.
    ...doc.pageColor !== void 0 ? { pageColor: doc.pageColor } : {},
    ...doc.pageBorders !== void 0 ? { pageBorders: doc.pageBorders } : {},
    ...doc.lineNumbering !== void 0 ? { lineNumbering: doc.lineNumbering } : {},
    ...doc.pageMargins !== void 0 ? { pageMargins: doc.pageMargins } : {}
  };
}
function runText(runs) {
  let out = "";
  for (const r of runs) out += r.text;
  return out;
}
function runComments(runs) {
  return fragmentCommentSpans({ runs });
}
function listItemRuns2(item) {
  return Array.isArray(item) ? item : item.runs;
}
function tableCellBlocks(block, id) {
  const out = [];
  const emit = (cells, header, row) => {
    cells.forEach((c, col) => {
      const comments = runComments(c.runs);
      const cellId = id != null ? tableCellAddress(id, { header, row, col }) : void 0;
      out.push({
        ...cellId != null ? { id: cellId } : {},
        text: runText(c.runs),
        kind: "table_cell",
        ...comments.length > 0 ? { comments } : {}
      });
    });
  };
  if (block.header) emit(block.header.cells, true, 0);
  block.rows.forEach((r, row) => emit(r.cells, false, row));
  return out;
}
function tableFromCell(block, runs, base) {
  const seg = block.id != null ? parseBlockAddress(block.id).segments[0] : void 0;
  if (seg?.kind !== "tableCell") return null;
  const cols = Math.max(base?.columns.length ?? 0, seg.col + 1);
  const columns = Array.from({ length: cols }, (_, i) => base?.columns[i] ?? {});
  const pad = (row) => Array.from({ length: cols }, (_, i) => row?.cells[i] ?? { runs: [] });
  const write = (row) => {
    const cells = pad(row);
    cells[seg.col] = { ...cells[seg.col], runs: [...runs] };
    return { cells };
  };
  const rowCount = Math.max(base?.rows.length ?? 0, seg.header ? 0 : seg.row + 1);
  const rows = Array.from({ length: rowCount }, (_, i) => ({
    cells: pad(base?.rows[i])
  }));
  if (seg.header) return { kind: "table", columns, header: write(base?.header), rows };
  const header = base?.header ? { cells: pad(base.header) } : void 0;
  rows[seg.row] = write(base?.rows[seg.row]);
  return { kind: "table", columns, ...header ? { header } : {}, rows };
}
function legacyFromStructuredBlock(block) {
  return legacyProjectionFromStructuredBlock(block).map((p) => p.block);
}
function legacyProjectionFromStructuredBlock(block) {
  const out = projectStructuredBlock(block);
  return out.length === 1 ? [{ ...out[0], oneToOne: true }] : out.map((p) => ({ ...p, oneToOne: false }));
}
function projectStructuredBlock(block) {
  const own = (blocks) => blocks.map((b) => ({ block: b, sourceKind: block.kind }));
  const id = block.id;
  const withId = (b) => id != null ? { ...b, id } : b;
  const paragraph2 = (text, comments, kind = "paragraph") => withId({ text, kind, ...comments.length > 0 ? { comments } : {} });
  switch (block.kind) {
    case "paragraph":
      return own([paragraph2(runText(block.runs), runComments(block.runs))]);
    case "heading":
      return own([
        withId({
          text: runText(block.runs),
          kind: "heading",
          level: block.level,
          comments: runComments(block.runs)
        })
      ]);
    case "list":
      return own(
        block.items.map((item, i) => {
          const runs = listItemRuns2(item);
          const comments = runComments(runs);
          const itemId = id != null ? listItemAddress(id, i) : void 0;
          return {
            ...itemId != null ? { id: itemId } : {},
            text: runText(runs),
            kind: "list_item",
            ...comments.length > 0 ? { comments } : {}
          };
        })
      );
    case "blockquote":
      return own(
        block.children.map((child, i) => {
          const inner = legacyFromStructuredBlock(child);
          const childId = id != null ? quoteChildAddress(id, i) : void 0;
          const comments = inner.length === 1 ? inner[0]?.comments ?? [] : [];
          return {
            ...childId != null ? { id: childId } : {},
            text: inner.map((b) => b.text).join("\n"),
            kind: "blockquote",
            ...comments.length > 0 ? { comments } : {}
          };
        })
      );
    case "code":
      return own([withId({ text: block.code, kind: "code" })]);
    case "table":
      return own(tableCellBlocks(block, id));
    case "image":
      return own([withId({ text: block.alt ?? "", kind: "paragraph" })]);
    case "imageSvg":
      return own([withId({ text: block.alt ?? "", kind: "paragraph" })]);
    case "horizontalRule":
      return own([withId({ text: "---", kind: "paragraph" })]);
    // ─── blitz-10 transport widening (docz BlockTag 10..21) ──────────────────
    // The zero-TEXT nodes (a page break, vertical whitespace, an anchor) have no
    // plain-text form at all — not "a lossy one", none. They yield no legacy
    // block, which is the same call this adapter already makes for every
    // non-textual channel it drops. Nothing is lost that this transport could
    // have carried: the block-dto remains the canonical, lossless one.
    case "pageBreak":
    case "spacer":
    case "bookmark":
    case "referenceMark":
      return [];
    case "text":
      return own([paragraph2(runText(block.runs), runComments(block.runs))]);
    case "figure": {
      const inner = block.content.flatMap((c) => projectStructuredBlock(c));
      if (!block.caption) return inner;
      const cap = own([paragraph2(runText(block.caption.runs), runComments(block.caption.runs))]);
      return block.caption.placement === "above" ? [...cap, ...inner] : [...inner, ...cap];
    }
    case "note":
      return block.body.flatMap((b) => projectStructuredBlock(b));
    case "field":
      return block.cachedValue !== void 0 && block.cachedValue.length > 0 ? own([paragraph2(block.cachedValue, [])]) : [];
    case "rawHtml":
      return own([withId({ text: block.html, kind: "html" })]);
    case "rawOoxml":
      return own([withId({ text: block.xml, kind: "html" })]);
    case "rawSvg":
      return own([withId({ text: block.svg, kind: "html" })]);
    case "generated":
      return block.cachedBlocks.flatMap((b) => projectStructuredBlock(b));
    // ─── forward compatibility ───────────────────────────────────────────────
    case "unknown":
      return own([
        withId({
          text: `[unsupported content \u2014 ${block.blockCount} block(s), wire tag ${block.tag}]`,
          kind: "paragraph"
        })
      ]);
  }
}
function legacyBlocksFromStructured(blocks) {
  return blocks.flatMap((b) => legacyFromStructuredBlock(b));
}
function legacyProjectionsFromStructured(blocks) {
  return blocks.flatMap(
    (b, sourceIndex) => legacyProjectionFromStructuredBlock(b).map((p) => ({ ...p, sourceIndex }))
  );
}
function runsHaveComment(runs, commentId) {
  return runs.some(
    (r) => r.marks.some((m) => m.type === "comment" && m.commentId === commentId)
  );
}
var BorderSchema = z.object({
  widthPt: z.number().optional(),
  color: z.string().optional(),
  style: z.enum(["solid", "dashed", "dotted", "double"]).optional()
});
var ParaPropsSchema = z.object({
  textAlign: z.enum(["start", "center", "end", "justify"]).optional(),
  indent: z.object({
    leftPt: z.number().optional(),
    rightPt: z.number().optional(),
    firstLinePt: z.number().optional(),
    hangingPt: z.number().optional()
  }).optional(),
  lineSpacing: z.object({ rule: z.enum(["multiple", "atLeast", "exact"]), value: z.number() }).optional(),
  spaceBeforePt: z.number().optional(),
  spaceAfterPt: z.number().optional(),
  tabStops: z.array(
    z.object({
      posPt: z.number(),
      align: z.enum(["left", "center", "right", "decimal", "bar"]).optional(),
      leader: z.enum(["none", "dot", "hyphen", "underscore", "middle_dot"]).optional()
    })
  ).optional(),
  borders: z.object({
    top: BorderSchema.optional(),
    left: BorderSchema.optional(),
    bottom: BorderSchema.optional(),
    right: BorderSchema.optional(),
    between: BorderSchema.optional()
  }).optional(),
  shading: z.string().optional(),
  dropCap: z.object({
    lines: z.number(),
    kind: z.enum(["dropped", "in_margin"]),
    distancePt: z.number()
  }).optional()
});
var RunsSchema = z.array(FragmentRunSchema);
var ListItemSchema = z.union([
  RunsSchema,
  z.object({
    runs: RunsSchema,
    level: z.number().optional(),
    checked: z.boolean().nullable().optional()
  })
]);
var TableCellSchema = z.object({
  runs: RunsSchema,
  colSpan: z.number().optional(),
  vMerge: z.enum(["none", "restart", "continue"]).optional(),
  shading: z.string().optional(),
  vAlign: z.enum(["top", "middle", "bottom"]).optional(),
  alignH: z.enum(["start", "center", "end"]).optional(),
  borders: z.object({
    top: BorderSchema.optional(),
    left: BorderSchema.optional(),
    bottom: BorderSchema.optional(),
    right: BorderSchema.optional()
  }).optional()
});
var TableRowSchema = z.object({ cells: z.array(TableCellSchema) });
var TableColumnSchema = z.object({
  align: z.enum(["left", "center", "right"]).optional(),
  width: z.number().optional(),
  widthKind: z.enum(["fixed", "fraction"]).optional()
});
var IdSchema = z.string().min(1).optional();
var BaseBlockSchema = z.union([
  z.object({ id: IdSchema, kind: z.literal("paragraph"), runs: RunsSchema, props: ParaPropsSchema.optional() }),
  z.object({
    id: IdSchema,
    kind: z.literal("heading"),
    level: z.number().int().min(1).max(6),
    runs: RunsSchema,
    props: ParaPropsSchema.optional()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("list"),
    style: z.enum([
      "bullet",
      "decimal",
      "lower_alpha",
      "upper_alpha",
      "lower_roman",
      "upper_roman",
      "decimal_leading_zero"
    ]),
    items: z.array(ListItemSchema),
    numId: z.string().optional(),
    startOverride: z.number().optional(),
    bulletChar: z.string().optional()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("table"),
    columns: z.array(TableColumnSchema),
    header: TableRowSchema.optional(),
    rows: z.array(TableRowSchema),
    rowHeadColumns: z.number().optional()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("image"),
    src: z.string(),
    srcKind: z.enum(["path", "bytes"]).optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    alt: z.string().optional()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("imageSvg"),
    svg: z.string(),
    width: z.number().optional(),
    height: z.number().optional(),
    alt: z.string().optional()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("code"),
    language: z.string().optional(),
    code: z.string()
  }),
  z.object({
    id: IdSchema,
    kind: z.literal("horizontalRule"),
    widthPct: z.number().optional(),
    thicknessPt: z.number().optional(),
    color: z.string().optional(),
    style: z.enum(["solid", "dashed", "dotted", "double"]).optional(),
    alignment: z.enum(["start", "center", "end", "justify"]).optional()
  })
]);
var BlockquoteSchema = z.object({
  id: IdSchema,
  kind: z.literal("blockquote"),
  children: z.lazy(() => z.array(StructuredBlockSchema))
});
var StructuredBlockSchema = z.union([
  BaseBlockSchema,
  BlockquoteSchema
]);
var StructuredBlockListSchema = z.array(
  StructuredBlockSchema
);
function hardenMarks(marks) {
  return marks.filter((m) => m.type !== "link" || isSafeHref(m.href));
}
function hardenRuns(runs) {
  return runs.map((r) => ({ text: r.text, marks: hardenMarks(r.marks) }));
}
function hardenBlock(block) {
  switch (block.kind) {
    case "paragraph":
    case "heading":
      return { ...block, runs: hardenRuns(block.runs) };
    case "list":
      return {
        ...block,
        items: block.items.map(
          (item) => Array.isArray(item) ? hardenRuns(item) : { ...item, runs: hardenRuns(item.runs) }
        )
      };
    case "table":
      return {
        ...block,
        ...block.header ? {
          header: {
            cells: block.header.cells.map((c) => ({ ...c, runs: hardenRuns(c.runs) }))
          }
        } : {},
        rows: block.rows.map((row) => ({
          cells: row.cells.map((c) => ({ ...c, runs: hardenRuns(c.runs) }))
        }))
      };
    case "blockquote":
      return { ...block, children: block.children.map((c) => hardenBlock(c)) };
    default:
      return block;
  }
}
function parseStructuredBlocks(json) {
  let raw;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  const parsed = StructuredBlockListSchema.safeParse(raw);
  if (!parsed.success) return null;
  return parsed.data.map((b) => hardenBlock(b));
}
function serializeStructuredBlocks(blocks) {
  return JSON.stringify(blocks);
}

// src/clipboard-codec.ts
var CLIPBOARD_MIME_PLAIN = "text/plain";
var CLIPBOARD_MIME_HTML = "text/html";
var CLIPBOARD_MIME_DOCZ = "application/x-norkron-docz+json";
var CLIPBOARD_MIME_DOCZ_WEB = "web application/x-norkron-docz+json";
function paragraph(text) {
  const runs = [{ text, marks: [] }];
  return { kind: "paragraph", runs };
}
function clipboardFlavours(module, blocks) {
  const plain = legacyBlocksFromStructured(blocks).map((b) => b.text).join("\n");
  const html = blocks.map((block) => {
    const r = module.renderBlockDto(block);
    return r.ok ? bodyInnerHtml(r.html) : "";
  }).filter((s) => s.length > 0).join("\n");
  return { plain, html, payload: serializeStructuredBlocks(blocks) };
}
function blocksFromPlain(plain) {
  const normalized = plain.replace(/\r\n?/g, "\n");
  if (normalized.length === 0) return [];
  const out = [];
  for (const group of normalized.split(/\n{2,}/)) {
    for (const line of group.split("\n")) out.push(paragraph(line));
  }
  return out;
}
function parseClipboard(input, opts) {
  if (input.payload) {
    const blocks = parseStructuredBlocks(input.payload);
    if (blocks && blocks.length > 0) return blocks;
  }
  const parseDoc = opts.parseDoc ?? ambientParseDoc();
  if (input.html && parseDoc) {
    const safe = sanitizeHtml(input.html, PASTE_PROFILE);
    const exploded = explodeRawHtml(safe, parseDoc);
    if (exploded && exploded.length > 0) return exploded;
  }
  if (input.plain) return blocksFromPlain(input.plain);
  return [];
}
function ambientParseDoc() {
  return typeof document !== "undefined" ? document : null;
}

// src/clipboard-write.ts
function confirmedWrite(data, mime, value) {
  try {
    if (data.getData(mime) === value) return true;
  } catch {
  }
  try {
    return Array.from(data.types ?? []).includes(mime);
  } catch {
    return false;
  }
}
function writeFlavoursTo(data, flavours) {
  if (data == null) return false;
  try {
    data.setData(CLIPBOARD_MIME_PLAIN, flavours.plain);
  } catch {
    return false;
  }
  if (flavours.html) {
    try {
      data.setData(CLIPBOARD_MIME_HTML, flavours.html);
    } catch {
    }
  }
  if (flavours.payload) {
    try {
      data.setData(CLIPBOARD_MIME_DOCZ, flavours.payload);
    } catch {
    }
  }
  return confirmedWrite(data, CLIPBOARD_MIME_PLAIN, flavours.plain);
}
function nativeClipboardCommandSupported(doc, type) {
  const d = doc;
  if (typeof d.execCommand !== "function") return false;
  try {
    return d.queryCommandSupported?.(type) ?? true;
  } catch {
    return true;
  }
}
function runNativeClipboardCommand(opts) {
  const { doc, type, handle } = opts;
  const d = doc;
  if (typeof d.execCommand !== "function") return "unsupported";
  let sawEvent = false;
  let wrote = false;
  const oneShot = (ev) => {
    sawEvent = true;
    ev.stopPropagation();
    wrote = handle(ev);
  };
  doc.addEventListener(type, oneShot, true);
  try {
    d.execCommand(type);
  } catch {
    return "unsupported";
  } finally {
    doc.removeEventListener(type, oneShot, true);
  }
  if (!sawEvent) return "unsupported";
  return wrote ? "ok" : "denied";
}
function asyncClipboardWritable() {
  const nav = globalThis.navigator;
  if (!nav || typeof nav.clipboard?.writeText !== "function") return false;
  return globalThis.isSecureContext === true;
}
function asyncClipboardWritePresent() {
  const clip = globalThis.navigator?.clipboard;
  return typeof clip?.write === "function" || typeof clip?.writeText === "function";
}
function clipboardWriteSupported(doc, type) {
  return nativeClipboardCommandSupported(doc, type) || asyncClipboardWritable();
}
function clipboardItem(flavours, withPayload) {
  const Ctor = globalThis.ClipboardItem;
  if (typeof Ctor !== "function") return null;
  const parts = {
    [CLIPBOARD_MIME_PLAIN]: new Blob([flavours.plain], { type: CLIPBOARD_MIME_PLAIN })
  };
  if (flavours.html) {
    parts[CLIPBOARD_MIME_HTML] = new Blob([flavours.html], { type: CLIPBOARD_MIME_HTML });
  }
  if (withPayload && flavours.payload) {
    parts[CLIPBOARD_MIME_DOCZ_WEB] = new Blob([flavours.payload], { type: "text/plain" });
  }
  try {
    return new Ctor(parts);
  } catch {
    return null;
  }
}
async function writeFlavoursToSystem(flavours) {
  const nav = globalThis.navigator;
  const clip = nav?.clipboard;
  if (!clip) return false;
  if (typeof clip.write === "function") {
    for (const withPayload of [true, false]) {
      const item = clipboardItem(flavours, withPayload);
      if (!item) break;
      try {
        await clip.write([item]);
        return true;
      } catch {
      }
    }
  }
  if (typeof clip.writeText !== "function") return false;
  try {
    await clip.writeText(flavours.plain);
    return true;
  } catch {
    return false;
  }
}
function asyncClipboardReadable() {
  const nav = globalThis.navigator;
  if (!nav || typeof nav.clipboard?.read !== "function") return false;
  return globalThis.isSecureContext === true;
}
function watchClipboardReadPermission(onChange) {
  const perms = globalThis.navigator?.permissions;
  if (!perms || typeof perms.query !== "function") {
    onChange("unknown");
    return () => {
    };
  }
  let disposed = false;
  let detach = null;
  try {
    void perms.query({ name: "clipboard-read" }).then((status) => {
      if (disposed) return;
      onChange(status.state);
      const listener = () => onChange(status.state);
      status.addEventListener?.("change", listener);
      detach = () => status.removeEventListener?.("change", listener);
    }).catch(() => {
      if (!disposed) onChange("unknown");
    });
  } catch {
    onChange("unknown");
  }
  return () => {
    disposed = true;
    detach?.();
  };
}

// src/codec.ts
var UTF83 = new TextEncoder();
var BLOCK_KIND_TO_TAG2 = {
  paragraph: 0,
  heading: 1,
  list_item: 2,
  blockquote: 3,
  code: 4,
  html: 5,
  // A `table_cell` is a PROJECTION kind, not a transport kind: it names one cell
  // of a structured `table` so a canvas caret can address it (`types.ts`). The
  // legacy render wire has no cell tag and never needs one — a cell holds a plain
  // run slice, which renders exactly as a paragraph does. Mapping it to 0 keeps
  // this table TOTAL (the compile-time gate that makes a new kind a decision
  // rather than a silent default) without inventing a wire tag the shim would
  // reject. The DECODE table below deliberately does NOT produce it: tag 0 is a
  // paragraph, and a cell only ever comes back through the structured block-dto.
  table_cell: 0
};
var TAG_TO_BLOCK_KIND = [
  "paragraph",
  "heading",
  "list_item",
  "blockquote",
  "code",
  "html"
];
var MAX_U32 = 4294967295;
function encodeRenderInput(input) {
  const textBytes = UTF83.encode(input.text);
  const comments = input.comments;
  const idByteArrays = comments.map((c) => UTF83.encode(c.commentId));
  let total = 4 + textBytes.length + 1 + 1 + 4;
  for (const id of idByteArrays) total += 4 + id.length + 4 + 4;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let o = 0;
  view.setUint32(o, textBytes.length >>> 0, true);
  o += 4;
  out.set(textBytes, o);
  o += textBytes.length;
  out[o] = BLOCK_KIND_TO_TAG2[input.kind ?? "paragraph"];
  o += 1;
  out[o] = (input.level ?? 0) & 255;
  o += 1;
  if (comments.length > MAX_U32) {
    throw new RangeError("web-docz: too many comment spans for the u32 ABI");
  }
  view.setUint32(o, comments.length >>> 0, true);
  o += 4;
  for (let i = 0; i < comments.length; i++) {
    const c = comments[i];
    const id = idByteArrays[i];
    view.setUint32(o, id.length >>> 0, true);
    o += 4;
    out.set(id, o);
    o += id.length;
    view.setUint32(o, c.start >>> 0, true);
    o += 4;
    view.setUint32(o, c.end >>> 0, true);
    o += 4;
  }
  return out;
}
function decodeRenderInput(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const dec = new TextDecoder();
  let p = 0;
  const need = (n2) => {
    if (p + n2 > bytes.length) throw new RangeError("web-docz: truncated ABI buffer");
  };
  need(4);
  const textLen = view.getUint32(p, true);
  p += 4;
  need(textLen);
  const text = dec.decode(bytes.subarray(p, p + textLen));
  p += textLen;
  need(2);
  const kind = TAG_TO_BLOCK_KIND[view.getUint8(p)] ?? "paragraph";
  p += 1;
  const level = view.getUint8(p);
  p += 1;
  need(4);
  const n = view.getUint32(p, true);
  p += 4;
  const comments = [];
  for (let i = 0; i < n; i++) {
    need(4);
    const idLen = view.getUint32(p, true);
    p += 4;
    need(idLen);
    const commentId = dec.decode(bytes.subarray(p, p + idLen));
    p += idLen;
    need(8);
    const start = view.getUint32(p, true);
    p += 4;
    const end = view.getUint32(p, true);
    p += 4;
    comments.push({ commentId, start, end });
  }
  return { text, comments, kind, level };
}

// src/dom-text.ts
var BLOCK_TAGS = /* @__PURE__ */ new Set([
  "address",
  "article",
  "blockquote",
  "div",
  "dd",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "li",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "tbody",
  "tfoot",
  "thead",
  "tr",
  "ul"
]);
function domBlockText(root, opts) {
  if (!root) return "";
  const sep = opts?.separator ?? "\n";
  let out = "";
  let pending = 0;
  const boundary = (n) => {
    if (out.length === 0) return;
    pending = Math.max(pending, 1) ;
  };
  const visit = (node) => {
    if (node.nodeType === 3) {
      const value = node.nodeValue ?? "";
      if (value.length === 0) return;
      if (pending > 0) {
        out += sep.repeat(pending);
        pending = 0;
      }
      out += value;
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node;
    const tag = el.tagName.toLowerCase();
    if (tag === "br") {
      if (out.length > 0) pending += 1;
      return;
    }
    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock) boundary();
    for (const child of Array.from(el.childNodes)) visit(child);
    if (isBlock) boundary();
  };
  visit(root);
  return out.replace(/\s+$/, "");
}
function domMarkdownText(root) {
  return domBlockText(root, { separator: "\n\n" }).replace(/\n{3,}/g, "\n\n");
}

// src/edit-verb-codec.ts
var LIST_STYLE_ARG = {
  bullet: 0,
  decimal: 1,
  lower_alpha: 2,
  upper_alpha: 3,
  lower_roman: 4,
  upper_roman: 5,
  decimal_leading_zero: 6
};
var VALIGN_ARG = { top: 1, middle: 2, bottom: 3 };
function tableWire(table) {
  const w = new ByteWriter();
  writeTablePayload(w, table);
  return w;
}
function encodeInsertRow(table, atRow, where) {
  return tableWire(table).u32(atRow).u8(where === "below" ? 1 : 0).finish();
}
function encodeDeleteRow(table, row) {
  return tableWire(table).u32(row).finish();
}
function encodeInsertColumn(table, atCol, where) {
  return tableWire(table).u32(atCol).u8(where === "right" ? 1 : 0).finish();
}
function encodeDeleteColumn(table, col) {
  return tableWire(table).u32(col).finish();
}
function encodeMergeCells(table, r0, c0, r1, c1) {
  return tableWire(table).u32(r0).u32(c0).u32(r1).u32(c1).finish();
}
function encodeSplitCell(table, row, col, intoRows, intoCols) {
  return tableWire(table).u32(row).u32(col).u16(intoRows).u16(intoCols).finish();
}
function encodeSetCellShading(table, row, col, hex) {
  const w = tableWire(table).u32(row).u32(col);
  if (hex !== void 0) w.u8(1).str(hex);
  else w.u8(0);
  return w.finish();
}
function encodeSetCellVAlign(table, row, col, valign) {
  return tableWire(table).u32(row).u32(col).u8(valign === void 0 ? 0 : VALIGN_ARG[valign]).finish();
}
function encodeSetCellBorders(table, row, col, borders) {
  const w = tableWire(table).u32(row).u32(col);
  writeBordersArg(w, borders);
  return w.finish();
}
function encodeSetColumnWidth(table, col, width, kind) {
  return tableWire(table).u32(col).u8(kind === "fixed" ? 0 : 1).f32(width).finish();
}
function encodeToggleHeader(table, on) {
  return tableWire(table).u8(on ? 1 : 0).finish();
}
function encodeSetRowHeadColumns(table, n) {
  return tableWire(table).u32(n).finish();
}
function decodeTable(bytes) {
  return decodeTablePayload(bytes);
}
function blockListWire(blocks) {
  const w = new ByteWriter();
  writeBlockList(w, blocks);
  return w;
}
function encodeSetListItemLevel(blocks, blockIndex, itemIndex, delta) {
  return blockListWire(blocks).u32(blockIndex).u32(itemIndex).u8(delta & 255).finish();
}
function encodeSetListStyle(blocks, index, style) {
  return blockListWire(blocks).u32(index).u8(LIST_STYLE_ARG[style]).finish();
}
function encodeRestartList(blocks, index, opts) {
  const w = blockListWire(blocks).u32(index);
  if (opts.numId !== void 0) w.u8(1).str(opts.numId);
  else w.u8(0);
  if (opts.start !== void 0) w.u8(1).u32(opts.start);
  else w.u8(0);
  return w.finish();
}
function encodeSetStartOverride(blocks, index, start) {
  const w = blockListWire(blocks).u32(index);
  if (start !== void 0) w.u8(1).u32(start);
  else w.u8(0);
  return w.finish();
}
function encodeSetListBulletChar(blocks, index, glyph) {
  const w = blockListWire(blocks).u32(index);
  if (glyph !== void 0) w.u8(1).str(glyph);
  else w.u8(0);
  return w.finish();
}
function encodeSetListChecklist(blocks, index, on) {
  return blockListWire(blocks).u32(index).u8(on ? 1 : 0).finish();
}
function encodeToggleListItemChecked(blocks, blockIndex, itemIndex) {
  return blockListWire(blocks).u32(blockIndex).u32(itemIndex).finish();
}
function decodeDocBody(bytes) {
  return decodeBlockList(bytes);
}

// src/verbs.ts
function callOutPtr(ex, fn, input) {
  const inLen = input.length;
  const inAlloc = inLen === 0 ? 1 : inLen;
  const inPtr = ex.dz_alloc(inAlloc);
  if (inPtr === 0) return { ok: false, errCode: DZ_ERR_OOM };
  new Uint8Array(ex.memory.buffer, inPtr, inLen).set(input);
  const scratch = ex.dz_alloc(8);
  if (scratch === 0) {
    ex.dz_free(inPtr, inAlloc);
    return { ok: false, errCode: DZ_ERR_OOM };
  }
  try {
    const rc = fn(inPtr, inLen, scratch, scratch + 4);
    if (rc !== DZ_OK) return { ok: false, errCode: rc };
    const view = new DataView(ex.memory.buffer);
    const outPtr = view.getUint32(scratch, true);
    const outLen = view.getUint32(scratch + 4, true);
    const bytes = new Uint8Array(ex.memory.buffer, outPtr, outLen).slice();
    ex.dz_free(outPtr, outLen);
    return { ok: true, value: bytes };
  } finally {
    ex.dz_free(inPtr, inAlloc);
    ex.dz_free(scratch, 8);
  }
}
function runVerb(fn, input, ex, decode) {
  if (typeof fn !== "function") return { ok: false, errCode: DZ_ERR_UNAVAILABLE };
  const raw = callOutPtr(ex, fn, input);
  if (!raw.ok) return raw;
  try {
    return { ok: true, value: decode(raw.value) };
  } catch {
    return { ok: false, errCode: DZ_ERR_DECODE };
  }
}
function buildVerbs(ex, features) {
  const hasAnchor = typeof ex.dz_anchor_build_selector === "function";
  const hasTransform = typeof ex.dz_transform_apply_mark === "function" || typeof ex.dz_transform_split_runs === "function" || typeof ex.dz_transform_edit_text === "function";
  const hasStyleMarks = typeof ex.dz_transform_apply_style_mark === "function";
  const hasMarkToggle = typeof ex.dz_transform_toggle_mark === "function";
  const hasBlockPalette = typeof ex.dz_render_block_to_html_dto === "function";
  return {
    anchorBuildSelector: (input) => runVerb(ex.dz_anchor_build_selector, encodeBuildSelectorInput(input), ex, decodeSelector),
    anchorRebase: (input) => runVerb(ex.dz_anchor_rebase, encodeRebaseInput(input), ex, decodeRebaseOutcome),
    transformApplyMark: (input) => runVerb(ex.dz_transform_apply_mark, encodeApplyMarkInput(input), ex, decodeRuns),
    transformApplyStyleMark: (input) => runVerb(ex.dz_transform_apply_style_mark, encodeApplyStyleMarkInput(input), ex, decodeRuns),
    transformToggleMark: (input) => runVerb(ex.dz_transform_toggle_mark, encodeToggleStyleMarkInput(input), ex, decodeRuns),
    transformSplitRuns: (input) => runVerb(ex.dz_transform_split_runs, encodeSplitRunsInput(input), ex, decodeRuns),
    transformEditText: (input) => runVerb(ex.dz_transform_edit_text, encodeEditTextInput(input), ex, decodeRuns),
    supportsAnchor: hasAnchor || (features & DZ_FEATURE_ANCHOR) !== 0,
    supportsTransform: hasTransform || (features & DZ_FEATURE_TRANSFORM) !== 0,
    supportsBlockKind: (features & DZ_FEATURE_BLOCK_KIND) !== 0,
    supportsStyleMarks: hasStyleMarks || (features & DZ_FEATURE_STYLE_MARKS) !== 0,
    supportsMarkToggle: hasMarkToggle || (features & DZ_FEATURE_MARK_TOGGLE) !== 0,
    supportsBlockPalette: hasBlockPalette || (features & DZ_FEATURE_BLOCK_PALETTE) !== 0,
    // W2 paragraph geometry rides on the mark-general render verb (bit3): when
    // present, `dz_render_runs_to_html` decodes the optional ParaProps tail.
    supportsParaProps: hasStyleMarks || (features & DZ_FEATURE_STYLE_MARKS) !== 0
  };
}

// src/edit-verbs.ts
function buildEditVerbs(ex, features) {
  const hasTable = typeof ex.dz_table_insert_row === "function";
  const hasList = typeof ex.dz_transform_set_list_item_level === "function";
  return {
    tableInsertRow: (table, atRow, where) => runVerb(ex.dz_table_insert_row, encodeInsertRow(table, atRow, where), ex, decodeTable),
    tableDeleteRow: (table, row) => runVerb(ex.dz_table_delete_row, encodeDeleteRow(table, row), ex, decodeTable),
    tableInsertColumn: (table, atCol, where) => runVerb(ex.dz_table_insert_column, encodeInsertColumn(table, atCol, where), ex, decodeTable),
    tableDeleteColumn: (table, col) => runVerb(ex.dz_table_delete_column, encodeDeleteColumn(table, col), ex, decodeTable),
    tableMergeCells: (table, r0, c0, r1, c1) => runVerb(ex.dz_table_merge_cells, encodeMergeCells(table, r0, c0, r1, c1), ex, decodeTable),
    tableSplitCell: (table, row, col, intoRows, intoCols) => runVerb(
      ex.dz_table_split_cell,
      encodeSplitCell(table, row, col, intoRows, intoCols),
      ex,
      decodeTable
    ),
    tableSetCellShading: (table, row, col, hex) => runVerb(
      ex.dz_table_set_cell_shading,
      encodeSetCellShading(table, row, col, hex),
      ex,
      decodeTable
    ),
    tableSetCellVAlign: (table, row, col, valign) => runVerb(
      ex.dz_table_set_cell_valign,
      encodeSetCellVAlign(table, row, col, valign),
      ex,
      decodeTable
    ),
    tableSetCellBorders: (table, row, col, borders) => runVerb(
      ex.dz_table_set_cell_borders,
      encodeSetCellBorders(table, row, col, borders),
      ex,
      decodeTable
    ),
    tableSetColumnWidth: (table, col, width, kind) => runVerb(
      ex.dz_table_set_column_width,
      encodeSetColumnWidth(table, col, width, kind),
      ex,
      decodeTable
    ),
    tableToggleHeader: (table, on) => runVerb(ex.dz_table_toggle_header, encodeToggleHeader(table, on), ex, decodeTable),
    tableSetRowHeadColumns: (table, n) => runVerb(
      ex.dz_table_set_row_head_columns,
      encodeSetRowHeadColumns(table, n),
      ex,
      decodeTable
    ),
    setListItemLevel: (blocks, blockIndex, itemIndex, delta) => runVerb(
      ex.dz_transform_set_list_item_level,
      encodeSetListItemLevel(blocks, blockIndex, itemIndex, delta),
      ex,
      decodeDocBody
    ),
    setListStyle: (blocks, index, style) => runVerb(
      ex.dz_transform_set_list_style,
      encodeSetListStyle(blocks, index, style),
      ex,
      decodeDocBody
    ),
    restartList: (blocks, index, opts) => runVerb(
      ex.dz_transform_restart_list,
      encodeRestartList(blocks, index, opts),
      ex,
      decodeDocBody
    ),
    setStartOverride: (blocks, index, start) => runVerb(
      ex.dz_transform_set_start_override,
      encodeSetStartOverride(blocks, index, start),
      ex,
      decodeDocBody
    ),
    setListBulletChar: (blocks, index, glyph) => runVerb(
      ex.dz_transform_set_bullet_char,
      encodeSetListBulletChar(blocks, index, glyph),
      ex,
      decodeDocBody
    ),
    setListChecklist: (blocks, index, on) => runVerb(
      ex.dz_transform_set_list_checklist,
      encodeSetListChecklist(blocks, index, on),
      ex,
      decodeDocBody
    ),
    toggleListItemChecked: (blocks, blockIndex, itemIndex) => runVerb(
      ex.dz_transform_toggle_list_check,
      encodeToggleListItemChecked(blocks, blockIndex, itemIndex),
      ex,
      decodeDocBody
    ),
    supportsTableVerbs: hasTable || (features & DZ_FEATURE_TABLE_VERBS) !== 0,
    supportsListVerbs: hasList || (features & DZ_FEATURE_LIST_VERBS) !== 0,
    // EXPORT PROBE ONLY — deliberately NOT or-ed with the list feature bit.
    // The bit was defined when the six original verbs shipped, so an older
    // blob sets it while exporting nothing named `set_list_checklist`; reading
    // the bit here would report the OFF path available on precisely the blobs
    // that lack it.
    supportsChecklistOff: typeof ex.dz_transform_set_list_checklist === "function"
  };
}

// src/editable-floor.ts
var FLOOR_BLOCK_ID = "b0";
function blocksWithEditableFloor(blocks) {
  if (blocks.length > 0) return blocks;
  return [{ id: FLOOR_BLOCK_ID, text: "", kind: "paragraph" }];
}
function structuredBlocksWithEditableFloor(blocks) {
  if (blocks.length > 0) return blocks;
  return [{ id: FLOOR_BLOCK_ID, kind: "paragraph", runs: [] }];
}

// src/fragment-dom.ts
var FRAGMENT_SURFACE_TESTID = "docz-fragment";
function renderFragmentToHtml(module, frag) {
  if (module.supportsStyleMarks) {
    const para = frag.para;
    const props = para && !isDefaultParaProps(para) ? para : void 0;
    const r2 = module.renderRunsToHtml(fragmentToWasmRuns(frag), frag.kind, frag.level, props);
    if (r2.ok) return bodyInnerHtml(r2.html);
  }
  const input = fragmentToRenderInput(frag);
  const r = module.renderBlockToHtml(input.text, input.comments, input.kind, input.level);
  return r.ok ? bodyInnerHtml(r.html) : "";
}
function stamp(root, frag, styleMarksInHtml) {
  if (!root) return;
  root.classList.add(DOCZ_BLOCK_CLASS);
  root.setAttribute("data-block-kind", frag.kind ?? "paragraph");
  if (frag.kind === "heading" && frag.level) root.setAttribute("data-level", String(frag.level));
  const types = /* @__PURE__ */ new Set();
  let hasText = false;
  for (const run of frag.runs) {
    if (byteLen(run.text) > 0) hasText = true;
    for (const m of run.marks) if (m.type !== "comment") types.add(m.type);
  }
  if (!styleMarksInHtml && types.size > 0) root.setAttribute("data-marks", [...types].sort().join(" "));
  if (!hasText) root.setAttribute("data-empty", "true");
}
function buildFragmentDom(module, frag) {
  const html = renderFragmentToHtml(module, frag);
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const root = tpl.content.firstElementChild;
  stamp(root, frag, module.supportsStyleMarks);
  return { fragment: tpl.content, root };
}
function renderFragmentToDom(module, frag) {
  return buildFragmentDom(module, frag).fragment;
}
function mountFragment(module, container, frag) {
  const { fragment, root } = buildFragmentDom(module, frag);
  container.appendChild(fragment);
  return root;
}
function renderFragmentInto(module, container, frag) {
  container.replaceChildren();
  return mountFragment(module, container, frag);
}

// src/fragment-ops.ts
function meta(frag) {
  return { kind: frag.kind ?? "paragraph", level: frag.level, baseVersionId: frag.baseVersionId };
}
function editFragmentText(verbs, frag, edit) {
  const runs = fragmentToWasmRuns(frag);
  const res = verbs.transformEditText({
    runs,
    start: edit.start,
    end: edit.end,
    replacement: edit.replacement
  });
  if (!res.ok) return res;
  return { ok: true, fragment: wasmRunsToFragment(res.value, meta(frag)) };
}
function applyCommentMark(verbs, frag, mark) {
  const runs = fragmentToWasmRuns(frag);
  const res = verbs.transformApplyMark({
    runs,
    commentId: mark.commentId,
    start: mark.start,
    end: mark.end
  });
  if (!res.ok) return res;
  return { ok: true, fragment: wasmRunsToFragment(res.value, meta(frag)) };
}
function splitFragment(verbs, frag, offsets) {
  const runs = fragmentToWasmRuns(frag);
  const res = verbs.transformSplitRuns({ runs, offsets });
  if (!res.ok) return res;
  return { ok: true, fragment: wasmRunsToFragment(res.value, meta(frag)) };
}
function applyStyleMark(verbs, frag, mark, range) {
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  if (hi <= lo) return { ok: true, fragment: frag };
  if (mark.type === "link" && !isSafeHref(mark.href)) return { ok: true, fragment: frag };
  const runs = fragmentToWasmRuns(frag);
  if (verbs.supportsStyleMarks) {
    const res2 = verbs.transformApplyStyleMark({ runs, mark, start: lo, end: hi });
    if (!res2.ok) return res2;
    return { ok: true, fragment: wasmRunsToFragment(res2.value, meta(frag)) };
  }
  const res = verbs.transformSplitRuns({ runs, offsets: [lo, hi] });
  if (!res.ok) return res;
  const styleMarks = [{ mark, start: lo, end: hi }];
  return { ok: true, fragment: wasmRunsToFragment(res.value, { ...meta(frag), styleMarks }) };
}
function toggleStyleMark(verbs, frag, mark, range) {
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  if (hi <= lo) return { ok: true, fragment: frag };
  if (mark.type === "link" && !isSafeHref(mark.href)) return { ok: true, fragment: frag };
  if (verbs.supportsMarkToggle) {
    const runs = fragmentToWasmRuns(frag);
    const res = verbs.transformToggleMark({ runs, mark, start: lo, end: hi });
    if (!res.ok) return res;
    return { ok: true, fragment: wasmRunsToFragment(res.value, meta(frag)) };
  }
  return applyStyleMark(verbs, frag, mark, { start: lo, end: hi });
}
function setFragmentKind(frag, kind, level) {
  return {
    ...frag,
    kind,
    level: level == null ? frag.level : Math.min(6, Math.max(1, level))
  };
}
function clearFragmentMarks(verbs, frag, range) {
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  if (hi <= lo) return { ok: true, fragment: frag };
  const res = verbs.transformSplitRuns({ runs: fragmentToWasmRuns(frag), offsets: [lo, hi] });
  if (!res.ok) return res;
  let offset = 0;
  const runs = res.value.map((run) => {
    const start = offset;
    const end = offset + byteLen(run.text);
    offset = end;
    if (start >= lo && end <= hi) {
      return { text: run.text, marks: run.marks.filter((m) => m.type === "comment") };
    }
    return run;
  });
  return { ok: true, fragment: wasmRunsToFragment(runs, meta(frag)) };
}
function sliceFragment(verbs, frag, range) {
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  if (hi <= lo) return { ok: true, fragment: { ...meta(frag), runs: [{ text: "", marks: [] }] } };
  const res = verbs.transformSplitRuns({ runs: fragmentToWasmRuns(frag), offsets: [lo, hi] });
  if (!res.ok) return res;
  let offset = 0;
  const inRange = [];
  for (const run of res.value) {
    const start = offset;
    const end = offset + byteLen(run.text);
    offset = end;
    if (start >= lo && end <= hi && end > start) inRange.push(run);
  }
  return { ok: true, fragment: wasmRunsToFragment(inRange, meta(frag)) };
}
function changeFragmentCase(verbs, frag, range, mode) {
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  if (hi <= lo) return { ok: true, fragment: frag };
  const res = verbs.transformSplitRuns({ runs: fragmentToWasmRuns(frag), offsets: [lo, hi] });
  if (!res.ok) return res;
  let offset = 0;
  const before = [];
  const inRange = [];
  const after = [];
  for (const run of res.value) {
    const start = offset;
    const end = offset + byteLen(run.text);
    offset = end;
    if (end <= lo) before.push(run);
    else if (start >= hi) after.push(run);
    else inRange.push(run);
  }
  const cased = changeCaseRuns(inRange, mode);
  return { ok: true, fragment: wasmRunsToFragment([...before, ...cased, ...after], meta(frag)) };
}

// src/handle-mutations.ts
var UTF84 = new TextEncoder();
function legacyWithBlockComment(doc, blockId, span2) {
  const blocks = doc.blocks.map(
    (b) => b.id === blockId ? { ...b, comments: [...b.comments ?? [], span2] } : b
  );
  return { ...doc, blocks };
}
function legacyWithBlockText(doc, blockId, newText) {
  const byteLen2 = UTF84.encode(newText).length;
  const blocks = doc.blocks.map((b) => {
    if (b.id !== blockId) return b;
    const comments = (b.comments ?? []).filter((c) => c.end <= byteLen2 && c.start < c.end);
    return { ...b, text: newText, comments };
  });
  return { ...doc, blocks };
}
function legacyEditBlockText(module, doc, blockId, edit) {
  const block = doc.blocks.find((b) => b.id === blockId);
  if (!block) return doc;
  let frag = textFragment(block.text, {
    kind: block.kind,
    level: block.level,
    baseVersionId: doc.baseVersionId
  });
  for (const c of block.comments ?? []) {
    const marked = applyCommentMark(module, frag, {
      commentId: c.commentId,
      start: c.start,
      end: c.end
    });
    if (marked.ok) frag = marked.fragment;
  }
  const edited = editFragmentText(module, frag, {
    start: edit.at,
    end: edit.at + edit.deleteBytes,
    replacement: edit.insert
  });
  if (!edited.ok) return doc;
  const nextBlock = {
    ...block,
    text: fragmentText(edited.fragment),
    comments: fragmentCommentSpans(edited.fragment)
  };
  return { ...doc, blocks: doc.blocks.map((b) => b.id === blockId ? nextBlock : b) };
}
function legacySplitBlock(doc, blockId, atByte, newBlockId) {
  const idx = doc.blocks.findIndex((b) => b.id === blockId);
  if (idx < 0) return doc;
  const block = doc.blocks[idx];
  if (block.kind === "html") return doc;
  const bytes = UTF84.encode(block.text);
  const at = Math.max(0, Math.min(atByte, bytes.length));
  const decoder = new TextDecoder();
  const headText = decoder.decode(bytes.subarray(0, at));
  const tailText = decoder.decode(bytes.subarray(at));
  const comments = block.comments ?? [];
  const headComments = comments.filter((c) => c.start < c.end && c.end <= at);
  const tailComments = comments.filter((c) => c.start < c.end && c.start >= at).map((c) => ({ ...c, start: c.start - at, end: c.end - at }));
  const headBlock = { ...block, text: headText, comments: headComments };
  const tailBlock = {
    id: newBlockId,
    text: tailText,
    kind: "paragraph",
    comments: tailComments
  };
  const blocks = [...doc.blocks];
  blocks.splice(idx, 1, headBlock, tailBlock);
  return { ...doc, blocks };
}
function legacyMergeBlock(doc, blockId) {
  const idx = doc.blocks.findIndex((b) => b.id === blockId);
  if (idx <= 0) return doc;
  const prev = doc.blocks[idx - 1];
  const block = doc.blocks[idx];
  if (prev.kind === "html" || block.kind === "html") return doc;
  const joinByte = UTF84.encode(prev.text).length;
  const shifted = (block.comments ?? []).filter((c) => c.start < c.end).map((c) => ({ ...c, start: c.start + joinByte, end: c.end + joinByte }));
  const mergedBlock = {
    ...prev,
    text: prev.text + block.text,
    comments: [...prev.comments ?? [], ...shifted]
  };
  const blocks = [...doc.blocks];
  blocks.splice(idx - 1, 2, mergedBlock);
  return { ...doc, blocks };
}
function legacyDeleteBlock(doc, blockId) {
  if (blockId === "") return doc;
  const idx = doc.blocks.findIndex((b) => b.id === blockId);
  if (idx < 0) return doc;
  const blocks = [...doc.blocks];
  blocks.splice(idx, 1);
  return { ...doc, blocks };
}
function legacyInsertBlockAfter(doc, blockId, block) {
  const idx = doc.blocks.findIndex((b) => b.id === blockId);
  const insertAt = idx < 0 ? doc.blocks.length : idx + 1;
  const blocks = [...doc.blocks];
  blocks.splice(insertAt, 0, block);
  return { ...doc, blocks };
}

// src/sub-block.ts
function listItemRuns3(item) {
  return Array.isArray(item) ? item : item.runs;
}
function listItemLevel(item) {
  return Array.isArray(item) ? void 0 : item.level;
}
function itemWithRuns(item, runs) {
  return Array.isArray(item) ? [...runs] : { ...item, runs: [...runs] };
}
function itemLike(item, runs) {
  const level = listItemLevel(item);
  if (Array.isArray(item)) return [...runs];
  return level != null ? { runs: [...runs], level } : { runs: [...runs] };
}
function cellAt(block, seg) {
  const row = seg.header ? block.header : block.rows[seg.row];
  return row?.cells[seg.col];
}
function concretePath(block, segments) {
  const seg = segments[0];
  if (seg === void 0) {
    if (block.kind === "paragraph" || block.kind === "heading") return [];
    if (block.kind === "list" && block.items.length === 1) return [{ kind: "listItem", index: 0 }];
    if (block.kind === "blockquote" && block.children.length === 1) {
      const inner = concretePath(block.children[0], []);
      return inner ? [{ kind: "quoteChild", index: 0 }, ...inner] : null;
    }
    return null;
  }
  const rest = segments.slice(1);
  switch (seg.kind) {
    case "listItem": {
      if (block.kind !== "list" || rest.length > 0) return null;
      return block.items[seg.index] === void 0 ? null : [seg];
    }
    case "quoteChild": {
      if (block.kind !== "blockquote") return null;
      const child = block.children[seg.index];
      if (child === void 0) return null;
      const inner = concretePath(child, rest);
      return inner ? [seg, ...inner] : null;
    }
    case "tableCell": {
      if (block.kind !== "table" || rest.length > 0) return null;
      return cellAt(block, seg) === void 0 ? null : [seg];
    }
  }
}
function readAtPath(block, path) {
  const seg = path[0];
  if (seg === void 0) {
    if (block.kind === "paragraph") return { kind: "paragraph", runs: block.runs };
    if (block.kind === "heading") return { kind: "heading", level: block.level, runs: block.runs };
    return null;
  }
  switch (seg.kind) {
    case "listItem": {
      if (block.kind !== "list") return null;
      const item = block.items[seg.index];
      if (item === void 0) return null;
      const level = listItemLevel(item);
      return { kind: "list_item", ...level != null ? { level } : {}, runs: listItemRuns3(item) };
    }
    case "quoteChild": {
      if (block.kind !== "blockquote") return null;
      const child = block.children[seg.index];
      return child === void 0 ? null : readAtPath(child, path.slice(1));
    }
    case "tableCell": {
      if (block.kind !== "table") return null;
      const cell = cellAt(block, seg);
      return cell === void 0 ? null : { kind: "paragraph", runs: cell.runs };
    }
  }
}
function tableWithCell(block, seg, runs) {
  const patch = (cells) => cells.map((c, i) => i === seg.col ? { ...c, runs: [...runs] } : c);
  if (seg.header) {
    const header = block.header;
    if (!header) return block;
    return { ...block, header: { cells: patch(header.cells) } };
  }
  return {
    ...block,
    rows: block.rows.map((row, r) => r === seg.row ? { cells: patch(row.cells) } : row)
  };
}
function writeAtPath(block, path, frag, writeSelf) {
  const seg = path[0];
  if (seg === void 0) return writeSelf(block, frag);
  switch (seg.kind) {
    case "listItem": {
      if (block.kind !== "list") return null;
      if (block.items[seg.index] === void 0) return null;
      return {
        ...block,
        items: block.items.map((it, i) => i === seg.index ? itemWithRuns(it, frag.runs) : it)
      };
    }
    case "quoteChild": {
      if (block.kind !== "blockquote") return null;
      const child = block.children[seg.index];
      if (child === void 0) return null;
      const inner = writeAtPath(child, path.slice(1), frag, writeSelf);
      if (inner === null) return null;
      return {
        ...block,
        children: block.children.map((c, i) => i === seg.index ? inner : c)
      };
    }
    case "tableCell":
      if (block.kind !== "table") return null;
      return cellAt(block, seg) === void 0 ? null : tableWithCell(block, seg, frag.runs);
  }
}
function insertSiblingAfterPath(block, path, frag, blockFromFragment) {
  const seg = path[0];
  if (seg === void 0) return null;
  switch (seg.kind) {
    case "listItem": {
      if (block.kind !== "list") return null;
      const item = block.items[seg.index];
      if (item === void 0) return null;
      const items = [...block.items];
      items.splice(seg.index + 1, 0, itemLike(item, frag.runs));
      return { ...block, items };
    }
    case "quoteChild": {
      if (block.kind !== "blockquote") return null;
      const child = block.children[seg.index];
      if (child === void 0) return null;
      const rest = path.slice(1);
      if (rest.length > 0) {
        const inner = insertSiblingAfterPath(child, rest, frag, blockFromFragment);
        if (!inner) return null;
        return { ...block, children: block.children.map((c, i) => i === seg.index ? inner : c) };
      }
      const children = [...block.children];
      children.splice(seg.index + 1, 0, blockFromFragment(frag));
      return { ...block, children };
    }
    case "tableCell":
      return null;
  }
}

// src/sub-block-target.ts
function resolveTarget(blocks, blockId) {
  const addr = parseBlockAddress(blockId);
  let index = blocks.findIndex((b) => b.id === blockId);
  let segments = [];
  if (index < 0) {
    index = blocks.findIndex((b) => b.id === addr.rootId);
    segments = addr.segments;
  }
  if (index < 0) return null;
  const block = blocks[index];
  const path = concretePath(block, segments);
  return path ? { index, block, path } : null;
}
function containerIndexOf(blocks, blockId) {
  const exact = blocks.findIndex((b) => b.id === blockId);
  if (exact >= 0) return exact;
  return blocks.findIndex((b) => b.id === parseBlockAddress(blockId).rootId);
}
function keepId(block, id) {
  return id != null ? { ...block, id } : block;
}
function fragmentToBlock(frag, id) {
  const idPart = id != null ? { id } : {};
  const runs = [...frag.runs];
  if (frag.kind === "heading") {
    const level = Math.min(6, Math.max(1, frag.level ?? 1));
    return { ...idPart, kind: "heading", level, runs };
  }
  return { ...idPart, kind: "paragraph", runs };
}
function fragmentIntoBlock(block, frag) {
  const id = block.id;
  if (block.kind === "paragraph" || block.kind === "heading") {
    const leaf = fragmentToBlock(frag, id);
    const props = block.props;
    return props !== void 0 ? { ...leaf, props } : leaf;
  }
  const path = concretePath(block, []);
  if (path == null || path.length === 0) return null;
  return writeAtPath(
    block,
    path,
    frag,
    (b, f) => fragmentIntoBlock(b, f)
  );
}
function writeFragmentAt(blocks, t, frag) {
  const written = writeAtPath(
    t.block,
    t.path,
    frag,
    (b, f) => fragmentIntoBlock(b, f)
  );
  if (written == null) return null;
  const next = [...blocks];
  next[t.index] = keepId(written, t.block.id);
  return next;
}

// src/sub-block-structure.ts
var MAX_LIST_LEVEL = 8;
function regionSlots(block) {
  switch (block.kind) {
    case "paragraph":
    case "heading":
      return [{ kind: "region", path: [] }];
    case "list":
      return block.items.map((_, index) => ({
        kind: "region",
        path: [{ kind: "listItem", index }]
      }));
    case "blockquote":
      return block.children.flatMap((child, index) => {
        const inner = regionSlots(child);
        if (inner.length === 0) return [{ kind: "barrier" }];
        return inner.map(
          (s) => s.kind === "barrier" ? s : {
            kind: "region",
            path: [{ kind: "quoteChild", index }, ...s.path]
          }
        );
      });
    default:
      return [];
  }
}
function regionPaths(block) {
  return regionSlots(block).flatMap((s) => s.kind === "region" ? [s.path] : []);
}
function samePath(a, b) {
  if (a.length !== b.length) return false;
  return a.every((seg, i) => {
    const other = b[i];
    if (seg.kind !== other.kind) return false;
    if (seg.kind === "tableCell" && other.kind === "tableCell") {
      return seg.header === other.header && seg.row === other.row && seg.col === other.col;
    }
    return seg.index === other.index;
  });
}
function removalOf(block, survivors) {
  return survivors === 0 ? { kind: "empty" } : { kind: "block", block };
}
function removeRegionAt(block, path) {
  const seg = path[0];
  if (seg === void 0) return null;
  switch (seg.kind) {
    case "listItem": {
      if (block.kind !== "list" || block.items[seg.index] === void 0) return null;
      const items = block.items.filter((_, i) => i !== seg.index);
      return removalOf({ ...block, items }, items.length);
    }
    case "quoteChild": {
      if (block.kind !== "blockquote") return null;
      const child = block.children[seg.index];
      if (child === void 0) return null;
      const rest = path.slice(1);
      if (rest.length === 0) {
        const children2 = block.children.filter((_, i) => i !== seg.index);
        return removalOf({ ...block, children: children2 }, children2.length);
      }
      const inner = removeRegionAt(child, rest);
      if (inner === null) return null;
      const children = inner.kind === "empty" ? block.children.filter((_, i) => i !== seg.index) : block.children.map((c, i) => i === seg.index ? inner.block : c);
      return removalOf({ ...block, children }, children.length);
    }
    case "tableCell":
      return null;
  }
}
function appendRunsAt(block, path, runs) {
  const frag = readAtPath(block, path);
  if (!frag) return null;
  return writeAtPath(
    block,
    path,
    { ...frag, runs: [...frag.runs, ...runs] },
    (b, f) => b.kind === "paragraph" || b.kind === "heading" ? { ...b, runs: [...f.runs] } : b
  );
}
function isBlankRegionAt(block, path) {
  const frag = readAtPath(block, path);
  return frag !== null && fragmentText(frag).trim().length === 0;
}
function appendFragmentAdoptingKind(block, path, frag) {
  const into = readAtPath(block, path);
  if (!into) return null;
  const merged = {
    ...into,
    kind: frag.kind ?? "paragraph",
    level: frag.level,
    runs: [...into.runs, ...frag.runs]
  };
  return writeAtPath(block, path, merged, (b, f) => fragmentIntoBlock(b, f));
}
function listItemAt(block, path) {
  const seg = path[0];
  if (seg === void 0) return null;
  if (seg.kind === "listItem") {
    if (block.kind !== "list" || path.length > 1) return null;
    return block.items[seg.index] ?? null;
  }
  if (seg.kind === "quoteChild") {
    if (block.kind !== "blockquote") return null;
    const child = block.children[seg.index];
    return child === void 0 ? null : listItemAt(child, path.slice(1));
  }
  return null;
}
function itemLevelAt(block, path) {
  const item = listItemAt(block, path);
  return item === null ? null : listItemLevel(item) ?? 0;
}
function withItemLevel(block, path, level) {
  const seg = path[0];
  if (seg === void 0) return null;
  if (seg.kind === "listItem") {
    if (block.kind !== "list" || path.length > 1) return null;
    const item = block.items[seg.index];
    if (item === void 0) return null;
    const dto = Array.isArray(item) ? { runs: [...item], level } : { ...item, level };
    return { ...block, items: block.items.map((it, i) => i === seg.index ? dto : it) };
  }
  if (seg.kind === "quoteChild") {
    if (block.kind !== "blockquote") return null;
    const child = block.children[seg.index];
    if (child === void 0) return null;
    const inner = withItemLevel(child, path.slice(1), level);
    if (!inner) return null;
    return { ...block, children: block.children.map((c, i) => i === seg.index ? inner : c) };
  }
  return null;
}
function splitContainerAt(block, path) {
  const seg = path[0];
  if (seg === void 0 || path.length !== 1) return null;
  if (seg.kind === "listItem") {
    if (block.kind !== "list") return null;
    const item = block.items[seg.index];
    if (item === void 0) return null;
    const before = block.items.slice(0, seg.index);
    const after = block.items.slice(seg.index + 1);
    return {
      head: before.length > 0 ? { ...block, items: before } : null,
      region: { kind: "paragraph", runs: [...listItemRuns3(item)] },
      tail: after.length > 0 ? { ...block, items: after } : null
    };
  }
  if (seg.kind === "quoteChild") {
    if (block.kind !== "blockquote") return null;
    const child = block.children[seg.index];
    if (child === void 0) return null;
    const before = block.children.slice(0, seg.index);
    const after = block.children.slice(seg.index + 1);
    return {
      head: before.length > 0 ? { ...block, children: before } : null,
      region: child,
      tail: after.length > 0 ? { ...block, children: after } : null
    };
  }
  return null;
}

// src/handle-structure-ops.ts
function derivedId(blocks, base) {
  const taken = new Set(blocks.map((b) => b.id).filter((i) => i != null));
  let n = 2;
  while (taken.has(`${base}#${n}`)) n += 1;
  return `${base}#${n}`;
}
function crossesTable(path) {
  return path.some((s) => s.kind === "tableCell");
}
function structuredMergeBackward(blocks, blockId, ids) {
  const t = resolveTarget(blocks, blockId);
  if (!t || crossesTable(t.path)) return null;
  const frag = readAtPath(t.block, t.path);
  if (!frag) return null;
  const slots = regionSlots(t.block);
  const at = slots.findIndex((s) => s.kind === "region" && samePath(s.path, t.path));
  if (at < 0) return null;
  if (at > 0) {
    const before = slots[at - 1];
    if (before?.kind !== "region") return null;
    const withPrev = appendRunsAt(t.block, before.path, frag.runs);
    if (!withPrev) return null;
    return dropRegion(blocks, t, withPrev);
  }
  if (t.index === 0) return structuredUnwrapToParagraph(blocks, blockId, ids);
  const prev = blocks[t.index - 1];
  const prevSlots = regionSlots(prev);
  const last = prevSlots[prevSlots.length - 1];
  if (last?.kind !== "region") {
    if (fragmentText(frag).length > 0) return null;
    return dropRegion(blocks, t, t.block);
  }
  const prevPath = last.path;
  const husk = prev.kind === "heading" && prevPath.length === 0 && isBlankRegionAt(prev, prevPath);
  const mergedPrev = husk ? appendFragmentAdoptingKind(prev, prevPath, frag) : appendRunsAt(prev, prevPath, frag.runs);
  if (!mergedPrev) return null;
  const next = dropRegion(blocks, t, t.block);
  if (!next) return null;
  next[t.index - 1] = keepId(mergedPrev, prev.id);
  return next;
}
function dropRegion(blocks, t, container) {
  const next = [...blocks];
  if (t.path.length === 0) {
    next.splice(t.index, 1);
    return next;
  }
  const removed = removeRegionAt(container, t.path);
  if (removed === null) return null;
  if (removed.kind === "empty") next.splice(t.index, 1);
  else next[t.index] = keepId(removed.block, t.block.id);
  return next;
}
function structuredUnwrapToParagraph(blocks, blockId, ids) {
  const t = resolveTarget(blocks, blockId);
  if (!t || crossesTable(t.path)) return null;
  const split = splitContainerAt(t.block, t.path);
  if (!split) return null;
  const rootId = t.block.id;
  const mint = (taken) => rootId != null ? derivedId(taken, rootId) : void 0;
  const replacement = [];
  if (split.head) replacement.push(keepId(split.head, rootId));
  const liftedId = ids?.paragraphId ?? (split.head === null && split.tail === null ? rootId : mint(blocks));
  replacement.push(keepId(split.region, liftedId));
  if (split.tail) {
    const tailId = split.head === null ? rootId : ids?.tailContainerId ?? mint([...blocks, ...replacement]);
    replacement.push(keepId(split.tail, tailId));
  }
  const next = [...blocks];
  next.splice(t.index, 1, ...replacement);
  return next;
}
function structuredIndentItem(blocks, blockId, delta, ids) {
  if (delta === 0) return null;
  const t = resolveTarget(blocks, blockId);
  if (!t) return null;
  const level = itemLevelAt(t.block, t.path);
  if (level === null) return null;
  const wanted = level + delta;
  if (wanted < 0) return structuredUnwrapToParagraph(blocks, blockId, ids);
  if (wanted > MAX_LIST_LEVEL) return null;
  const written = withItemLevel(t.block, t.path, wanted);
  if (!written) return null;
  const next = [...blocks];
  next[t.index] = keepId(written, t.block.id);
  return next;
}

// src/handle-structured.ts
var UTF85 = new TextEncoder();
function blockFragment(block) {
  const path = concretePath(block, []);
  return path ? readAtPath(block, path) : null;
}
function paragraphFromFragment(frag) {
  return { kind: "paragraph", runs: [...frag.runs] };
}
function structuredFragmentAt(blocks, blockId) {
  const t = resolveTarget(blocks, blockId);
  return t ? readAtPath(t.block, t.path) : null;
}
function structuredEditText(module, blocks, blockId, edit) {
  const t = resolveTarget(blocks, blockId);
  if (!t) return null;
  const frag = readAtPath(t.block, t.path);
  if (!frag) return null;
  const edited = editFragmentText(module, frag, {
    start: edit.at,
    end: edit.at + edit.deleteBytes,
    replacement: edit.insert
  });
  if (!edited.ok) return null;
  return writeFragmentAt(blocks, t, edited.fragment);
}
function structuredSplit(module, blocks, blockId, atByte, newBlockId) {
  const t = resolveTarget(blocks, blockId);
  if (!t) return null;
  const frag = readAtPath(t.block, t.path);
  if (!frag) return null;
  const total = UTF85.encode(fragmentText(frag)).length;
  const at = Math.max(0, Math.min(atByte, total));
  const head = sliceFragment(module, frag, { start: 0, end: at });
  const tail = sliceFragment(module, frag, { start: at, end: total });
  if (!head.ok || !tail.ok) return null;
  if (t.path.length === 0) {
    const headBlock = fragmentIntoBlock(t.block, {
      ...head.fragment,
      kind: frag.kind,
      level: frag.level
    });
    if (headBlock == null) return null;
    const tailBlock = {
      id: newBlockId,
      kind: "paragraph",
      runs: [...tail.fragment.runs]
    };
    const next = [...blocks];
    next.splice(t.index, 1, headBlock, tailBlock);
    return next;
  }
  const withHead = writeFragmentAt(blocks, t, head.fragment);
  if (!withHead) return null;
  const container = withHead[t.index];
  const split = insertSiblingAfterPath(container, t.path, tail.fragment, paragraphFromFragment);
  if (!split) return null;
  const id = container.id;
  withHead[t.index] = id != null ? { ...split, id } : split;
  return withHead;
}
function structuredMerge(blocks, blockId) {
  return structuredMergeBackward(blocks, blockId);
}
function structuredInsertAfter(blocks, blockId, block) {
  const idx = containerIndexOf(blocks, blockId);
  const at = idx < 0 ? blocks.length : idx + 1;
  const next = [...blocks];
  next.splice(at, 0, block);
  return next;
}
function structuredDeleteBlock(blocks, blockId) {
  if (blockId === "") return null;
  const at = blocks.findIndex((b) => b.id === blockId);
  if (at < 0) return null;
  const next = [...blocks];
  next.splice(at, 1);
  return next;
}
function structuredWithComment(module, blocks, blockId, span2) {
  const t = resolveTarget(blocks, blockId);
  if (!t) return null;
  const frag = readAtPath(t.block, t.path);
  if (!frag) return null;
  const marked = applyCommentMark(module, frag, {
    commentId: span2.commentId,
    start: span2.start,
    end: span2.end
  });
  if (!marked.ok) return null;
  return writeFragmentAt(blocks, t, marked.fragment);
}
function structuredWithRuns(blocks, blockId, frag) {
  const t = resolveTarget(blocks, blockId);
  if (!t) return null;
  return writeFragmentAt(blocks, t, frag);
}

// src/page-decoration.ts
var PAGE_BORDER_SCOPE_TO_ORD = {
  all_pages: 0,
  first_page_only: 1,
  all_but_first: 2
};
var ORD_TO_PAGE_BORDER_SCOPE = [
  "all_pages",
  "first_page_only",
  "all_but_first"
];
var PAGE_BORDER_OFFSET_FROM_TO_ORD = { page: 0, text: 1 };
var ORD_TO_PAGE_BORDER_OFFSET_FROM = ["page", "text"];
var PAGE_BORDERS_DEFAULT_OFFSET_PT = 24;
var LINE_NUMBER_RESTART_TO_ORD = {
  continuous: 0,
  new_page: 1,
  new_section: 2
};
var ORD_TO_LINE_NUMBER_RESTART = [
  "continuous",
  "new_page",
  "new_section"
];
var LINE_NUMBERING_DEFAULT_START = 1;
var LINE_NUMBERING_DEFAULT_COUNT_BY = 1;
var LINE_NUMBERING_DEFAULT_DISTANCE_PT = 18;
var A4_PAGE_BOX = { widthPt: 595, heightPt: 842 };
var MIN_TEXT_COLUMN_WIDTH_PT = 72;
var MIN_TEXT_COLUMN_HEIGHT_PT = 72;
var MARGIN_SIDES = ["topPt", "rightPt", "bottomPt", "leftPt"];
var SIDE_LABEL = {
  topPt: "Top",
  rightPt: "Right",
  bottomPt: "Bottom",
  leftPt: "Left"
};
function validatePageMargins(m, box) {
  for (const side of MARGIN_SIDES) {
    const v = m[side];
    if (!Number.isFinite(v)) {
      return { field: side, message: `${SIDE_LABEL[side]} margin must be a number.` };
    }
    if (v < 0) {
      return { field: side, message: `${SIDE_LABEL[side]} margin cannot be negative.` };
    }
  }
  const header = box.headerBandPt ?? 0;
  if (header > 0 && m.topPt < header) {
    return {
      field: "topPt",
      message: `Top margin must be at least ${header}pt \u2014 the page reserves that band for the running header, and a smaller margin makes the header overprint the body text.`
    };
  }
  const footer = box.footerBandPt ?? 0;
  if (footer > 0 && m.bottomPt < footer) {
    return {
      field: "bottomPt",
      message: `Bottom margin must be at least ${footer}pt \u2014 the page reserves that band for the running footer, and a smaller margin makes the footer overprint the body text.`
    };
  }
  const colW = box.widthPt - m.leftPt - m.rightPt;
  if (colW < MIN_TEXT_COLUMN_WIDTH_PT) {
    return {
      field: "width",
      message: `Left and right margins leave a ${Math.round(colW)}pt text column; at least ${MIN_TEXT_COLUMN_WIDTH_PT}pt is needed.`
    };
  }
  const colH = box.heightPt - m.topPt - m.bottomPt;
  if (colH < MIN_TEXT_COLUMN_HEIGHT_PT) {
    return {
      field: "height",
      message: `Top and bottom margins leave a ${Math.round(colH)}pt text column; at least ${MIN_TEXT_COLUMN_HEIGHT_PT}pt is needed.`
    };
  }
  return null;
}
var A4_NORMAL_MARGINS = {
  topPt: 72,
  rightPt: 72,
  bottomPt: 72,
  leftPt: 72
};
var PAGE_MARGIN_PRESETS = [
  { id: "normal", label: "Normal", margins: A4_NORMAL_MARGINS },
  { id: "narrow", label: "Narrow", margins: { topPt: 54, rightPt: 36, bottomPt: 54, leftPt: 36 } },
  { id: "wide", label: "Wide", margins: { topPt: 72, rightPt: 108, bottomPt: 72, leftPt: 108 } }
];
function writePageMargins(w, m) {
  w.f32(m.topPt).f32(m.rightPt).f32(m.bottomPt).f32(m.leftPt);
}
function readPageMargins(r) {
  const topPt = r.f32();
  const rightPt = r.f32();
  const bottomPt = r.f32();
  const leftPt = r.f32();
  return { topPt, rightPt, bottomPt, leftPt };
}
var SECTION_DECORATION_KEYS = [
  "pageColor",
  "pageBorders",
  "lineNumbering",
  "pageMargins"
];
function carrySectionDecoration(from) {
  const out = {};
  for (const key of SECTION_DECORATION_KEYS) {
    const value = from[key];
    if (value !== void 0) out[key] = value;
  }
  return out;
}
function isDefaultSectionDecoration(d) {
  return SECTION_DECORATION_KEYS.every((k) => d[k] === void 0);
}
function writePageBorders(w, b) {
  writeBorderSide(w, b.top);
  writeBorderSide(w, b.left);
  writeBorderSide(w, b.bottom);
  writeBorderSide(w, b.right);
  w.u8(PAGE_BORDER_SCOPE_TO_ORD[b.scope ?? "all_pages"]).u8(PAGE_BORDER_OFFSET_FROM_TO_ORD[b.offsetFrom ?? "page"]).f32(b.offsetPt ?? PAGE_BORDERS_DEFAULT_OFFSET_PT);
}
function readPageBorders(r) {
  const top = readBorderSide(r);
  const left = readBorderSide(r);
  const bottom = readBorderSide(r);
  const right = readBorderSide(r);
  const scope = ORD_TO_PAGE_BORDER_SCOPE[r.u8()] ?? "all_pages";
  const offsetFrom = ORD_TO_PAGE_BORDER_OFFSET_FROM[r.u8()] ?? "page";
  const offsetPt = r.f32();
  return {
    ...top !== void 0 ? { top } : {},
    ...left !== void 0 ? { left } : {},
    ...bottom !== void 0 ? { bottom } : {},
    ...right !== void 0 ? { right } : {},
    scope,
    offsetFrom,
    offsetPt
  };
}
function writeLineNumbering(w, ln) {
  w.u32(ln.start ?? LINE_NUMBERING_DEFAULT_START).u16(ln.countBy ?? LINE_NUMBERING_DEFAULT_COUNT_BY).u8(LINE_NUMBER_RESTART_TO_ORD[ln.restart ?? "new_page"]).f32(ln.distancePt ?? LINE_NUMBERING_DEFAULT_DISTANCE_PT);
}
function readLineNumbering(r) {
  const start = r.u32();
  const countBy = r.u16();
  const restart = ORD_TO_LINE_NUMBER_RESTART[r.u8()] ?? "new_page";
  const distancePt = r.f32();
  return { start, countBy, restart, distancePt };
}
function writeSectionDecoration(w, d) {
  let mask = 0;
  if (d.pageColor !== void 0) mask |= 1;
  if (d.pageBorders !== void 0) mask |= 2;
  if (d.lineNumbering !== void 0) mask |= 4;
  if (d.pageMargins !== void 0) mask |= 8;
  w.u8(mask);
  if (d.pageColor !== void 0) w.str(d.pageColor);
  if (d.pageBorders !== void 0) writePageBorders(w, d.pageBorders);
  if (d.lineNumbering !== void 0) writeLineNumbering(w, d.lineNumbering);
  if (d.pageMargins !== void 0) writePageMargins(w, d.pageMargins);
}
function readSectionDecoration(r) {
  const mask = r.u8();
  const pageColor = (mask & 1) !== 0 ? r.str() : void 0;
  const pageBorders = (mask & 2) !== 0 ? readPageBorders(r) : void 0;
  const lineNumbering = (mask & 4) !== 0 ? readLineNumbering(r) : void 0;
  const pageMargins = (mask & 8) !== 0 ? readPageMargins(r) : void 0;
  return {
    ...pageColor !== void 0 ? { pageColor } : {},
    ...pageBorders !== void 0 ? { pageBorders } : {},
    ...lineNumbering !== void 0 ? { lineNumbering } : {},
    ...pageMargins !== void 0 ? { pageMargins } : {}
  };
}

// src/table-styles.ts
var DEFAULT_BAND_COLOR = "#F1F5F9";
var DEFAULT_HEADER_FILL = "#E2E8F0";
var GRID_BORDER = { widthPt: 1, color: "#CBD5E1", style: "solid" };
var HEADER_RULE = { widthPt: 2, color: "#334155", style: "solid" };
var TABLE_STYLE_PRESETS = [
  { id: "plain", label: "Plain", border: "none" },
  { id: "grid", label: "Grid", border: "grid" },
  {
    id: "header-emphasis",
    label: "Header",
    headerFill: DEFAULT_HEADER_FILL,
    border: "header-underline"
  },
  {
    id: "banded-rows",
    label: "Banded rows",
    bandFill: DEFAULT_BAND_COLOR,
    bandParity: 0,
    border: "none"
  },
  {
    id: "banded-header",
    label: "Banded + header",
    headerFill: DEFAULT_HEADER_FILL,
    bandFill: "#F8FAFC",
    bandParity: 0,
    border: "grid"
  }
];
function schemeForPreset(id) {
  return TABLE_STYLE_PRESETS.find((s) => s.id === id);
}
function shadingForCell(scheme2, isHeaderRow, bodyRowIndex) {
  if (isHeaderRow) return scheme2.headerFill;
  if (scheme2.bandFill == null) return void 0;
  return bodyRowIndex % 2 === (scheme2.bandParity ?? 0) ? scheme2.bandFill : void 0;
}
function borderForCell(scheme2, isHeaderRow) {
  switch (scheme2.border) {
    case "none":
      return void 0;
    case "grid":
      return { top: GRID_BORDER, left: GRID_BORDER, bottom: GRID_BORDER, right: GRID_BORDER };
    case "header-underline":
      return isHeaderRow ? { bottom: HEADER_RULE } : void 0;
  }
}

// src/table-edit.ts
function tableGrid(table) {
  const hasHeader = table.header != null;
  return {
    rows: (hasHeader ? 1 : 0) + table.rows.length,
    cols: columnCount(table),
    hasHeader
  };
}
function isHuskTable(block) {
  if (block.kind !== "table") return false;
  const t = block;
  return t.header == null && t.rows.length === 0;
}
function withoutHuskTables(blocks) {
  return blocks.some(isHuskTable) ? blocks.filter((b) => !isHuskTable(b)) : blocks;
}
function verbEmptiesTable(table, region, verb) {
  if (verb.op !== "deleteRow" && verb.op !== "deleteColumn") return false;
  const reg = normalizeRegion(region);
  if (reg == null || !regionInRange(table, reg)) return false;
  const grid = tableGrid(table);
  if (verb.op === "deleteRow") return reg.r1 - reg.r0 + 1 >= grid.rows;
  return grid.cols <= 1;
}
function applyTableVerb(module, table, region, verb) {
  const reg = normalizeRegion(region);
  if (reg == null) return null;
  if (verb.op === "setCellAlignH") {
    if (!regionInRange(table, reg)) return null;
    return foldCells(
      table,
      cellsIn(table, reg),
      (running, row, col) => setCellAlignH(running, row, col, verb.a ?? void 0)
    );
  }
  if (!module.supportsTableVerbs) return null;
  switch (verb.op) {
    case "setCellShading":
      return foldRegionVerb(
        module,
        table,
        reg,
        (m, t, row, col) => m.tableSetCellShading(t, row, col, verb.hex ?? void 0)
      );
    case "setCellVAlign":
      return foldRegionVerb(
        module,
        table,
        reg,
        (m, t, row, col) => m.tableSetCellVAlign(t, row, col, verb.v ?? void 0)
      );
    case "setCellBorders":
      return foldRegionVerb(
        module,
        table,
        reg,
        (m, t, row, col) => m.tableSetCellBorders(t, row, col, verb.borders ?? void 0)
      );
    case "insertRow":
      if (!regionInRange(table, reg)) return null;
      return unwrap2(module.tableInsertRow(table, reg.r0, verb.where));
    case "deleteRow": {
      if (!regionInRange(table, reg)) return null;
      if (verbEmptiesTable(table, reg, verb)) return null;
      let cur = table;
      for (let i = reg.r0; i <= reg.r1; i += 1) {
        const next = unwrap2(module.tableDeleteRow(cur, reg.r0));
        if (next == null) return null;
        cur = next;
      }
      return cur;
    }
    case "insertColumn":
      if (!regionInRange(table, reg)) return null;
      return unwrap2(module.tableInsertColumn(table, reg.c0, verb.where));
    case "deleteColumn":
      if (!regionInRange(table, reg)) return null;
      return unwrap2(module.tableDeleteColumn(table, reg.c0));
    case "mergeCells":
      if (!regionInRange(table, reg)) return null;
      if (reg.r0 === reg.r1 && reg.c0 === reg.c1) return null;
      return unwrap2(module.tableMergeCells(table, reg.r0, reg.c0, reg.r1, reg.c1));
    case "splitCell":
      if (!regionInRange(table, reg)) return null;
      return unwrap2(module.tableSplitCell(table, reg.r0, reg.c0, verb.intoRows, verb.intoCols));
    case "toggleHeader":
      return toggleHeader(module, table, verb.on);
    case "setColumnWidth": {
      if (!isIndex(verb.col) || verb.col >= columnCount(table)) return null;
      return unwrap2(module.tableSetColumnWidth(table, verb.col, verb.pt, "fixed"));
    }
    case "setRowHeadColumns":
      if (!isIndex(verb.n)) return null;
      return unwrap2(module.tableSetRowHeadColumns(table, verb.n));
    case "applyTableStyle":
      return applyTableStyle(module, table, verb.preset);
    case "setHeaderStyle":
      return setHeaderStyle(module, table, verb.style);
    case "setRowBanding":
      return setRowBanding(module, table, verb.on, verb.color);
  }
}
function applyTableStyle(module, table, preset) {
  const scheme2 = schemeForPreset(preset);
  if (!scheme2) return null;
  return foldCells(table, allCells(table), (running, row, col, isHeaderRow, bodyRowIndex) => {
    const shaded = module.tableSetCellShading(
      running,
      row,
      col,
      shadingForCell(scheme2, isHeaderRow, bodyRowIndex)
    );
    if (!shaded.ok) return null;
    const bordered = module.tableSetCellBorders(
      shaded.value,
      row,
      col,
      borderForCell(scheme2, isHeaderRow)
    );
    return bordered.ok ? bordered.value : null;
  });
}
function setHeaderStyle(module, table, style) {
  let base = table;
  if (base.header == null) {
    const made = toggleHeader(module, base, true);
    if (made == null) return null;
    base = made;
  }
  const fill = style?.fill == null ? void 0 : style.fill;
  const cols = columnCount(base);
  const coords = [];
  for (let col = 0; col < cols; col += 1)
    coords.push({ row: 0, col, isHeaderRow: true, bodyRowIndex: -1 });
  return foldCells(base, dedupe(base, coords), (running, row, col) => {
    const res = module.tableSetCellShading(running, row, col, fill);
    return res.ok ? res.value : null;
  });
}
function setRowBanding(module, table, on, color) {
  const band = on ? color ?? DEFAULT_BAND_COLOR : void 0;
  const body = allCells(table).filter((c) => !c.isHeaderRow);
  return foldCells(table, body, (running, row, col, _isHeaderRow, bodyRowIndex) => {
    const fill = on ? bodyRowIndex % 2 === 0 ? band : void 0 : void 0;
    const res = module.tableSetCellShading(running, row, col, fill);
    return res.ok ? res.value : null;
  });
}
function toggleHeader(module, table, on) {
  const res = module.tableToggleHeader(table, on);
  if (!res.ok) return null;
  return res.value.header != null === on ? res.value : null;
}
function normalizeRegion(region) {
  const { r0, c0, r1, c1 } = region;
  if (![r0, c0, r1, c1].every(isIndex)) return null;
  return {
    r0: Math.min(r0, r1),
    c0: Math.min(c0, c1),
    r1: Math.max(r0, r1),
    c1: Math.max(c0, c1)
  };
}
var isIndex = (n) => Number.isInteger(n) && n >= 0;
function regionInRange(table, reg) {
  const grid = tableGrid(table);
  return reg.r1 < grid.rows && reg.c1 < grid.cols;
}
function columnCount(table) {
  if (table.columns.length > 0) return table.columns.length;
  let max = 0;
  for (const r of table.header ? [table.header, ...table.rows] : table.rows) {
    let acc = 0;
    for (const c of r.cells) acc += c.colSpan && c.colSpan > 0 ? c.colSpan : 1;
    if (acc > max) max = acc;
  }
  return max;
}
function resolveVisual(table, row, col) {
  const hasHeader = table.header != null;
  const isHeaderRow = hasHeader && row === 0;
  const bodyRowIdx = hasHeader ? row - 1 : row;
  const srcRow = isHeaderRow ? table.header : table.rows[bodyRowIdx];
  if (!srcRow) return null;
  let acc = 0;
  for (let i = 0; i < srcRow.cells.length; i += 1) {
    const cell = srcRow.cells[i];
    const span2 = cell.colSpan && cell.colSpan > 0 ? cell.colSpan : 1;
    if (col < acc + span2) return { srcRow, cellIdx: i, cell, isHeaderRow, bodyRowIdx };
    acc += span2;
  }
  return null;
}
function cellsIn(table, reg) {
  const hasHeader = table.header != null;
  const out = [];
  for (let row = reg.r0; row <= reg.r1; row += 1) {
    const isHeaderRow = hasHeader && row === 0;
    for (let col = reg.c0; col <= reg.c1; col += 1) {
      out.push({
        row,
        col,
        isHeaderRow,
        bodyRowIndex: isHeaderRow ? -1 : hasHeader ? row - 1 : row
      });
    }
  }
  return dedupe(table, out);
}
function allCells(table) {
  const grid = tableGrid(table);
  if (grid.rows === 0 || grid.cols === 0) return [];
  return cellsIn(table, { r0: 0, c0: 0, r1: grid.rows - 1, c1: grid.cols - 1 });
}
function dedupe(table, coords) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const c of coords) {
    const hit = resolveVisual(table, c.row, c.col);
    if (!hit) continue;
    if (hit.cell.vMerge === "continue") continue;
    const key = `${hit.isHeaderRow ? "h" : hit.bodyRowIdx}:${hit.cellIdx}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}
function foldCells(table, coords, perCell) {
  if (coords.length === 0) return null;
  let cur = table;
  for (const c of coords) {
    const next = perCell(cur, c.row, c.col, c.isHeaderRow, c.bodyRowIndex);
    if (next == null) return null;
    cur = next;
  }
  return cur;
}
function foldRegionVerb(module, table, reg, call) {
  if (!regionInRange(table, reg)) return null;
  return foldCells(table, cellsIn(table, reg), (running, row, col) => {
    const res = call(module, running, row, col);
    return res.ok ? res.value : null;
  });
}
function unwrap2(res) {
  return res.ok ? res.value : null;
}
function setCellAlignH(table, row, col, alignH) {
  const hit = resolveVisual(table, row, col);
  if (!hit) return null;
  const cells = hit.srcRow.cells.map((c, i) => {
    if (i !== hit.cellIdx) return c;
    if (alignH === void 0) {
      const { alignH: _drop, ...rest } = c;
      return rest;
    }
    return { ...c, alignH };
  });
  const nextRow = { ...hit.srcRow, cells };
  if (hit.isHeaderRow) return { ...table, header: nextRow };
  return { ...table, rows: table.rows.map((r, i) => i === hit.bodyRowIdx ? nextRow : r) };
}
var CommentSpanSchema = z.object({
  commentId: z.string().min(1),
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative()
});
var DoczBlockKindSchema2 = z.enum([
  "paragraph",
  "heading",
  "list_item",
  "blockquote",
  "code",
  "html",
  // A structured `table` projects to one of these per CELL. It reaches this
  // schema because `createStructuredDoczHandle` validates the LEGACY view it
  // derives from the structured blocks — so refusing the kind here would reject
  // every table-bearing document at the handle boundary.
  "table_cell"
]);
var DoczBlockSchema = z.object({
  id: z.string().min(1).optional(),
  text: z.string(),
  kind: DoczBlockKindSchema2.optional(),
  level: z.number().int().min(1).max(6).optional(),
  comments: z.array(CommentSpanSchema).readonly().optional()
});
var HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
var BorderStyleSchema = z.enum(["solid", "dashed", "dotted", "double"]);
var BorderSchema2 = z.object({
  widthPt: z.number().nonnegative().optional(),
  color: z.string().regex(HEX_COLOR).optional(),
  style: BorderStyleSchema.optional()
});
var PageBorderScopeSchema = z.enum([
  "all_pages",
  "first_page_only",
  "all_but_first"
]);
var PageBordersSchema = z.object({
  top: BorderSchema2.optional(),
  left: BorderSchema2.optional(),
  bottom: BorderSchema2.optional(),
  right: BorderSchema2.optional(),
  scope: PageBorderScopeSchema.optional(),
  offsetFrom: z.enum(["page", "text"]).optional(),
  offsetPt: z.number().nonnegative().optional()
});
var LineNumberRestartSchema = z.enum([
  "continuous",
  "new_page",
  "new_section"
]);
var LineNumberingSchema = z.object({
  start: z.number().int().nonnegative().optional(),
  countBy: z.number().int().nonnegative().optional(),
  restart: LineNumberRestartSchema.optional(),
  distancePt: z.number().nonnegative().optional()
});
var PageMarginsSchema = z.object({
  topPt: z.number().finite().nonnegative(),
  rightPt: z.number().finite().nonnegative(),
  bottomPt: z.number().finite().nonnegative(),
  leftPt: z.number().finite().nonnegative()
});
var sectionDecorationShape = {
  pageColor: z.string().regex(HEX_COLOR).optional(),
  pageBorders: PageBordersSchema.optional(),
  lineNumbering: LineNumberingSchema.optional(),
  pageMargins: PageMarginsSchema.optional()
};
var SectionDecorationSchema = z.object(sectionDecorationShape);
var DoczDocumentSchema = z.object({
  id: z.string().min(1).optional(),
  baseVersionId: z.string().min(1).optional(),
  blocks: z.array(DoczBlockSchema).readonly(),
  ...sectionDecorationShape
});

// src/handle.ts
function ambientParseDoc2() {
  return typeof document !== "undefined" ? document : void 0;
}
function createDoczHandle(module, doc, structuredSourceIn) {
  const structuredSource = structuredSourceIn === void 0 ? void 0 : structuredBlocksWithEditableFloor(structuredSourceIn);
  const parsed = DoczDocumentSchema.safeParse(doc);
  if (!parsed.success) {
    reportInvalidInput({
      fn: "createDoczHandle",
      parseResult: parsed
    });
    throw new TypeError(`${LIB}: createDoczHandle received an invalid DoczDocument`);
  }
  const floored = blocksWithEditableFloor(parsed.data.blocks);
  const validated = floored === parsed.data.blocks ? parsed.data : { ...parsed.data, blocks: floored };
  let liveContainer = null;
  function structuredDoc(blocks) {
    return {
      ...validated.id != null ? { id: validated.id } : {},
      ...validated.baseVersionId != null ? { baseVersionId: validated.baseVersionId } : {},
      // EVERY SectionDecoration member, via the single exhaustive carrier — never
      // a hand-enumerated subset. Enumerating here is what silently reset the
      // document's page margins on every structural mutation once `pageMargins`
      // joined the interface.
      ...carrySectionDecoration(validated),
      blocks
    };
  }
  function restructure(blocks) {
    if (!blocks) return handle;
    return createStructuredDoczHandle(module, structuredDoc(blocks));
  }
  function relegacy(doc2) {
    if (doc2 === validated) return handle;
    return createDoczHandle(module, doc2);
  }
  const handle = {
    doc: validated,
    baseVersionId: validated.baseVersionId,
    blocks: validated.blocks,
    structured: structuredSource !== void 0,
    renderInto(container) {
      container.replaceChildren();
      const roots = [];
      for (const block of validated.blocks) {
        const root = mountBlock(module, container, block);
        if (root) roots.push(root);
      }
      liveContainer = container;
      return roots;
    },
    toBlocks() {
      if (structuredSource) return [...structuredSource];
      return structuredBlocksFromLegacy(validated, ambientParseDoc2());
    },
    toHtml() {
      if (structuredSource) {
        return structuredSource.map((block) => {
          const r = module.renderBlockDto(block);
          return r.ok ? bodyInnerHtml(r.html) : "";
        }).join("\n");
      }
      return validated.blocks.map((block) => {
        const r = module.renderBlockToHtml(block.text, block.comments, block.kind, block.level);
        return r.ok ? bodyInnerHtml(r.html) : "";
      }).join("\n");
    },
    commentSpans(commentId) {
      return liveContainer ? commentSpans(liveContainer, commentId) : [];
    },
    highlight(commentId, state = "exact") {
      return liveContainer ? highlightComment(liveContainer, commentId, state) : [];
    },
    withBlockComment(blockId, span2) {
      if (structuredSource) {
        return restructure(structuredWithComment(module, structuredSource, blockId, span2));
      }
      return relegacy(legacyWithBlockComment(validated, blockId, span2));
    },
    withBlockText(blockId, newText) {
      if (structuredSource) {
        const frag = structuredFragmentAt(structuredSource, blockId);
        if (!frag) return handle;
        return restructure(
          structuredWithRuns(structuredSource, blockId, {
            ...frag,
            runs: [{ text: newText, marks: [] }]
          })
        );
      }
      return relegacy(legacyWithBlockText(validated, blockId, newText));
    },
    editBlockText(blockId, edit) {
      if (structuredSource) {
        return restructure(structuredEditText(module, structuredSource, blockId, edit));
      }
      return relegacy(legacyEditBlockText(module, validated, blockId, edit));
    },
    splitBlock(blockId, atByte, newBlockId) {
      if (structuredSource) {
        return restructure(structuredSplit(module, structuredSource, blockId, atByte, newBlockId));
      }
      return relegacy(legacySplitBlock(validated, blockId, atByte, newBlockId));
    },
    mergeBlock(blockId) {
      if (structuredSource) return restructure(structuredMerge(structuredSource, blockId));
      return relegacy(legacyMergeBlock(validated, blockId));
    },
    insertBlockAfter(blockId, block) {
      if (structuredSource) {
        const promoted = structuredFromLegacyBlock(block, ambientParseDoc2());
        let next = structuredSource;
        let anchor = blockId;
        for (const b of promoted) {
          next = structuredInsertAfter(next, anchor, b);
          if (b.id != null) anchor = b.id;
        }
        return restructure(next);
      }
      return relegacy(legacyInsertBlockAfter(validated, blockId, block));
    },
    deleteBlock(blockId) {
      if (structuredSource) return restructure(structuredDeleteBlock(structuredSource, blockId));
      return relegacy(legacyDeleteBlock(validated, blockId));
    },
    indentBlock(blockId, delta) {
      const source = structuredSource ?? handle.toBlocks();
      const next = structuredIndentItem(source, blockId, delta);
      if (!next) return handle;
      return createStructuredDoczHandle(module, structuredDoc(next));
    },
    unwrapBlock(blockId) {
      const source = structuredSource ?? handle.toBlocks();
      const next = structuredUnwrapToParagraph(source, blockId);
      if (!next) return handle;
      return createStructuredDoczHandle(module, structuredDoc(next));
    },
    withBlockRuns(blockId, fragment) {
      const source = structuredSource ?? handle.toBlocks();
      const next = structuredWithRuns(source, blockId, fragment);
      if (!next) return handle;
      return createStructuredDoczHandle(module, structuredDoc(next));
    },
    verbs: module,
    module
  };
  return handle;
}
function createStructuredDoczHandle(module, doc) {
  const blocks = withoutHuskTables(doc.blocks);
  if (blocks !== doc.blocks) return createStructuredDoczHandle(module, { ...doc, blocks });
  const legacy = {
    ...doc.id != null ? { id: doc.id } : {},
    ...doc.baseVersionId != null ? { baseVersionId: doc.baseVersionId } : {},
    // FEATURE BUNDLE 2 — page decoration is a document/section property; carry it
    // across so a structured mutation never silently drops it. The exhaustive
    // carrier is load-bearing: the hand-written subset this replaced dropped
    // `pageMargins` for every mutation after margins became a document property.
    ...carrySectionDecoration(doc),
    blocks: legacyBlocksFromStructured(doc.blocks)
  };
  return createDoczHandle(module, legacy, doc.blocks);
}

// src/history.ts
var DEFAULT_HISTORY_LIMIT = 200;
var DEFAULT_COALESCE_WINDOW_MS = 700;
var RUN_BREAKING = /[\s\p{P}\p{S}]/u;
function isCoalescibleText(text) {
  if (text.length === 0) return false;
  return !RUN_BREAKING.test(text);
}
var SnapshotHistory = class {
  undoStack = [];
  redoStack = [];
  limit;
  coalesceWindowMs;
  now;
  lastKey = null;
  lastAt = 0;
  constructor(opts = {}) {
    this.limit = Math.max(1, opts.limit ?? DEFAULT_HISTORY_LIMIT);
    this.coalesceWindowMs = Math.max(0, opts.coalesceWindowMs ?? DEFAULT_COALESCE_WINDOW_MS);
    this.now = opts.now ?? Date.now;
  }
  /**
   * Record `snapshot` (the state BEFORE the mutation about to be applied). A
   * fresh edit always invalidates the redo stack. When `coalesceKey` repeats the
   * previous key within the window the entry is SKIPPED (the older, pre-run
   * snapshot stays on top — that is what one undo must restore).
   */
  push(snapshot, coalesceKey) {
    const at = this.now();
    const key = coalesceKey ?? null;
    const coalesces = key !== null && key === this.lastKey && this.undoStack.length > 0 && at - this.lastAt < this.coalesceWindowMs;
    this.lastKey = key;
    this.lastAt = at;
    this.redoStack.length = 0;
    if (coalesces) return;
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.limit) this.undoStack.shift();
  }
  /** Restore the previous snapshot, banking `current` for redo. `null` when the
   *  undo stack is empty. Always breaks the typing run. */
  undo(current) {
    const snap = this.undoStack.pop();
    if (snap === void 0) return null;
    this.redoStack.push(current);
    this.breakCoalesce();
    return snap;
  }
  /** Re-apply the most recently undone snapshot, banking `current` for undo.
   *  `null` when the redo stack is empty. Always breaks the typing run. */
  redo(current) {
    const snap = this.redoStack.pop();
    if (snap === void 0) return null;
    this.undoStack.push(current);
    if (this.undoStack.length > this.limit) this.undoStack.shift();
    this.breakCoalesce();
    return snap;
  }
  canUndo() {
    return this.undoStack.length > 0;
  }
  canRedo() {
    return this.redoStack.length > 0;
  }
  /** End the current typing run so the NEXT push starts a fresh undo entry
   *  (caret motion, a selection change, a format op, a programmatic edit). */
  breakCoalesce() {
    this.lastKey = null;
  }
  /**
   * Drop BOTH stacks — the history BARRIER. Used for an act that must not be
   * undoable past (in elaborate: placing a review comment, whose server-side
   * anchor rebase is not client-reversible).
   */
  barrier() {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.breakCoalesce();
  }
  /** Alias of {@link barrier} for a plain "forget everything" reset. */
  clear() {
    this.barrier();
  }
  get undoDepth() {
    return this.undoStack.length;
  }
  get redoDepth() {
    return this.redoStack.length;
  }
};

// src/norkron-media-type.ts
var NORKRON_DOCUMENT_KINDS = [
  "document",
  "presentation",
  "spreadsheet"
];
var NORKRON_WIRE_VERSION = 1;
var NORKRON_VERSION_PARAM = "v";
var NORKRON_MEDIA_TYPES = Object.freeze({
  document: "application/vnd.norkron.document",
  presentation: "application/vnd.norkron.presentation",
  spreadsheet: "application/vnd.norkron.spreadsheet"
});
var NORKRON_SUBTYPES = Object.freeze({
  document: "vnd.norkron.document",
  presentation: "vnd.norkron.presentation",
  spreadsheet: "vnd.norkron.spreadsheet"
});
var NORKRON_EXTENSIONS = Object.freeze({
  document: "nkdoc",
  presentation: "nkslides",
  spreadsheet: "nksheet"
});
function mediaTypeForKind(kind) {
  return NORKRON_MEDIA_TYPES[kind];
}
function subtypeForKind(kind) {
  return NORKRON_SUBTYPES[kind];
}
function extensionForKind(kind) {
  return NORKRON_EXTENSIONS[kind];
}
function versionedMediaTypeForKind(kind, version = NORKRON_WIRE_VERSION) {
  if (!Number.isSafeInteger(version) || version < 0) {
    throw new RangeError(`web-docz: invalid Norkron wire version: ${version}`);
  }
  return `${NORKRON_MEDIA_TYPES[kind]};${NORKRON_VERSION_PARAM}=${version}`;
}
var NORKRON_MEDIA_TYPES_VERSIONED = Object.freeze({
  document: versionedMediaTypeForKind("document"),
  presentation: versionedMediaTypeForKind("presentation"),
  spreadsheet: versionedMediaTypeForKind("spreadsheet")
});
function kindFromExtension(ext) {
  const clean = (ext.startsWith(".") ? ext.slice(1) : ext).toLowerCase();
  for (const kind of NORKRON_DOCUMENT_KINDS) {
    if (NORKRON_EXTENSIONS[kind] === clean) return kind;
  }
  return null;
}
function kindFromMediaType(mediaType) {
  const lower = mediaType.toLowerCase();
  for (const kind of NORKRON_DOCUMENT_KINDS) {
    if (NORKRON_MEDIA_TYPES[kind] === lower) return kind;
  }
  return null;
}

// src/structural-census.ts
var ZERO = {
  table: 0,
  tableRow: 0,
  listItem: 0,
  list: 0,
  blockquote: 0,
  code: 0,
  image: 0,
  horizontalRule: 0
};
var FIELDS = Object.keys(ZERO);
function tallyBlock(b, t) {
  switch (b.kind) {
    case "table":
      t.table += 1;
      t.tableRow += b.rows.length + (b.header ? 1 : 0);
      return;
    case "list":
      t.list += 1;
      t.listItem += b.items.length;
      return;
    case "blockquote":
      t.blockquote += 1;
      for (const c of b.children) tallyBlock(c, t);
      return;
    case "code":
      t.code += 1;
      return;
    case "image":
    case "imageSvg":
      t.image += 1;
      return;
    case "horizontalRule":
      t.horizontalRule += 1;
      return;
    case "figure":
      for (const c of b.content) tallyBlock(c, t);
      return;
    case "note":
      for (const c of b.body) tallyBlock(c, t);
      return;
    case "generated":
      for (const c of b.cachedBlocks) tallyBlock(c, t);
      return;
    default:
      return;
  }
}
function censusOfBlocks(blocks) {
  const t = { ...ZERO };
  for (const b of blocks) tallyBlock(b, t);
  return t;
}
function censusOfHtml(html, doc) {
  const tpl = doc.createElement("template");
  tpl.innerHTML = html;
  const root = tpl.content;
  const count = (sel) => root.querySelectorAll(sel).length;
  return {
    table: count("table"),
    tableRow: count("tr"),
    listItem: count("li"),
    list: count("ul,ol"),
    blockquote: count("blockquote"),
    code: count("pre"),
    image: count("img,svg"),
    horizontalRule: count("hr")
  };
}
function structuralLoss(before, after) {
  const out = [];
  for (const kind of FIELDS) {
    if (after[kind] < before[kind]) out.push({ kind, before: before[kind], after: after[kind] });
  }
  return out;
}
function describeStructuralLoss(loss) {
  return loss.map((l) => `${l.kind} ${l.before}\u2192${l.after}`).join(", ");
}

// src/version.ts
var LIB_VERSION = "0.3.3";

// src/render-verbs.ts
var UTF8_DECODER2 = new TextDecoder();
function buildRenderVerbs(ex) {
  function renderVerb(fn, input) {
    const inLen = input.length;
    const inAlloc = inLen === 0 ? 1 : inLen;
    const inPtr = ex.dz_alloc(inAlloc);
    if (inPtr === 0) return { ok: false, errCode: DZ_ERR_OOM };
    new Uint8Array(ex.memory.buffer, inPtr, inLen).set(input);
    const scratch = ex.dz_alloc(8);
    if (scratch === 0) {
      ex.dz_free(inPtr, inAlloc);
      return { ok: false, errCode: DZ_ERR_OOM };
    }
    try {
      const rc = fn(inPtr, inLen, scratch, scratch + 4);
      if (rc !== DZ_OK) return { ok: false, errCode: rc };
      const view = new DataView(ex.memory.buffer);
      const htmlPtr = view.getUint32(scratch, true);
      const htmlLen = view.getUint32(scratch + 4, true);
      const html = UTF8_DECODER2.decode(new Uint8Array(ex.memory.buffer, htmlPtr, htmlLen));
      ex.dz_free(htmlPtr, htmlLen);
      return { ok: true, html };
    } finally {
      ex.dz_free(inPtr, inAlloc);
      ex.dz_free(scratch, 8);
    }
  }
  return {
    renderBlockToHtml: (text, comments = [], kind, level) => renderVerb(ex.dz_render_block_to_html, encodeRenderInput({ text, comments, kind, level })),
    renderRunsToHtml: (runs, kind, level, props) => typeof ex.dz_render_runs_to_html === "function" ? renderVerb(
      ex.dz_render_runs_to_html,
      props === void 0 ? encodeRenderRunsInput({ runs, kind, level }) : encodeRenderRunsInputWithProps({ runs, kind, level, props })
    ) : { ok: false, errCode: DZ_ERR_UNAVAILABLE },
    renderBlockDto: (block) => typeof ex.dz_render_block_to_html_dto === "function" ? renderVerb(ex.dz_render_block_to_html_dto, encodeBlock(block)) : { ok: false, errCode: DZ_ERR_UNAVAILABLE }
  };
}

// src/wasm-loader.ts
function detectRuntime() {
  if (typeof Bun !== "undefined") return "bun";
  if (typeof Deno !== "undefined") return "deno";
  const proc = globalThis.process;
  const hasNode = typeof proc?.versions?.node === "string";
  if (!hasNode && typeof window !== "undefined") return "browser";
  if (!hasNode && typeof self !== "undefined") return "browser";
  return "node-esm";
}
function wasmUrl() {
  return new URL("../wasm/docz.wasm", import.meta.url);
}
function wasmFsPath() {
  const url = wasmUrl();
  if (url.protocol === "file:") {
    const p = decodeURIComponent(url.pathname);
    return /^\/[A-Za-z]:/.test(p) ? p.slice(1) : p;
  }
  return url.href;
}
function toArrayBuffer(u8) {
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}
async function readWasmBytes(runtime) {
  switch (runtime) {
    case "bun": {
      const file = Bun.file(wasmFsPath());
      return await file.arrayBuffer();
    }
    case "deno": {
      const u8 = await Deno.readFile(
        wasmUrl()
      );
      return toArrayBuffer(u8);
    }
    case "node-esm":
    case "node-cjs": {
      const fsp = await import('fs/promises');
      const buf = await fsp.readFile(wasmUrl());
      return toArrayBuffer(buf);
    }
    case "browser": {
      const resp = await fetch(wasmUrl().href);
      return await resp.arrayBuffer();
    }
  }
}
async function instantiate(runtime) {
  const imports = {};
  if (runtime === "browser") {
    const href = wasmUrl().href;
    try {
      if (typeof WebAssembly.instantiateStreaming === "function") {
        const result3 = await WebAssembly.instantiateStreaming(fetch(href), imports);
        return result3.instance.exports;
      }
    } catch {
    }
    const bytes2 = await (await fetch(href)).arrayBuffer();
    const result2 = await WebAssembly.instantiate(bytes2, imports);
    return result2.instance.exports;
  }
  const bytes = await readWasmBytes(runtime);
  const result = await WebAssembly.instantiate(bytes, imports);
  return result.instance.exports;
}
var _syncRequire = null;
function syncRequire(id) {
  if (_syncRequire) return _syncRequire(id);
  const g = globalThis;
  if (typeof g.require === "function") {
    _syncRequire = g.require;
    return g.require(id);
  }
  const proc = globalThis.process;
  const getBuiltin = proc?.getBuiltinModule;
  if (typeof getBuiltin === "function") {
    const mod = getBuiltin("node:module");
    _syncRequire = mod.createRequire(import.meta.url);
    return _syncRequire(id);
  }
  throw new Error(
    `${LIB}: loadDoczSync() needs a synchronous require \u2014 neither globalThis.require nor process.getBuiltinModule is available in this runtime.`
  );
}
function instantiateSync(runtime) {
  if (runtime !== "node-cjs" && runtime !== "node-esm" && runtime !== "bun") {
    throw new Error(
      `${LIB}: loadDoczSync() is only supported on Node/Bun (runtime='${runtime}'). Use loadDocz() (async) elsewhere.`
    );
  }
  const fs = syncRequire("node:fs");
  const bytes = fs.readFileSync(wasmFsPath());
  const mod = new WebAssembly.Module(bytes);
  const inst = new WebAssembly.Instance(mod, {});
  return inst.exports;
}
var UTF8_DECODER3 = new TextDecoder();
function readCString(ex, ptr) {
  const mem = new Uint8Array(ex.memory.buffer);
  let end = ptr;
  while (mem[end] !== 0 && end < mem.length) end++;
  return UTF8_DECODER3.decode(mem.subarray(ptr, end));
}
function buildModule(ex) {
  const version = readCString(ex, ex.dz_version());
  const features = ex.dz_features() >>> 0;
  return {
    ...buildRenderVerbs(ex),
    version,
    features,
    ...buildVerbs(ex, features),
    ...buildEditVerbs(ex, features)
  };
}
function guardText(fn, value) {
  if (typeof value === "string") return;
  const received = value === null ? "null" : typeof value;
  reportInvalidInput({
    fn,
    parseResult: {
      success: false,
      error: {
        issues: [
          {
            code: "invalid_type",
            expected: "string",
            received,
            path: ["text"],
            message: `${fn}: block text must be a string, received ${received}`
          }
        ]
      }
    }
  });
  throw new TypeError(`${LIB}: ${fn} requires a string block text`);
}
function withInputGuards(mod) {
  return {
    ...mod,
    // `renderRunsToHtml` takes structured runs — its boundary guard is the codec
    // framing (the spread carries it through); only the string entry needs a guard.
    renderBlockToHtml: (text, comments, kind, level) => {
      guardText("renderBlockToHtml", text);
      return mod.renderBlockToHtml(text, comments, kind, level);
    }
  };
}
var _cachedPromise = null;
var _cachedSync = null;
function loadDocz() {
  if (_cachedPromise != null) return _cachedPromise;
  _cachedPromise = (async () => {
    const runtime = detectRuntime();
    const ex = await instantiate(runtime);
    const mod = withInputGuards(buildModule(ex));
    _cachedSync = mod;
    return mod;
  })().catch((e) => {
    _cachedPromise = null;
    throw e instanceof Error ? e : new Error(String(e));
  });
  return _cachedPromise;
}
function loadDoczSync() {
  if (_cachedSync != null) return _cachedSync;
  const runtime = detectRuntime();
  const ex = instantiateSync(runtime);
  const mod = withInputGuards(buildModule(ex));
  _cachedSync = mod;
  if (_cachedPromise == null) _cachedPromise = Promise.resolve(mod);
  return mod;
}

export { A4_NORMAL_MARGINS, A4_PAGE_BOX, ADDRESS_SEP, CLIPBOARD_MIME_DOCZ, CLIPBOARD_MIME_DOCZ_WEB, CLIPBOARD_MIME_HTML, CLIPBOARD_MIME_PLAIN, COMMENT_PROFILE, CommentSpanSchema, DEFAULT_BAND_COLOR, DEFAULT_COALESCE_WINDOW_MS, DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE_PT, DEFAULT_HEADER_FILL, DEFAULT_HISTORY_LIMIT, DEFAULT_TEXT_COLOR, DOCUMENT_URL_SCHEMES, DOCZ_BLOCK_CLASS, DOCZ_BLOOM_CLASS, DOCZ_COMMENT_CLASS, DZ_ERR_DECODE, DZ_ERR_INVALID, DZ_ERR_OOM, DZ_ERR_RENDER, DZ_ERR_TRANSFORM, DZ_ERR_UNAVAILABLE, DZ_FEATURE_ANCHOR, DZ_FEATURE_BLOCK_KIND, DZ_FEATURE_BLOCK_PALETTE, DZ_FEATURE_LIST_VERBS, DZ_FEATURE_MARK_TOGGLE, DZ_FEATURE_RENDER, DZ_FEATURE_STYLE_MARKS, DZ_FEATURE_TABLE_VERBS, DZ_FEATURE_TRANSFORM, DZ_OK, DocumentMirror, DoczBlockKindSchema2 as DoczBlockKindSchema, DoczBlockSchema, DoczDocumentSchema, DoczFragmentSchema, FLOOR_BLOCK_ID, FRAGMENT_SURFACE_TESTID, FragmentMarkSchema, FragmentRunSchema, LIB, LIB_VERSION, LIST_PROPS, LineNumberingSchema, MARK_RANK, MAX_LIST_LEVEL, MEDIA_URL_SCHEMES, MIN_TEXT_COLUMN_HEIGHT_PT, MIN_TEXT_COLUMN_WIDTH_PT, COMMENT_CLASS as MIRROR_COMMENT_CLASS, COMMENT_NOTE_CLASS as MIRROR_COMMENT_NOTE_CLASS, MIRROR_PROFILE, MIRROR_ROOT_STYLE, MIRROR_VISIBLE_STYLE, MIXED, NORKRON_DOCUMENT_KINDS, NORKRON_EXTENSIONS, NORKRON_MEDIA_TYPES, NORKRON_MEDIA_TYPES_VERSIONED, NORKRON_SUBTYPES, NORKRON_VERSION_PARAM, NORKRON_WIRE_VERSION, PAGE_MARGIN_PRESETS, PASTE_PROFILE, PageBordersSchema, PageMarginsSchema, SAFE_URL_SCHEMES, SANITIZE_ALLOWED_TAGS, SANITIZE_DROP_TAGS, SANITIZE_SAFE_CLASSES, SECTION_DECORATION_KEYS, STYLE_PROPS, SectionDecorationSchema, SnapshotHistory, StructuredBlockListSchema, StructuredBlockSchema, TABLE_STYLE_PRESETS, WEB_DOCZ_TOKEN, allCommentIds, applyAcrossBlocks, applyAcrossSlices, applyCommentMark, applyStyle, applyStyleMark, applyTableVerb, asyncClipboardReadable, asyncClipboardWritable, asyncClipboardWritePresent, blockFragment, blockPlainText, blocksWithEditableFloor, bodyInnerHtml, borderForCell, buildEditVerbs, buildMirrorArticle, byteLen, canonicalizeMarks, caretMarks, carrySectionDecoration, censusOfBlocks, censusOfHtml, changeCaseRuns, changeCaseText, changeFragmentCase, clearFragmentMarks, clearHighlight, clipboardFlavours, clipboardWriteSupported, commentIdsIn, commentSpans, createDoczHandle, createStructuredDoczHandle, decodeAddressSegment, decodeBlock, decodeBlockList, decodeDocBody, decodeFragment, decodeRebaseOutcome, decodeRenderInput, decodeSelector, decodeTable, decodeTablePayload, describeStructuralLoss, domBlockText, domMarkdownText, editFragmentText, emptyFragment, emptyTable, encodeAddressSegment, encodeApplyStyleMarkInput, encodeBlock, encodeBlockList, encodeBuildSelectorInput, encodeDeleteColumn, encodeDeleteRow, encodeFragment, encodeInsertColumn, encodeInsertRow, encodeMergeCells, encodeRebaseInput, encodeRenderInput, encodeRenderRunsInput, encodeRenderRunsInputWithProps, encodeRestartList, encodeSetCellBorders, encodeSetCellShading, encodeSetCellVAlign, encodeSetColumnWidth, encodeSetListBulletChar, encodeSetListChecklist, encodeSetListItemLevel, encodeSetListStyle, encodeSetRowHeadColumns, encodeSetStartOverride, encodeSplitCell, encodeTablePayload, encodeToggleHeader, encodeToggleListItemChecked, encodeToggleStyleMarkInput, explodeRawHtml, explodeTable, extensionForKind, formatBlockAddress, fragmentByteLength, fragmentCommentSpans, fragmentFromJson, fragmentIntoBlock, fragmentStyleMarks, fragmentText, fragmentToBlock, fragmentToJson, fragmentToRenderInput, fragmentToWasmRuns, hideFromAssistiveTech, highlightComment, highlightRebase, isCoalescibleText, isDefaultParaProps, isDefaultSectionDecoration, isEmptyFragment, isSafeDocumentHref, isSafeHref, isSafeImageSrc, isSubBlockAddress, isUnknownBlock, itemLevelAt, kindFromExtension, kindFromMediaType, legacyBlocksFromStructured, legacyFromStructuredBlock, legacyProjectionFromStructuredBlock, legacyProjectionsFromStructured, listItemAddress, loadDocz, loadDoczSync, looksLikeRawHtml, markCoexistence, markKey, marksAcrossSlices, marksInRange, mediaTypeForKind, mirrorBlocksFromHandle, mirrorIdToken, mirrorPlainText, mountBlock, mountFragment, nativeClipboardCommandSupported, normaliseLang, normalizeFragmentRuns, parseBlockAddress, parseClipboard, parseStructuredBlocks, projectHandle, quoteChildAddress, readBlock, readBlockList, readLineNumbering, readPageBorders, readPageMargins, readParaProps, readParaPropsGate, readSectionDecoration, readTable, rebaseToAnchorState, regionPaths, regionSlots, renderBlockToFragment, renderFragmentInto, renderFragmentToDom, renderFragmentToHtml, resolveBlockProp, resolveListState, resolveStyle, runNativeClipboardCommand, runsFromCommentSpans, runsHaveComment, safeDocumentHref, safeHref, safeSrc, sameMarks, sanitizeCommentHtml, sanitizeHtml, schemeForPreset, serializeStructuredBlocks, setFragmentKind, shadingForCell, shiftStyleRange, sliceFragment, splitFragment, stampByteOffsets, stepComment, structuralLoss, structuredBlocksFromLegacy, structuredBlocksWithEditableFloor, structuredDocFromLegacy, structuredEditText, structuredFragmentAt, structuredFromLegacyBlock, structuredIndentItem, structuredInsertAfter, structuredMerge, structuredMergeBackward, structuredSplit, structuredUnwrapToParagraph, structuredWithComment, structuredWithRuns, subtypeForKind, tableCellAddress, tableGrid, textCell, textFragment, toggleStyleMark, upgradeCommentAnchors, validatePageMargins, verbEmptiesTable, versionedMediaTypeForKind, wasmRunsToFragment, watchClipboardReadPermission, withoutHuskTables, writeBlock, writeBlockList, writeBordersArg, writeFlavoursTo, writeFlavoursToSystem, writeLineNumbering, writePageBorders, writePageMargins, writeParaProps, writeParaPropsGate, writeSectionDecoration, writeTablePayload };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map